// src/components/DocCanvasEditor.js
// UPDATED 2026-08-18 v22: Word-like Canvas pagination, visible manual page breaks,
// canonical AI/imported page margins, immediate overflow flow, and settled PDF page maps.
// Blank Letter-page clicks still land in real editable prose, protected Table/Chart/Math/
// Image objects keep their existing ownership, and live-DOM pagination/history/drag/resize
// safeguards remain intact.

import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import DocCanvasToolbar from "./DocCanvasToolbar";
import createDocWriterTable, {
  normalizeCanvasTables as normalizeTablesWithDocWriterTable,
  reassembleAllLogicalTables as reassembleAllTablesWithDocWriterTable,
} from "./DocWriterTable";
import DocWriterChart from "./DocWriterChart";
import DocWriterMath from "./DocWriterMath";
import DocWriterImage, {
  normalizeDocWriterImageValue,
  serializeDocWriterImageValue,
} from "./DocWriterImage";

/**
 * DocCanvasEditor
 *
 * Editable Doc Writer canvas with a print-layout presentation that mirrors the
 * authoritative backend renderer:
 *
 * - US Letter paper: 8.5in × 11in
 * - 0.75in visual margins
 * - Arial 11pt
 * - compact PDF-like 1.22 line height and block spacing
 * - clean paragraph, heading, list, protected table/chart/math/image objects, and page-break presentation
 *
 * This component remains responsible only for editing and document presentation.
 * Preview PDF and Finalize are document-level actions owned by the parent Editor
 * page and the main Doc Writer backend.
 *
 * Props:
 *  - html: string
 *  - onChange: (html: string) => void
 *  - readOnly?: boolean
 *  - redline?: boolean
 *  - placeholder?: string
 *  - showToolbar?: boolean (default true)
 *  - onInsertPageBreak?: () => void
 */

const LETTER_WIDTH = "8.5in";
const LETTER_HEIGHT = "11in";
const LETTER_MARGIN = "0.75in";
const PAGE_GAP_PX = 24;
const PAGE_OVERFLOW_TOLERANCE_PX = 2;
const PAGINATION_DELAY_MS = 90;
const VISUAL_LAYOUT_SETTLE_MS = 140;
const DRAG_AUTOSCROLL_EDGE_PX = 96;
const DRAG_AUTOSCROLL_MAX_STEP_PX = 38;
const CANVAS_CARET_MARKER_ATTR = "data-doc-canvas-caret-marker";
const CANVAS_VISUAL_DRAGGING_ATTR = "data-doc-visual-dragging";
const CANVAS_TABLE_DRAGGING_ATTR = "data-doc-table-dragging";
const TABLE_MOVE_HANDLE_ATTR = "data-doc-table-move-handle";
const CANVAS_DRAG_OVER_ATTR = "data-doc-drag-over";
const DOCWRITER_EXPORT_PAGE_ATTR = "data-docwriter-export-page";
const DOCWRITER_EXPORT_PAGE_NUMBER_ATTR = "data-docwriter-export-page-number";
const DOCWRITER_EXPORT_PAGE_CLASS = "docwriter-canvas-export-page";
const EDITOR_HISTORY_LIMIT = 120;
const WORKSPACE_VERTICAL_GUTTER_PX = 24;
const FULL_PAGE_WORKSPACE_HEIGHT = `calc(${LETTER_HEIGHT} + ${
  WORKSPACE_VERTICAL_GUTTER_PX * 2
}px)`;

const BLOCK_BOUNDARY_TAGS = new Set([
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "DIV",
  "FOOTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "MAIN",
  "P",
  "SECTION",
]);

const STRUCTURAL_CONTAINER_TAGS = new Set([
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "DIV",
  "FOOTER",
  "HEADER",
  "MAIN",
  "SECTION",
]);

const FLOW_BLOCK_TAGS = new Set([
  ...STRUCTURAL_CONTAINER_TAGS,
  "ADDRESS",
  "DETAILS",
  "DL",
  "FIGCAPTION",
  "FIGURE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HR",
  "OL",
  "P",
  "PRE",
  "TABLE",
  "UL",
]);

const TEXT_SPLITTABLE_TAGS = new Set([
  "ADDRESS",
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "DIV",
  "FOOTER",
  "HEADER",
  "LI",
  "MAIN",
  "P",
  "PRE",
  "SECTION",
]);

const CANVAS_GROUP_ATTR = "data-doc-canvas-group";
const CANVAS_REPEAT_ONLY_ATTR = "data-doc-canvas-repeat-only";
const CANVAS_OVERFLOW_ATTR = "data-doc-canvas-overflow";
const CANVAS_SELECTED_PAGE_BREAK_ATTR = "data-doc-page-break-selected";
const PAGE_BREAK_SELECTOR =
  '.page-break, [data-page-break="true"], [data-doc-page-break="true"], hr[data-page-break]';
const CANVAS_TABLE_FRAGMENT_ATTR = "data-doc-table-fragment";
const CANVAS_SELECTED_TABLE_ATTR = "data-doc-table-selected";
const CANVAS_SELECTED_CELL_ATTR = "data-doc-cell-selected";
const CANVAS_SELECTED_VISUAL_ATTR = "data-doc-visual-selected";
const DOC_TABLE_ATTR = "data-doc-table";
const DOC_TABLE_ID_ATTR = "data-doc-table-id";
const DOC_ROW_ID_ATTR = "data-doc-row-id";
const DOC_CELL_ID_ATTR = "data-doc-cell-id";
const DOC_PROTECTED_BLOCK_ATTR = "data-doc-protected-block";
const DOC_BLOCK_ID_ATTR = "data-doc-block-id";
const DOC_BLOCK_SOURCE_ATTR = "data-doc-block-source";
const DOC_BLOCK_CANONICAL_SOURCE_ATTR = "data-doc-block-canonical-source";
const DOC_VISUAL_HOST_ATTR = "data-doc-visual-host";
const DOC_VISUAL_STATIC_ATTR = "data-doc-visual-static";
const DOC_VISUAL_STATIC_READY_ATTR = "data-doc-static-ready";
const DOC_BLOCK_TYPE_ATTR = "data-doc-block-type";
const DOC_VISUAL_WIDTH_ATTR = "data-doc-visual-width";
const DOC_VISUAL_HEIGHT_ATTR = "data-doc-visual-height";
const DOC_VISUAL_ALIGNMENT_ATTR = "data-doc-visual-alignment";
const DOC_WRITER_VISUAL_MOUNTED_ATTR = "data-doc-writer-mounted";
const DOC_WRITER_OBJECT_EDITING_ATTR = "data-doc-writer-object-editing";
const DOC_WRITER_MATH_DRAG_HANDLE_ATTR = "data-docwriter-math-drag-handle";
const TABLE_RESIZE_EDGE_PX = 7;
const MIN_TABLE_COLUMN_PX = 38;
const MIN_TABLE_WIDTH_PX = 160;
const MIN_VISUAL_WIDTH_PX = 140;
const MIN_VISUAL_HEIGHT_PX = 80;


function createDocumentObjectId(prefix = "object") {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return `${prefix}-${crypto.randomUUID()}`;
    }
  } catch (_) {}

  return `${prefix}-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

function elementFromNode(node) {
  if (!node) return null;
  return node.nodeType === 1 ? node : node.parentElement || null;
}

function closestWithin(root, node, selector) {
  const element = elementFromNode(node);
  const match = element?.closest?.(selector) || null;
  return match && root?.contains(match) ? match : null;
}

function isProtectedVisualElement(element) {
  if (!element || element.nodeType !== 1) return false;

  // Protected TABLE objects belong to DocWriterTable, not the generic
  // Chart/Math/Image visual system.
  if (isStructuredTableNode(element)) return false;

  if (element.getAttribute(DOC_PROTECTED_BLOCK_ATTR) === "true") return true;

  const type = String(
    element.getAttribute("data-doc-block-type") ||
      element.getAttribute("data-doc-visual-type") ||
      ""
  ).toLowerCase();

  if (
    [
      "chart",
      "graph",
      "math",
      "formula",
      "financial-chart",
      "financial-visual",
      "geometry",
      "diagram",
      "image",
      "picture",
      "photo",
    ].includes(type)
  ) {
    return true;
  }

  return Boolean(
    element.matches?.(
      ".answer-chart-card, .answer-display-math, .answer-math-block, .doc-canvas-visual-block, .doc-canvas-image-block"
    )
  );
}

function isAtomicCanvasObject(node) {
  if (!node || node.nodeType !== 1) return false;
  if (node.tagName === "TABLE") return true;
  if (isProtectedVisualElement(node)) return true;

  // Preserve a harmless structural wrapper around one protected object as one
  // unit too. Worker HTML frequently wraps a table/math/chart in a DIV/SECTION.
  const elementChildren = Array.from(node.children || []).filter(
    (child) => child.tagName !== "BR"
  );
  const directText = Array.from(node.childNodes || [])
    .filter((child) => child.nodeType === 3)
    .map((child) => String(child.nodeValue || ""))
    .join("")
    .replace(/\u00a0/g, " ")
    .trim();
  return (
    !directText &&
    elementChildren.length === 1 &&
    (elementChildren[0].tagName === "TABLE" ||
      isProtectedVisualElement(elementChildren[0]))
  );
}

function getTableColumnCount(table) {
  if (!table) return 0;

  return Array.from(table.rows || []).reduce((maximum, row) => {
    const count = Array.from(row.cells || []).reduce(
      (total, cell) => total + Math.max(1, Number(cell.colSpan || 1)),
      0
    );
    return Math.max(maximum, count);
  }, 0);
}

function ensureTableColgroup(table, columnCount = getTableColumnCount(table)) {
  if (!table || columnCount < 1) return null;

  let colgroup = Array.from(table.children || []).find(
    (child) => child.tagName === "COLGROUP"
  );

  if (!colgroup) {
    colgroup = table.ownerDocument.createElement("colgroup");
    const firstSection = Array.from(table.children || []).find((child) =>
      ["CAPTION", "THEAD", "TBODY", "TFOOT", "TR"].includes(child.tagName)
    );
    table.insertBefore(colgroup, firstSection || table.firstChild);
  }

  const existing = Array.from(colgroup.children || []).filter(
    (child) => child.tagName === "COL"
  );

  while (existing.length < columnCount) {
    const col = table.ownerDocument.createElement("col");
    col.style.width = `${100 / columnCount}%`;
    colgroup.appendChild(col);
    existing.push(col);
  }

  while (existing.length > columnCount) {
    existing.pop()?.remove();
  }

  if (!existing.some((col) => String(col.style.width || "").trim())) {
    existing.forEach((col) => {
      col.style.width = `${100 / columnCount}%`;
    });
  }

  return colgroup;
}

function isStructuredTableNode(element) {
  if (!element || element.nodeType !== 1) return false;

  const blockType = String(
    element.getAttribute?.(DOC_BLOCK_TYPE_ATTR) ||
      element.getAttribute?.("data-doc-visual-type") ||
      element.getAttribute?.("data-content-type") ||
      element.getAttribute?.("data-doc-structured-type") ||
      ""
  )
    .trim()
    .toLowerCase();

  if (["table", "datatable", "data-table", "grid"].includes(blockType)) {
    return true;
  }

  const source = String(
    element.getAttribute?.(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
      element.getAttribute?.(DOC_BLOCK_SOURCE_ATTR) ||
      ""
  ).trim();

  return /^\s*(?:```(?:\s*)table\b|\[table\])/i.test(source);
}


function normalizeVisualFamily(value) {
  const token = String(value || "").trim().toLowerCase().replace(/[\s_]+/g, "-");
  if (["math", "formula", "equation", "latex", "calculation"].includes(token)) {
    return "math";
  }
  if (["image", "picture", "photo", "photograph", "docwriter-image"].includes(token)) {
    return "image";
  }
  return "chart";
}

function explicitStructuredVisual(value, hintType = "") {
  const raw = String(value || "").trim();
  const hinted = String(hintType || "").trim().toLowerCase();
  const marker = raw.match(
    /^\s*\[(chart|graph|visual|diagram|geometry|flowchart|math|formula|equation|latex|calculation)\]\s*/i
  );
  const fence = raw.match(
    /^\s*```\s*(chart|graph|visual|diagram|geometry|flowchart|math|formula|equation|latex|calculation)\s*\n([\s\S]*?)\n```\s*$/i
  );
  const type = String(marker?.[1] || fence?.[1] || hinted || "").toLowerCase();
  if (!type) return null;

  // Tables are NOT generic visuals. DocWriterTable is the only table owner.
  // Never let a structured table hint fall through normalizeVisualFamily(),
  // whose default family is chart.
  if (["table", "datatable", "data-table", "grid"].includes(type)) return null;

  const family = normalizeVisualFamily(type);
  let source = raw;
  if (fence) source = `[${fence[1]}]\n${fence[2]}`;
  if (!marker && !fence) {
    source = `[${family === "math" ? "math" : "chart"}]\n${raw}`;
  }
  return { family, type, source: source.trim() };
}

function directChildBySelector(parent, selector) {
  if (!parent?.children) return null;
  return Array.from(parent.children).find((child) => child.matches?.(selector)) || null;
}

function ensureDocWriterVisualStructure(block) {
  if (!block?.ownerDocument) return { host: null, template: null };
  let host = directChildBySelector(block, `[${DOC_VISUAL_HOST_ATTR}="true"]`);
  let template = directChildBySelector(
    block,
    `template[${DOC_VISUAL_STATIC_ATTR}="true"]`
  );

  if (!host) {
    host = block.ownerDocument.createElement("div");
    host.setAttribute(DOC_VISUAL_HOST_ATTR, "true");
    host.setAttribute("contenteditable", "false");
    host.style.width = "100%";
    host.style.maxWidth = "100%";
    host.style.minWidth = "0";
    block.insertBefore(host, block.firstChild || null);
  }

  if (!template) {
    template = block.ownerDocument.createElement("template");
    template.setAttribute(DOC_VISUAL_STATIC_ATTR, "true");
    block.appendChild(template);
  }

  return { host, template };
}

function applyDocWriterBlockAlignment(block, alignment = "center") {
  if (!block) return;
  const normalized = ["left", "center", "right"].includes(alignment)
    ? alignment
    : "center";
  block.setAttribute(DOC_VISUAL_ALIGNMENT_ATTR, normalized);
  if (normalized === "left") {
    block.style.marginLeft = "0";
    block.style.marginRight = "auto";
  } else if (normalized === "right") {
    block.style.marginLeft = "auto";
    block.style.marginRight = "0";
  } else {
    block.style.marginLeft = "auto";
    block.style.marginRight = "auto";
  }
}

function normalizeDocWriterMathAlignment(value, fallback = "center") {
  const normalized = String(value || "").trim().toLowerCase();
  if (["left", "center", "right"].includes(normalized)) return normalized;
  const fallbackNormalized = String(fallback || "").trim().toLowerCase();
  return ["left", "center", "right"].includes(fallbackNormalized)
    ? fallbackNormalized
    : "center";
}

function parseDocWriterMathValue(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value;
  }

  const raw = String(value ?? "").trim();
  if (!raw.startsWith("{")) return null;

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch (_) {
    return null;
  }
}

function docWriterMathMetaFromValue(value) {
  const parsed = parseDocWriterMathValue(value);
  return parsed?.meta && typeof parsed.meta === "object" && !Array.isArray(parsed.meta)
    ? parsed.meta
    : null;
}

function applyDocWriterMathOuterContract(
  block,
  sourceValue = "",
  fallbackAlignment = "center"
) {
  if (!block) return { alignment: "center", meta: null };

  const source =
    sourceValue && typeof sourceValue === "object"
      ? sourceValue
      : String(
          sourceValue ||
            block.getAttribute(DOC_BLOCK_SOURCE_ATTR) ||
            block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
            ""
        ).trim();
  const meta = docWriterMathMetaFromValue(source);
  const existingAlignment = block.getAttribute(DOC_VISUAL_ALIGNMENT_ATTR) || fallbackAlignment;
  const alignment = normalizeDocWriterMathAlignment(
    meta?.alignment,
    existingAlignment
  );

  // DocWriterMath owns its visible width, height, and left/center/right placement.
  // The Editor wrapper is deliberately neutral so it can provide full page flow,
  // pagination, selection, and whole-object movement without competing with the
  // component's saved user geometry.
  block.setAttribute(DOC_VISUAL_ALIGNMENT_ATTR, alignment);
  block.style.width = "100%";
  block.style.maxWidth = "100%";
  block.style.height = "auto";
  block.style.minHeight = "0";
  block.style.marginLeft = "0";
  block.style.marginRight = "0";
  block.style.overflow = "visible";
  block.removeAttribute(DOC_VISUAL_WIDTH_ATTR);
  block.removeAttribute(DOC_VISUAL_HEIGHT_ATTR);

  return { alignment, meta };
}

function createDocWriterVisualBlock(documentValue, descriptor = {}) {
  if (!documentValue) return null;
  const structured = explicitStructuredVisual(
    descriptor.source ?? descriptor.value ?? descriptor.payload ?? "",
    descriptor.type || descriptor.family || ""
  );
  if (!structured) return null;

  const block = documentValue.createElement("figure");
  const blockId = String(descriptor.id || "") || createDocumentObjectId(structured.family);
  const width = Number(descriptor.width || 0);
  const height = Number(descriptor.height || 0);
  const alignment = ["left", "center", "right"].includes(
    String(descriptor.alignment || "").toLowerCase()
  )
    ? String(descriptor.alignment).toLowerCase()
    : "center";

  block.className = `doc-canvas-visual-block doc-canvas-${structured.family}-block`;
  block.setAttribute(DOC_PROTECTED_BLOCK_ATTR, "true");
  block.setAttribute(DOC_BLOCK_ID_ATTR, blockId);
  block.setAttribute(DOC_BLOCK_TYPE_ATTR, structured.family);
  block.setAttribute(DOC_BLOCK_SOURCE_ATTR, structured.source);
  block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, structured.source);
  block.setAttribute("contenteditable", "false");
  block.setAttribute("draggable", "false");
  block.setAttribute("role", "group");
  block.setAttribute(
    "aria-label",
    structured.family === "math" ? "Mathematical equation" : "Chart or visual"
  );
  block.style.maxWidth = "100%";
  block.style.breakInside = "avoid";
  block.style.pageBreakInside = "avoid";
  block.style.overflow = "visible";

  if (structured.family === "chart") {
    const resolvedWidth = Number.isFinite(width) && width > 0 ? width : 620;
    block.setAttribute(DOC_VISUAL_WIDTH_ATTR, String(Math.round(resolvedWidth)));
    block.style.width = `${Math.round(resolvedWidth)}px`;
    if (Number.isFinite(height) && height > 0) {
      block.setAttribute(DOC_VISUAL_HEIGHT_ATTR, String(Math.round(height)));
    }
  } else {
    block.style.width = "100%";
  }

  if (structured.family === "math") {
    applyDocWriterMathOuterContract(block, structured.source, alignment);
  } else {
    applyDocWriterBlockAlignment(block, alignment);
  }
  ensureDocWriterVisualStructure(block);
  return block;
}

function createDocWriterImageBlock(documentValue, value, descriptor = {}) {
  if (!documentValue) return null;

  const normalized = normalizeDocWriterImageValue(value, {
    width: descriptor.width,
    height: descriptor.height,
    alignment: descriptor.alignment,
  });
  if (!normalized?.src) return null;

  const block = documentValue.createElement("figure");
  const blockId = String(descriptor.id || descriptor.blockId || "") || createDocumentObjectId("image");
  const source = JSON.stringify(normalized);
  const width = Number(normalized.meta?.width || descriptor.width || 0);
  const height = Number(normalized.meta?.height || descriptor.height || 0);
  const alignment = ["left", "center", "right"].includes(
    String(normalized.meta?.alignment || descriptor.alignment || "").toLowerCase()
  )
    ? String(normalized.meta?.alignment || descriptor.alignment).toLowerCase()
    : "center";

  block.className = "doc-canvas-visual-block doc-canvas-image-block";
  block.setAttribute(DOC_PROTECTED_BLOCK_ATTR, "true");
  block.setAttribute(DOC_BLOCK_ID_ATTR, blockId);
  block.setAttribute(DOC_BLOCK_TYPE_ATTR, "image");
  block.setAttribute(DOC_BLOCK_SOURCE_ATTR, source);
  block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, source);
  block.setAttribute("contenteditable", "false");
  block.setAttribute("draggable", "false");
  block.setAttribute("role", "group");
  block.setAttribute("aria-label", "Document image");
  block.style.maxWidth = "100%";
  block.style.breakInside = "avoid";
  block.style.pageBreakInside = "avoid";
  block.style.overflow = "visible";

  if (Number.isFinite(width) && width > 0) {
    block.setAttribute(DOC_VISUAL_WIDTH_ATTR, String(Math.round(width)));
    block.style.width = `${Math.round(width)}px`;
  } else {
    block.style.width = "100%";
  }
  if (Number.isFinite(height) && height > 0) {
    block.setAttribute(DOC_VISUAL_HEIGHT_ATTR, String(Math.round(height)));
  }

  applyDocWriterBlockAlignment(block, alignment);
  ensureDocWriterVisualStructure(block);
  return block;
}

function readImageFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(reader.error || new Error("Unable to read image."));
      reader.readAsDataURL(file);
    } catch (error) {
      reject(error);
    }
  });
}

function readImageDimensions(src) {
  return new Promise((resolve) => {
    if (!src || typeof Image === "undefined") {
      resolve({ width: 0, height: 0 });
      return;
    }
    const image = new Image();
    image.onload = () => resolve({
      width: Number(image.naturalWidth || image.width || 0),
      height: Number(image.naturalHeight || image.height || 0),
    });
    image.onerror = () => resolve({ width: 0, height: 0 });
    image.src = src;
  });
}

function pruneProtectedBlockFallbackMarkup(block) {
  if (!block?.ownerDocument) return;
  const source = String(
    block.getAttribute(DOC_BLOCK_SOURCE_ATTR) ||
      block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
      ""
  ).trim();
  if (!source) return;

  const { host, template } = ensureDocWriterVisualStructure(block);
  if (!host || !template) return;

  const extras = Array.from(block.childNodes || []).filter(
    (child) => child !== host && child !== template
  );
  if (!extras.length) return;

  const holder = block.ownerDocument.createElement("div");
  extras.forEach((child) => {
    if (child.nodeType === 3) return;
    const element = child;
    const sourceLike = Boolean(
      element.matches?.("pre, code, .doc-structured-block, [data-content-type], [data-doc-structured-type]")
    );
    if (!sourceLike) holder.appendChild(element.cloneNode(true));
  });

  // Old documents can contain a printable snapshot beside the canonical source.
  // Preserve a genuine visual snapshot, but never expose raw [math]/JSON/source text.
  if (!host.childNodes.length && !String(template.innerHTML || "").trim()) {
    const migrated = String(holder.innerHTML || "").trim();
    if (migrated) {
      template.innerHTML = migrated;
      host.innerHTML = migrated;
    }
  }
  extras.forEach((child) => child.remove());
}

function importDocWriterStructuredMarkers(root) {
  if (!root?.querySelectorAll) return [];
  const inserted = [];
  const documentValue = root.ownerDocument || document;

  // Existing explicit structured nodes from the worker are converted first.
  Array.from(
    root.querySelectorAll(
      "[data-content-type], [data-doc-structured-type], [data-doc-block-source]"
    )
  ).forEach((node) => {
    if (node.closest?.(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)) return;
    const hint =
      node.getAttribute?.("data-doc-structured-type") ||
      node.getAttribute?.("data-content-type") ||
      node.getAttribute?.(DOC_BLOCK_TYPE_ATTR) ||
      "";
    const source =
      node.getAttribute?.(DOC_BLOCK_SOURCE_ATTR) ||
      node.getAttribute?.(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
      String(node.textContent || "");

    // DocWriterTable already runs before this pass. If a table block reaches
    // the generic visual importer, preserve it rather than misclassifying it as
    // a chart. This is especially important for [table] worker blocks that the
    // backend PDF renderer also understands.
    const normalizedHint = String(hint || "").trim().toLowerCase();
    const tableSource = /^\s*(?:```\s*table\b|\[table\])/i.test(
      String(source || "")
    );
    if (
      ["table", "datatable", "data-table", "grid"].includes(normalizedHint) ||
      tableSource
    ) {
      return;
    }

    const descriptor = explicitStructuredVisual(source, hint);
    if (!descriptor) return;
    const block = createDocWriterVisualBlock(documentValue, descriptor);
    if (!block) return;
    node.replaceWith(block);
    inserted.push(block);
  });

  // Plain [chart]/[math] worker blocks are also accepted, but only when the
  // entire block explicitly declares itself. Ordinary prose is never inferred.
  Array.from(root.querySelectorAll("pre, p, div, section, article")).forEach((node) => {
    if (!node.isConnected || node === root) return;
    if (node.closest?.(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)) return;
    if (node.querySelector?.("table, figure, pre, p, section, article")) return;
    const source = String(node.textContent || "").trim();
    if (!/^\s*(?:```\s*)?\[?(?:chart|graph|visual|diagram|geometry|flowchart|math|formula|equation|latex|calculation)\]?/i.test(source)) {
      return;
    }
    const descriptor = explicitStructuredVisual(source);
    if (!descriptor) return;
    const block = createDocWriterVisualBlock(documentValue, descriptor);
    if (!block) return;
    node.replaceWith(block);
    inserted.push(block);
  });

  return inserted;
}

function normalizeProtectedBlocks(root) {
  if (!root?.querySelectorAll) return;

  const candidates = Array.from(
    root.querySelectorAll(
      [
        `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`,
        `[${DOC_BLOCK_TYPE_ATTR}]`,
        "[data-doc-visual-type]",
        ".answer-chart-card",
        ".answer-display-math",
        ".answer-math-block",
        ".doc-canvas-visual-block",
        ".doc-canvas-image-block",
      ].join(",")
    )
  );

  candidates.forEach((element) => {
    // Table is a separate object family. Never let normalizeVisualFamily()
    // reinterpret a table block as chart merely because it is protected.
    if (isStructuredTableNode(element)) return;
    if (!isProtectedVisualElement(element)) return;

    const nestedProtectedParent = element.parentElement?.closest?.(
      `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
    );
    if (nestedProtectedParent && root.contains(nestedProtectedParent)) return;

    element.setAttribute(DOC_PROTECTED_BLOCK_ATTR, "true");
    element.setAttribute("contenteditable", "false");
    element.setAttribute("draggable", "false");

    if (!element.getAttribute(DOC_BLOCK_ID_ATTR)) {
      element.setAttribute(DOC_BLOCK_ID_ATTR, createDocumentObjectId("block"));
    }

    if (!element.getAttribute(DOC_BLOCK_TYPE_ATTR)) {
      const inferred = element.matches?.(".answer-chart-card") ? "chart" : "math";
      element.setAttribute(DOC_BLOCK_TYPE_ATTR, inferred);
    }

    const family = normalizeVisualFamily(element.getAttribute(DOC_BLOCK_TYPE_ATTR));
    element.setAttribute(DOC_BLOCK_TYPE_ATTR, family);
    element.classList.add("doc-canvas-visual-block", `doc-canvas-${family}-block`);
    element.style.maxWidth = "100%";
    element.style.overflow = "visible";
    element.style.breakInside = "avoid";
    element.style.pageBreakInside = "avoid";

    const source = String(
      element.getAttribute(DOC_BLOCK_SOURCE_ATTR) ||
        element.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
        ""
    ).trim();

    if (family === "math") {
      // DocWriterMath v8 serializes the user's alignment, width, height, font,
      // appearance, and offsets in its source value. The Editor must not infer a
      // second layout from wrapper margins or old width/height attributes.
      applyDocWriterMathOuterContract(
        element,
        source,
        element.getAttribute(DOC_VISUAL_ALIGNMENT_ATTR) || "center"
      );
    } else {
      const alignment =
        element.getAttribute(DOC_VISUAL_ALIGNMENT_ATTR) ||
        (String(element.style.marginLeft || "").toLowerCase() === "auto" &&
        String(element.style.marginRight || "").toLowerCase() === "auto"
          ? "center"
          : String(element.style.marginLeft || "").toLowerCase() === "auto"
          ? "right"
          : "center");
      applyDocWriterBlockAlignment(element, alignment);
    }
    if (source) {
      ensureDocWriterVisualStructure(element);
      pruneProtectedBlockFallbackMarkup(element);
    }
  });
}

function removeEditorChromeNodePreservingDocumentTable(node) {
  if (!node) return;

  // Native TABLE elements are document content, never editor chrome.
  // If stale UI markers ever land on the table itself, strip those markers
  // without deleting the table object.
  if (node.tagName === "TABLE") {
    node.removeAttribute("data-doc-editor-only");
    node.removeAttribute("data-doc-writer-table-ui");
    node.removeAttribute("data-doc-table-move-handle");
    node.removeAttribute("data-doc-table-resize-handle");
    node.removeAttribute("data-doc-table-insert-control");
    return;
  }

  node.remove();
}


function stripDocWriterEditorOnlyMarkup(root) {
  if (!root?.querySelectorAll) return;
  root
    .querySelectorAll(
      [
        '[data-doc-editor-only="true"]',
        '[data-doc-writer-table-ui]',
        '[data-doc-table-move-handle]',
        '[data-doc-table-resize-handle]',
        '[data-doc-visual-move-handle]',
        '[data-doc-visual-drag-handle]',
        '[data-doc-visual-resize-handle]',
        '[data-doc-visual-drop-marker]',
        '.doc-canvas-resize-handle',
        '.doc-canvas-visual-drag-handle',
        '.doc-canvas-visual-drop-marker',
        '.doc-writer-chart-edit-overlay',
        '.docwriter-math-controls',
        '.docwriter-math-edit-panel',
        `[${DOC_WRITER_MATH_DRAG_HANDLE_ATTR}="true"]`,
      ].join(",")
    )
    .forEach((node) => removeEditorChromeNodePreservingDocumentTable(node));

  // Component runtime CSS is editor chrome, never document content. Printable
  // surfaces are self-contained and use inline layout styling.
  root.querySelectorAll("style").forEach((node) => node.remove());
}

function canonicalVisualSource(block) {
  if (!block) return "";
  return String(
    block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
      block.getAttribute(DOC_BLOCK_SOURCE_ATTR) ||
      ""
  ).trim();
}

function replaceDocWriterVisualsWithRevisionSource(root) {
  if (!root?.querySelectorAll) return;
  Array.from(root.querySelectorAll(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)).forEach(
    (block) => {
      const source = canonicalVisualSource(block);
      if (!source) return;
      const family = normalizeVisualFamily(block.getAttribute(DOC_BLOCK_TYPE_ATTR));
      if (family === "image") {
        pruneProtectedBlockFallbackMarkup(block);
        return;
      }
      const pre = root.ownerDocument.createElement("pre");
      pre.className = `doc-structured-block doc-structured-${family}`;
      pre.setAttribute("data-content-type", family);
      pre.setAttribute("data-doc-structured-type", family);
      pre.textContent = source;
      block.replaceWith(pre);
    }
  );
}

function ensureCanvasTableVisibility(root) {
  if (!root?.querySelectorAll) return;
  const tables = [
    ...(root.tagName === "TABLE" ? [root] : []),
    ...Array.from(root.querySelectorAll("table")),
  ];
  Array.from(new Set(tables)).forEach((table) => {
    // A table in the document model is printable/editable content. Stale hidden
    // state from pasted HTML or an older snapshot must never suppress it in Canvas.
    table.removeAttribute("hidden");
    table.removeAttribute("aria-hidden");
    table.style.display = "table";
    table.style.visibility = "visible";
    table.style.opacity = "1";
    table.style.maxWidth = "100%";
    if (!String(table.style.width || "").trim()) table.style.width = "100%";
  });
}

function normalizeDocumentObjects(root) {
  // DocWriterTable is the single canonical table model for AI-generated,
  // pasted, reopened, and toolbar-created tables. v10 also reads canonical
  // table source from protected-object attributes used by the PDF renderer.
  normalizeTablesWithDocWriterTable(root, {
    source: "reopened",
    inferWidths: true,
  });
  ensureCanvasTableVisibility(root);

  // Generic structured visual hydration is intentionally second. Table-marked
  // blocks are excluded from that importer, so Chart/Math/Image can never steal
  // a table before DocWriterTable owns it.
  importDocWriterStructuredMarkers(root);

  // A wrapper cleanup/structured conversion can expose a nested table candidate.
  // Re-run the idempotent table normalizer before protected visual cleanup.
  normalizeTablesWithDocWriterTable(root, {
    source: "reopened",
    inferWidths: true,
  });
  ensureCanvasTableVisibility(root);

  normalizeProtectedBlocks(root);
}

function escapeCssValue(value) {
  const raw = String(value ?? "");
  try {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      return CSS.escape(raw);
    }
  } catch (_) {}
  return raw.replace(/["\\]/g, "\\$&");
}

function getLogicalTableFragments(root, tableId) {
  if (!root || !tableId) return [];

  return Array.from(
    root.querySelectorAll(
      `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
    )
  );
}

function htmlContainsLogicalTableId(documentValue, value, tableId) {
  if (!documentValue || !tableId) return false;

  try {
    const holder = documentValue.createElement("div");
    holder.innerHTML = String(value || "");
    return Boolean(
      holder.querySelector(
        `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
      )
    );
  } catch (_) {
    return false;
  }
}

function extractLogicalTableFromHtml(documentValue, value, tableId) {
  if (!documentValue || !tableId) return null;
  try {
    const holder = documentValue.createElement("div");
    holder.innerHTML = String(value || "");
    unwrapCanvasPages(holder);
    stripDocWriterEditorOnlyMarkup(holder);
    try {
      reassembleAllTablesWithDocWriterTable(holder, {
        removeFragments: true,
        normalize: true,
      });
    } catch (_) {}
    return holder.querySelector(
      `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
    );
  } catch (_) {
    return null;
  }
}

function getLogicalTableCells(root, tableId) {
  const seen = new Set();
  const cells = [];

  getLogicalTableFragments(root, tableId).forEach((table) => {
    Array.from(table.querySelectorAll("th, td")).forEach((cell) => {
      if (cell.closest(`[${CANVAS_REPEAT_ONLY_ATTR}="true"]`)) return;
      const id = cell.getAttribute(DOC_CELL_ID_ATTR);
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      cells.push(cell);
    });
  });

  return cells;
}

function placeCaretInElement(element, atEnd = false) {
  if (!element) return false;

  try {
    const documentValue = element.ownerDocument;
    const range = documentValue.createRange();
    range.selectNodeContents(element);
    range.collapse(atEnd);
    const selection = documentValue.defaultView?.getSelection?.();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch (_) {
    return false;
  }
}

function measureTextOffsetWithin(root, node, offset) {
  if (!root || !node) return 0;

  try {
    const range = root.ownerDocument.createRange();
    range.selectNodeContents(root);
    range.setEnd(node, offset);
    return range.toString().length;
  } catch (_) {
    return 0;
  }
}

function captureEditorSelectionState(root) {
  const base = captureSelectionOffsets(root);
  if (!root || typeof window === "undefined") return base;

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return base;

  const anchorCell = closestWithin(root, selection.anchorNode, "th, td");
  const focusCell = closestWithin(root, selection.focusNode, "th, td");
  const anchorVisual = closestWithin(
    root,
    selection.anchorNode,
    `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
  );
  const focusVisual = closestWithin(
    root,
    selection.focusNode,
    `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
  );

  return {
    ...(base || {}),
    anchorCellId: anchorCell?.getAttribute(DOC_CELL_ID_ATTR) || "",
    focusCellId: focusCell?.getAttribute(DOC_CELL_ID_ATTR) || "",
    anchorCellOffset: anchorCell
      ? measureTextOffsetWithin(
          anchorCell,
          selection.anchorNode,
          selection.anchorOffset
        )
      : 0,
    focusCellOffset: focusCell
      ? measureTextOffsetWithin(
          focusCell,
          selection.focusNode,
          selection.focusOffset
        )
      : 0,
    anchorVisualId: anchorVisual?.getAttribute(DOC_BLOCK_ID_ATTR) || "",
    focusVisualId: focusVisual?.getAttribute(DOC_BLOCK_ID_ATTR) || "",
  };
}

function restoreTextOffsetWithin(element, requestedOffset) {
  const resolved = resolveTextOffsetWithin(element, requestedOffset);
  return resolved || { node: element, offset: 0 };
}

function restoreEditorSelectionState(root, snapshot) {
  if (!root || !snapshot) return false;

  const escapedAnchor = snapshot.anchorCellId
    ? escapeCssValue(snapshot.anchorCellId)
    : "";
  const escapedFocus = snapshot.focusCellId
    ? escapeCssValue(snapshot.focusCellId)
    : "";

  const anchorCell = escapedAnchor
    ? root.querySelector(`[${DOC_CELL_ID_ATTR}="${escapedAnchor}"]`)
    : null;
  const focusCell = escapedFocus
    ? root.querySelector(`[${DOC_CELL_ID_ATTR}="${escapedFocus}"]`)
    : null;

  if (anchorCell || focusCell) {
    const anchor = anchorCell
      ? restoreTextOffsetWithin(anchorCell, snapshot.anchorCellOffset)
      : null;
    const focus = focusCell
      ? restoreTextOffsetWithin(focusCell, snapshot.focusCellOffset)
      : anchor;

    if (anchor && focus) {
      try {
        const selection = root.ownerDocument.defaultView?.getSelection?.();
        if (!selection) return false;
        selection.removeAllRanges();

        if (typeof selection.setBaseAndExtent === "function") {
          selection.setBaseAndExtent(
            anchor.node,
            anchor.offset,
            focus.node,
            focus.offset
          );
        } else {
          const range = root.ownerDocument.createRange();
          range.setStart(anchor.node, anchor.offset);
          range.setEnd(focus.node, focus.offset);
          selection.addRange(range);
        }
        return true;
      } catch (_) {}
    }
  }

  restoreSelectionOffsets(root, snapshot);
  return Boolean(captureSelectionOffsets(root));
}

function clearCanvasObjectSelection(root) {
  if (!root?.querySelectorAll) return;

  root
    .querySelectorAll(
      [
        `[${CANVAS_SELECTED_TABLE_ATTR}="true"]`,
        `[${CANVAS_SELECTED_CELL_ATTR}="true"]`,
        `[${CANVAS_SELECTED_VISUAL_ATTR}="true"]`,
      ].join(",")
    )
    .forEach((element) => {
      element.removeAttribute(CANVAS_SELECTED_TABLE_ATTR);
      element.removeAttribute(CANVAS_SELECTED_CELL_ATTR);
      element.removeAttribute(CANVAS_SELECTED_VISUAL_ATTR);
    });

  root
    .querySelectorAll(`[${CANVAS_DRAG_OVER_ATTR}]`)
    .forEach((element) => element.removeAttribute(CANVAS_DRAG_OVER_ATTR));
}

function isPageBreakElement(node) {
  if (!node || node.nodeType !== 1) return false;

  return (
    node.classList?.contains("page-break") ||
    node.getAttribute("data-page-break") === "true" ||
    node.getAttribute("data-doc-page-break") === "true" ||
    (node.tagName === "HR" && node.hasAttribute("data-page-break"))
  );
}

function normalizeManualPageBreaks(root) {
  if (!root?.querySelectorAll) return [];

  const candidates = [
    ...(root.nodeType === 1 && root.matches?.(PAGE_BREAK_SELECTOR) ? [root] : []),
    ...Array.from(root.querySelectorAll(PAGE_BREAK_SELECTOR)),
  ];

  Array.from(new Set(candidates)).forEach((breakNode) => {
    breakNode.classList.add("page-break");
    breakNode.setAttribute("data-page-break", "true");
    breakNode.setAttribute("data-doc-page-break", "true");
    breakNode.setAttribute("data-doc-manual-page-break", "true");
    breakNode.setAttribute("contenteditable", "false");
    breakNode.setAttribute("draggable", "false");
    breakNode.setAttribute("role", "separator");
    breakNode.setAttribute(
      "aria-label",
      "Page break. Click to select, then press Delete or Backspace to remove."
    );
    breakNode.removeAttribute(CANVAS_SELECTED_PAGE_BREAK_ATTR);
  });

  return candidates;
}

function clearCanvasPageBreakSelection(root) {
  if (!root?.querySelectorAll) return;
  root
    .querySelectorAll(`[${CANVAS_SELECTED_PAGE_BREAK_ATTR}="true"]`)
    .forEach((node) => node.removeAttribute(CANVAS_SELECTED_PAGE_BREAK_ATTR));
}

function isBoundaryEmpty(node) {
  if (!node) return true;

  if (node.nodeType === 3) {
    return !String(node.nodeValue || "")
      .replace(/\u00a0/g, " ")
      .trim();
  }

  if (node.nodeType !== 1) return true;
  if (isPageBreakElement(node)) return false;
  if (isAtomicCanvasObject(node)) return false;

  if (
    node.querySelector?.(
      "img, table, video, audio, svg, canvas, iframe, object, embed"
    )
  ) {
    return false;
  }

  const text = String(node.textContent || "")
    .replace(/\u00a0/g, " ")
    .trim();

  if (text) return false;

  const meaningfulChildren = Array.from(node.children || []).filter(
    (child) => child.tagName !== "BR"
  );

  return meaningfulChildren.length === 0;
}

function trimDocumentBoundaries(
  root,
  { preserveEditingBoundaries = false } = {}
) {
  if (!root || preserveEditingBoundaries) return;

  let first = root.firstChild;
  while (first) {
    const next = first.nextSibling;
    const removableElement =
      first.nodeType === 1 &&
      (first.tagName === "BR" ||
        (BLOCK_BOUNDARY_TAGS.has(first.tagName) && isBoundaryEmpty(first)));

    if (first.nodeType === 3 && isBoundaryEmpty(first)) {
      root.removeChild(first);
    } else if (removableElement) {
      root.removeChild(first);
    } else {
      break;
    }

    first = next;
  }

  let last = root.lastChild;
  while (last) {
    const previous = last.previousSibling;
    const removableElement =
      last.nodeType === 1 &&
      (last.tagName === "BR" ||
        (BLOCK_BOUNDARY_TAGS.has(last.tagName) && isBoundaryEmpty(last)));

    if (last.nodeType === 3 && isBoundaryEmpty(last)) {
      root.removeChild(last);
    } else if (removableElement) {
      root.removeChild(last);
    } else {
      break;
    }

    last = previous;
  }
}

function removeUnsafeMarkup(root) {
  if (!root) return;

  root
    .querySelectorAll(
      "script, iframe, object, embed, meta, link, base, form"
    )
    .forEach((node) => node.remove());

  root.querySelectorAll("*").forEach((element) => {
    Array.from(element.attributes || []).forEach((attribute) => {
      const name = String(attribute.name || "").toLowerCase();

      if (
        name.startsWith("on") ||
        name === "srcdoc" ||
        name === "contenteditable"
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });
}


function unwrapCanvasPages(root) {
  if (!root) return;

  const directPages = Array.from(root.children || []).filter(
    (child) =>
      child?.nodeType === 1 &&
      child.getAttribute("data-doc-canvas-page") === "true"
  );

  if (!directPages.length) return;

  const fragment = root.ownerDocument.createDocumentFragment();

  directPages.forEach((page) => {
    Array.from(page.childNodes || []).forEach((child) => {
      fragment.appendChild(child);
    });
  });

  root.replaceChildren(fragment);
}

function stripCanvasOnlyMarkup(
  root,
  { preserveCaretMarkers = false } = {}
) {
  if (!root) return;

  root
    .querySelectorAll(`[${CANVAS_REPEAT_ONLY_ATTR}="true"]`)
    .forEach((node) => node.remove());

  root
    .querySelectorAll(
      [
        "[data-doc-table-resize-handle]",
        "[data-doc-visual-resize-handle]",
        "[data-doc-table-move-handle]",
        "[data-doc-visual-move-handle]",
        "[data-doc-visual-drag-handle]",
        "[data-doc-visual-drop-marker]",
        "[data-doc-table-insert-control]",
        // IMPORTANT: data-doc-table-context-menu is a capability marker
        // on the REAL table element. It must never be selected for node removal.
        "[data-doc-writer-table-ui]",
        ".doc-canvas-resize-handle",
        ".doc-canvas-visual-drag-handle",
        ".doc-canvas-visual-drop-marker",
        '[data-doc-editor-only="true"]',
        ".doc-writer-chart-edit-overlay",
        ".docwriter-math-controls",
        ".docwriter-math-edit-panel",
        ".doc-canvas-visual-drag-handle",
        ".doc-canvas-visual-drop-marker",
      ].join(",")
    )
    .forEach((node) => removeEditorChromeNodePreservingDocumentTable(node));

  if (!preserveCaretMarkers) {
    root
      .querySelectorAll(`[${CANVAS_CARET_MARKER_ATTR}]`)
      .forEach((node) => node.remove());
  }

  root.querySelectorAll("*").forEach((element) => {
    element.removeAttribute(CANVAS_GROUP_ATTR);
    element.removeAttribute(CANVAS_REPEAT_ONLY_ATTR);
    element.removeAttribute(CANVAS_OVERFLOW_ATTR);
    element.removeAttribute(CANVAS_TABLE_FRAGMENT_ATTR);
    element.removeAttribute(CANVAS_SELECTED_PAGE_BREAK_ATTR);
    element.removeAttribute(CANVAS_SELECTED_TABLE_ATTR);
    element.removeAttribute(CANVAS_SELECTED_CELL_ATTR);
    element.removeAttribute(CANVAS_SELECTED_VISUAL_ATTR);
    element.removeAttribute(CANVAS_VISUAL_DRAGGING_ATTR);
    element.removeAttribute(CANVAS_TABLE_DRAGGING_ATTR);
    element.removeAttribute(DOC_WRITER_VISUAL_MOUNTED_ATTR);
    element.removeAttribute(DOC_WRITER_OBJECT_EDITING_ATTR);
    element.removeAttribute("data-doc-visual-drop-marker");
    element.removeAttribute("data-doc-dragging");
    element.removeAttribute("data-doc-drag-over");
    element.removeAttribute(CANVAS_DRAG_OVER_ATTR);
  });
}

function mergeCanvasFragments(root) {
  if (!root) return;

  const mergeChildren = (parent) => {
    let element = parent.firstElementChild;

    while (element) {
      let next = element.nextElementSibling;
      const groupId = element.getAttribute(CANVAS_GROUP_ATTR);

      while (
        groupId &&
        next &&
        next.tagName === element.tagName &&
        next.getAttribute(CANVAS_GROUP_ATTR) === groupId
      ) {
        while (next.firstChild) {
          element.appendChild(next.firstChild);
        }

        const removeTarget = next;
        next = next.nextElementSibling;
        removeTarget.remove();
      }

      mergeChildren(element);
      element = next;
    }
  };

  mergeChildren(root);
}

function serializeEditorHtml(
  editor,
  { preserveCaretMarkers = false, preserveEditingBoundaries = true } = {}
) {
  if (!editor) return "";

  const documentValue = editor.ownerDocument;
  const output = documentValue.createElement("div");

  Array.from(editor.childNodes || []).forEach((node) => {
    if (
      node.nodeType === 1 &&
      node.getAttribute("data-doc-canvas-page") === "true"
    ) {
      Array.from(node.childNodes || []).forEach((child) => {
        output.appendChild(child.cloneNode(true));
      });
      return;
    }

    output.appendChild(node.cloneNode(true));
  });

  // Reassemble any older page-fragmented table before generic Canvas cleanup.
  // DocWriterTable is authoritative for logical table identity; the Editor never
  // serializes row fragments as separate competing tables.
  try {
    reassembleAllTablesWithDocWriterTable(output, {
      removeFragments: true,
      normalize: true,
    });
  } catch (_) {}

  output
    .querySelectorAll(`[${CANVAS_REPEAT_ONLY_ATTR}="true"]`)
    .forEach((node) => node.remove());

  mergeCanvasFragments(output);
  stripDocWriterEditorOnlyMarkup(output);
  stripCanvasOnlyMarkup(output, { preserveCaretMarkers });

  return normalizeDocumentHtml(output.innerHTML, {
    preserveCaretMarkers,
    preserveEditingBoundaries,
  });
}



function serializeEditorHtmlWithCanvasPages(
  editor,
  { preserveCaretMarkers = false } = {}
) {
  if (!editor) return "";

  const documentValue = editor.ownerDocument;
  const output = documentValue.createElement("div");
  const directPages = Array.from(editor.children || []).filter(
    (child) => child.getAttribute?.("data-doc-canvas-page") === "true"
  );

  // Backward-compatible fallback for any caller/document that has not yet been
  // laid out into explicit Letter pages.
  if (!directPages.length) {
    return serializeEditorHtml(editor, {
      preserveCaretMarkers,
      preserveEditingBoundaries: true,
    });
  }

  let pageIndex = 0;

  Array.from(editor.childNodes || []).forEach((node) => {
    const isCanvasPage =
      node.nodeType === 1 &&
      node.getAttribute?.("data-doc-canvas-page") === "true";

    if (!isCanvasPage) {
      // Preserve any transient non-page document node exactly as the legacy
      // serializer did. Normal Canvas settling moves such content into a page.
      output.appendChild(node.cloneNode(true));
      return;
    }

    pageIndex += 1;
    const exportPage = documentValue.createElement("div");
    exportPage.className = DOCWRITER_EXPORT_PAGE_CLASS;
    exportPage.setAttribute(DOCWRITER_EXPORT_PAGE_ATTR, "true");
    exportPage.setAttribute(
      DOCWRITER_EXPORT_PAGE_NUMBER_ATTR,
      String(node.getAttribute("data-page-number") || pageIndex)
    );

    // Intentionally copy CONTENT only. The backend already owns the physical
    // Letter sheet and 0.75in PDF margin, so Canvas width/height/padding/border
    // must not be handed off a second time.
    Array.from(node.childNodes || []).forEach((child) => {
      exportPage.appendChild(child.cloneNode(true));
    });

    output.appendChild(exportPage);
  });

  // Remove editor controls/runtime state while keeping each page wrapper,
  // including a wrapper with no children (an intentionally blank Canvas page).
  stripDocWriterEditorOnlyMarkup(output);
  stripCanvasOnlyMarkup(output, { preserveCaretMarkers });

  return output.innerHTML.trim();
}


function directVisualChild(block, selector) {
  if (!block?.children) return null;
  return (
    Array.from(block.children).find((child) => child.matches?.(selector)) || null
  );
}

function removeCommentNodes(root) {
  if (!root?.ownerDocument && root?.nodeType !== 11) return;

  const documentValue = root.ownerDocument || root;
  const nodeFilter =
    documentValue.defaultView?.NodeFilter ||
    (typeof NodeFilter !== "undefined" ? NodeFilter : null);

  if (!nodeFilter || typeof documentValue.createTreeWalker !== "function") return;

  const walker = documentValue.createTreeWalker(root, nodeFilter.SHOW_COMMENT);
  const comments = [];
  let current = walker.nextNode();

  while (current) {
    comments.push(current);
    current = walker.nextNode();
  }

  comments.forEach((comment) => comment.remove());
}

function stripReactOnlyTemporaryMarkup(root) {
  if (!root?.querySelectorAll) return;

  const cleanScope = (scope) => {
    const elements = [
      ...(scope.nodeType === 1 ? [scope] : []),
      ...Array.from(scope.querySelectorAll?.("*") || []),
    ];

    elements.forEach((element) => {
      Array.from(element.attributes || []).forEach((attribute) => {
        const name = String(attribute.name || "").toLowerCase();

        if (
          name.startsWith("data-react") ||
          name === "contenteditable" ||
          name === "tabindex" ||
          name === "draggable"
        ) {
          element.removeAttribute(attribute.name);
        }
      });
    });

    removeCommentNodes(scope);
  };

  cleanScope(root);

  root
    .querySelectorAll(`template[${DOC_VISUAL_STATIC_ATTR}="true"]`)
    .forEach((template) => {
      if (template.content) cleanScope(template.content);
    });
}

function stabilizeProtectedVisualSnapshots(
  root,
  { removeStaticTemplates = false } = {}
) {
  if (!root?.querySelectorAll) return;

  root
    .querySelectorAll(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)
    .forEach((block) => {
      const host = directVisualChild(
        block,
        `[${DOC_VISUAL_HOST_ATTR}="true"]`
      );
      const template = directVisualChild(
        block,
        `template[${DOC_VISUAL_STATIC_ATTR}="true"]`
      );
      const snapshotHtml = String(template?.innerHTML || "").trim();

      if (host && snapshotHtml) {
        host.innerHTML = snapshotHtml;
        host.querySelectorAll?.(".katex-mathml").forEach((node) => node.remove());
        host.removeAttribute("aria-hidden");
        block.setAttribute(DOC_VISUAL_STATIC_READY_ATTR, "true");
      }
      if (template?.content) {
        template.content
          .querySelectorAll?.(".katex-mathml")
          .forEach((node) => node.remove());
      }

      // These source attributes are intentionally preserved. R1/R2 preparation
      // uses them to reconstruct the canonical chart/math instructions.
      if (block.hasAttribute(DOC_BLOCK_SOURCE_ATTR)) {
        block.setAttribute(
          DOC_BLOCK_SOURCE_ATTR,
          block.getAttribute(DOC_BLOCK_SOURCE_ATTR) || ""
        );
      }
      if (block.hasAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR)) {
        block.setAttribute(
          DOC_BLOCK_CANONICAL_SOURCE_ATTR,
          block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) || ""
        );
      }

      if (removeStaticTemplates) template?.remove();
    });
}

function sanitizeTemplateContents(root) {
  if (!root?.querySelectorAll) return;

  root.querySelectorAll("template").forEach((template) => {
    if (template.content) {
      removeUnsafeMarkup(template.content);
      stripCanvasOnlyMarkup(template.content);
    }
  });
}

function stabilizePreparedDocumentHtml(
  value,
  {
    mode = "serialization",
    documentValue =
      typeof document !== "undefined" ? document : null,
  } = {}
) {
  if (!documentValue) return String(value || "").trim();

  const output = documentValue.createElement("div");
  output.innerHTML = String(value || "");

  const exportPages = Array.from(
    output.querySelectorAll?.(
      `:scope > [${DOCWRITER_EXPORT_PAGE_ATTR}="true"]`
    ) || []
  );
  const preserveCanvasPageMap =
    mode !== "revision" && exportPages.length > 0;

  if (!preserveCanvasPageMap) {
    unwrapCanvasPages(output);
    try {
      reassembleAllTablesWithDocWriterTable(output, {
        removeFragments: true,
        normalize: true,
      });
    } catch (_) {}
    mergeCanvasFragments(output);
  }

  stripDocWriterEditorOnlyMarkup(output);
  stripCanvasOnlyMarkup(output);

  // Save/Preview/Finalize normalize each already-paginated page independently
  // so a logical object can never pull content across a Canvas page boundary.
  // Revision/worker preparation keeps the established continuous editable source.
  if (preserveCanvasPageMap) {
    exportPages.forEach((page) => {
      normalizeTablesWithDocWriterTable(page, {
        source: "reopened",
        inferWidths: true,
      });
    });

    if (mode === "final-export") {
      // The Canvas page wrappers already encode the resolved physical page map.
      // Do not hand the backend a second hard-break instruction at the end of the
      // same page; that can create an extra blank page or double pagination.
      exportPages.forEach((page) => {
        page
          .querySelectorAll(PAGE_BREAK_SELECTOR)
          .forEach((breakNode) => breakNode.remove());
      });
    }
  } else {
    normalizeTablesWithDocWriterTable(output, {
      source: mode === "revision" ? "ai" : "reopened",
      inferWidths: true,
    });
  }

  if (mode === "revision") {
    // R1/R2 receives canonical editable [chart]/[math] source rather than
    // rendered SVG/KaTeX/editor chrome.
    replaceDocWriterVisualsWithRevisionSource(output);
  } else {
    stabilizeProtectedVisualSnapshots(output, {
      removeStaticTemplates: mode === "final-export",
    });
  }

  removeUnsafeMarkup(output);
  sanitizeTemplateContents(output);
  stripReactOnlyTemporaryMarkup(output);
  trimDocumentBoundaries(output, {
    preserveEditingBoundaries: preserveCanvasPageMap,
  });

  return output.innerHTML.trim();
}

function stableTableCellText(cell) {
  if (!cell) return "";

  try {
    const clone = cell.cloneNode(true);

    clone.querySelectorAll?.("br").forEach((br) => {
      br.replaceWith(clone.ownerDocument.createTextNode("\n"));
    });

    clone.querySelectorAll?.("input, textarea, select").forEach((control) => {
      let value = "";
      if (control.tagName === "SELECT") {
        value = String(
          control.selectedOptions?.[0]?.textContent ||
            control.value ||
            control.textContent ||
            ""
        );
      } else {
        value = String(control.value || control.getAttribute?.("value") || "");
      }
      control.replaceWith(clone.ownerDocument.createTextNode(value));
    });

    return String(clone.textContent || "")
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/[ \t]{2,}/g, " ")
      .trim();
  } catch (_) {
    return String(cell.textContent || "")
      .replace(/\u00a0/g, " ")
      .trim();
  }
}

function stableTableText(table) {
  if (!table) return "";

  const rows = Array.from(table.rows || []);
  const lines = rows
    .filter((row) => !row.closest?.(`[${CANVAS_REPEAT_ONLY_ATTR}="true"]`))
    .map((row) =>
      Array.from(row.cells || [])
        .map((cell) => stableTableCellText(cell))
        .join("\t")
        .replace(/[ \t]+$/g, "")
    );

  const visibleText = lines.join("\n").trim();
  if (visibleText) return visibleText;

  // An intentionally empty native table is still valid document content.
  // Preserve a small structural text representation so Save/PDF validation
  // never treats an existing table as a zero-content document.
  const rowCount = rows.length;
  const columnCount = getTableColumnCount(table);
  if (rowCount || columnCount) {
    return `[Table: ${rowCount} row${rowCount === 1 ? "" : "s"} × ${columnCount} column${
      columnCount === 1 ? "" : "s"
    }]`;
  }

  return "";
}

function stableTableTextFromRoot(root) {
  if (!root?.querySelectorAll) return "";

  const seenTableIds = new Set();
  const chunks = [];

  Array.from(root.querySelectorAll("table")).forEach((table) => {
    if (table.closest?.(`[${CANVAS_REPEAT_ONLY_ATTR}="true"]`)) return;

    const tableId = String(table.getAttribute?.(DOC_TABLE_ID_ATTR) || "");
    if (tableId && seenTableIds.has(tableId)) return;
    if (tableId) seenTableIds.add(tableId);

    const value = stableTableText(table);
    if (value) chunks.push(value);
  });

  return chunks.join("\n\n").trim();
}

function countTablesInHtml(value, documentValue) {
  if (!documentValue || !String(value || "").trim()) return 0;
  try {
    const holder = documentValue.createElement("div");
    holder.innerHTML = String(value || "");
    return holder.querySelectorAll("table").length;
  } catch (_) {
    return 0;
  }
}

function htmlToStableText(value, documentValue) {
  if (!documentValue) return String(value || "").trim();

  const holder = documentValue.createElement("div");
  holder.innerHTML = String(value || "");

  const tables = Array.from(holder.querySelectorAll("table"));
  if (!tables.length) {
    // Preserve the established non-table behavior exactly.
    return String(holder.innerText || holder.textContent || "").trim();
  }

  // Replace each table with deterministic row/cell text before asking the
  // detached holder for its document text. This avoids browser differences in
  // innerText for detached TABLE/TBODY/TR/TD structures.
  tables.forEach((table) => {
    const tableText = stableTableText(table);
    const replacement = documentValue.createElement("div");
    replacement.setAttribute("data-doc-table-text-snapshot", "true");
    replacement.textContent = tableText;
    table.replaceWith(replacement);
  });

  let stable = String(holder.textContent || holder.innerText || "")
    .replace(/\u00a0/g, " ")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  if (!stable) {
    stable = tables.map((table) => stableTableText(table)).filter(Boolean).join("\n\n");
  }

  return stable.trim();
}

function getSelectionNodePath(root, node) {
  if (!root || !node) return null;
  if (node === root) return [];

  const path = [];
  let current = node;

  while (current && current !== root) {
    const parent = current.parentNode;
    if (!parent) return null;
    const index = Array.prototype.indexOf.call(parent.childNodes, current);
    if (index < 0) return null;
    path.unshift(index);
    current = parent;
  }

  return current === root ? path : null;
}

function resolveSelectionNodePath(root, path) {
  if (!root || !Array.isArray(path)) return null;
  let current = root;

  for (const index of path) {
    current = current?.childNodes?.[index] || null;
    if (!current) return null;
  }

  return current;
}

function clampSelectionOffset(node, requestedOffset) {
  const value = Math.max(0, Number(requestedOffset || 0));
  if (!node) return 0;
  if (node.nodeType === 3) {
    return Math.min(String(node.nodeValue || "").length, value);
  }
  return Math.min(Number(node.childNodes?.length || 0), value);
}

function captureSelectionOffsets(root) {
  if (
    !root ||
    typeof window === "undefined" ||
    typeof window.getSelection !== "function"
  ) {
    return null;
  }

  const selection = window.getSelection();
  if (
    !selection ||
    selection.rangeCount === 0 ||
    !root.contains(selection.anchorNode) ||
    !root.contains(selection.focusNode)
  ) {
    return null;
  }

  const measureOffset = (node, offset) => {
    const range = root.ownerDocument.createRange();
    range.selectNodeContents(root);

    try {
      range.setEnd(node, offset);
      return range.toString().length;
    } catch (_) {
      return 0;
    }
  };

  return {
    anchor: measureOffset(selection.anchorNode, selection.anchorOffset),
    focus: measureOffset(selection.focusNode, selection.focusOffset),
    anchorPath: getSelectionNodePath(root, selection.anchorNode),
    focusPath: getSelectionNodePath(root, selection.focusNode),
    anchorNodeOffset: selection.anchorOffset,
    focusNodeOffset: selection.focusOffset,
    backward:
      selection.anchorNode !== selection.focusNode ||
      selection.anchorOffset !== selection.focusOffset
        ? (() => {
            try {
              const range = root.ownerDocument.createRange();
              range.setStart(selection.anchorNode, selection.anchorOffset);
              range.setEnd(selection.focusNode, selection.focusOffset);
              return range.collapsed;
            } catch (_) {
              return false;
            }
          })()
        : false,
  };
}

function resolveTextPosition(root, requestedOffset) {
  const targetOffset = Math.max(0, Number(requestedOffset || 0));
  const documentValue = root?.ownerDocument;

  if (!root || !documentValue) return null;

  const walker = documentValue.createTreeWalker(
    root,
    window.NodeFilter.SHOW_TEXT
  );

  let consumed = 0;
  let node = walker.nextNode();
  let lastTextNode = null;

  while (node) {
    const length = String(node.nodeValue || "").length;

    if (consumed + length >= targetOffset) {
      return {
        node,
        offset: Math.max(0, Math.min(length, targetOffset - consumed)),
      };
    }

    consumed += length;
    lastTextNode = node;
    node = walker.nextNode();
  }

  if (lastTextNode) {
    return {
      node: lastTextNode,
      offset: String(lastTextNode.nodeValue || "").length,
    };
  }

  const firstPage = root.querySelector('[data-doc-canvas-page="true"]');
  return firstPage ? { node: firstPage, offset: 0 } : { node: root, offset: 0 };
}

function restoreSelectionOffsets(root, snapshot) {
  if (
    !root ||
    !snapshot ||
    typeof window === "undefined" ||
    typeof window.getSelection !== "function"
  ) {
    return;
  }

  const pathAnchorNode = resolveSelectionNodePath(root, snapshot.anchorPath);
  const pathFocusNode = resolveSelectionNodePath(root, snapshot.focusPath);

  const anchor = pathAnchorNode
    ? {
        node: pathAnchorNode,
        offset: clampSelectionOffset(
          pathAnchorNode,
          snapshot.anchorNodeOffset
        ),
      }
    : resolveTextPosition(root, snapshot.anchor);

  const focus = pathFocusNode
    ? {
        node: pathFocusNode,
        offset: clampSelectionOffset(
          pathFocusNode,
          snapshot.focusNodeOffset
        ),
      }
    : resolveTextPosition(root, snapshot.focus);

  if (!anchor || !focus) return;

  try {
    const selection = window.getSelection();
    selection.removeAllRanges();

    if (typeof selection.setBaseAndExtent === "function") {
      selection.setBaseAndExtent(
        anchor.node,
        anchor.offset,
        focus.node,
        focus.offset
      );
      return;
    }

    const range = root.ownerDocument.createRange();
    if (snapshot.backward) {
      range.setStart(focus.node, focus.offset);
      range.setEnd(anchor.node, anchor.offset);
    } else {
      range.setStart(anchor.node, anchor.offset);
      range.setEnd(focus.node, focus.offset);
    }
    selection.addRange(range);
  } catch (_) {
    // Restoring the caret is best effort and must never block editing.
  }
}

function createCaretMarker(documentValue, markerId) {
  const marker = documentValue.createElement("span");
  marker.setAttribute(CANVAS_CARET_MARKER_ATTR, markerId);
  marker.setAttribute("aria-hidden", "true");
  marker.textContent = "\u2060";
  marker.style.display = "inline";
  marker.style.width = "0";
  marker.style.height = "0";
  marker.style.padding = "0";
  marker.style.margin = "0";
  marker.style.border = "0";
  marker.style.lineHeight = "0";
  marker.style.fontSize = "0";
  marker.style.overflow = "hidden";
  return marker;
}

function installCaretMarker(root) {
  if (!root || typeof window === "undefined") return null;

  const selection = window.getSelection?.();
  if (
    !selection ||
    selection.rangeCount === 0 ||
    !selection.isCollapsed ||
    !root.contains(selection.anchorNode)
  ) {
    return null;
  }

  try {
    const markerId = createDocumentObjectId("caret");
    const marker = createCaretMarker(root.ownerDocument, markerId);
    const range = selection.getRangeAt(0).cloneRange();
    range.collapse(true);
    range.insertNode(marker);

    const restoreRange = root.ownerDocument.createRange();
    restoreRange.setStartAfter(marker);
    restoreRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(restoreRange);

    return { marker, markerId };
  } catch (_) {
    return null;
  }
}

function removeLiveCaretMarker(marker) {
  if (!marker?.parentNode) return false;

  try {
    const parent = marker.parentNode;
    const index = Array.prototype.indexOf.call(parent.childNodes, marker);
    marker.remove();

    const selection = parent.ownerDocument.defaultView?.getSelection?.();
    if (!selection) return true;
    const range = parent.ownerDocument.createRange();
    range.setStart(parent, Math.max(0, Math.min(index, parent.childNodes.length)));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch (_) {
    try { marker.remove(); } catch (_) {}
    return false;
  }
}

function restoreCaretFromMarker(root, markerId) {
  if (!root || !markerId) return false;

  const marker = root.querySelector(
    `[${CANVAS_CARET_MARKER_ATTR}="${escapeCssValue(markerId)}"]`
  );
  if (!marker?.parentNode) return false;

  try {
    const parent = marker.parentNode;
    const index = Array.prototype.indexOf.call(parent.childNodes, marker);
    marker.remove();

    const selection = root.ownerDocument.defaultView?.getSelection?.();
    if (!selection) return true;
    const range = root.ownerDocument.createRange();
    range.setStart(parent, Math.max(0, Math.min(index, parent.childNodes.length)));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch (_) {
    try { marker.remove(); } catch (_) {}
    return false;
  }
}

function pageEndsWithHardBreak(page) {
  if (!page) return false;
  const children = Array.from(page.children || []);
  for (let index = children.length - 1; index >= 0; index -= 1) {
    const child = children[index];
    if (isPageBreakElement(child)) return true;
    if (nodeHasRenderableContent(child)) return false;
  }
  return false;
}

function isIntentionalBlankPageAfterBreak(page, pages = []) {
  if (!page || pageHasMeaningfulContent(page)) return false;
  const index = pages.indexOf(page);
  if (index <= 0) return false;
  return pageEndsWithHardBreak(pages[index - 1]);
}

function canvasInteractionIsMoving(editor) {
  if (!editor?.querySelector) return false;
  return Boolean(
    editor.querySelector(
      [
        `[${CANVAS_VISUAL_DRAGGING_ATTR}="true"]`,
        `[${CANVAS_TABLE_DRAGGING_ATTR}="true"]`,
        '[data-doc-dragging="true"]',
      ].join(",")
    )
  );
}

function protectedVisualRenderSignature(block) {
  if (!block) return "";
  return JSON.stringify([
    String(
      block.getAttribute("data-doc-block-type") ||
        block.getAttribute("data-doc-visual-type") ||
        ""
    ).toLowerCase(),
    block.getAttribute(DOC_BLOCK_SOURCE_ATTR) || "",
    block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) || "",
    block.getAttribute("data-doc-block-spec") || "",
    block.getAttribute("data-doc-visual-width") || "",
    block.getAttribute("data-doc-visual-height") || "",
  ]);
}

function captureReusableVisualNodes(editor) {
  const reusable = new Map();
  if (!editor?.querySelectorAll) return reusable;

  editor
    .querySelectorAll(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)
    .forEach((block) => {
      const blockId = block.getAttribute(DOC_BLOCK_ID_ATTR) || "";
      if (!blockId || reusable.has(blockId)) return;
      reusable.set(blockId, {
        block,
        signature: protectedVisualRenderSignature(block),
      });
    });

  return reusable;
}

function reuseLiveVisualNodesInSource(source, reusableVisualNodes) {
  if (!source?.querySelectorAll || !reusableVisualNodes?.size) return 0;
  let reused = 0;

  source
    .querySelectorAll(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)
    .forEach((placeholder) => {
      const blockId = placeholder.getAttribute(DOC_BLOCK_ID_ATTR) || "";
      const entry = blockId ? reusableVisualNodes.get(blockId) : null;
      if (!entry?.block) return;

      // Never preserve a stale React root across a genuine chart/math edit.
      // Reuse is only safe when the canonical render inputs are unchanged.
      if (entry.signature !== protectedVisualRenderSignature(placeholder)) return;

      const groupId = placeholder.getAttribute(CANVAS_GROUP_ATTR) || "";
      if (groupId) entry.block.setAttribute(CANVAS_GROUP_ATTR, groupId);
      else entry.block.removeAttribute(CANVAS_GROUP_ATTR);

      placeholder.replaceWith(entry.block);
      reused += 1;
    });

  return reused;
}

function createCanvasPage(documentValue, pageNumber) {
  const page = documentValue.createElement("div");
  page.className = "doc-canvas-page";
  page.setAttribute("data-doc-canvas-page", "true");
  page.setAttribute("data-page-number", String(pageNumber));
  page.setAttribute("aria-label", `Document page ${pageNumber}`);
  return page;
}

const DIRECT_CANVAS_INLINE_PROSE_TAGS = new Set([
  "A",
  "ABBR",
  "B",
  "BDI",
  "BDO",
  "CITE",
  "CODE",
  "DEL",
  "EM",
  "I",
  "INS",
  "KBD",
  "MARK",
  "Q",
  "S",
  "SAMP",
  "SMALL",
  "SPAN",
  "STRONG",
  "SUB",
  "SUP",
  "TIME",
  "U",
  "VAR",
]);

function isCanvasEditorChromeElement(element) {
  if (!element || element.nodeType !== 1) return false;

  // A real document TABLE is never editor chrome, even if an older runtime
  // accidentally left a capability/UI marker on it.
  if (element.tagName === "TABLE") return false;

  if (element.getAttribute("data-doc-editor-only") === "true") return true;
  if (element.hasAttribute("data-doc-writer-table-ui")) return true;
  if (element.hasAttribute("data-doc-table-move-handle")) return true;
  if (element.hasAttribute("data-doc-table-resize-handle")) return true;
  if (element.hasAttribute("data-doc-table-insert-control")) return true;
  if (element.hasAttribute("data-doc-visual-move-handle")) return true;
  if (element.hasAttribute("data-doc-visual-drag-handle")) return true;
  if (element.hasAttribute("data-doc-visual-resize-handle")) return true;
  if (element.hasAttribute("data-doc-visual-drop-marker")) return true;

  return Boolean(
    element.matches?.(
      [
        ".doc-canvas-resize-handle",
        ".doc-canvas-visual-drag-handle",
        ".doc-canvas-visual-drop-marker",
        ".doc-writer-chart-edit-overlay",
        ".docwriter-math-controls",
        ".docwriter-math-edit-panel",
      ].join(",")
    )
  );
}

function createCanvasProseParagraph(documentValue, { empty = true } = {}) {
  if (!documentValue) return null;
  const paragraph = documentValue.createElement("p");
  if (empty) paragraph.appendChild(documentValue.createElement("br"));
  return paragraph;
}

function isCanvasEditableFlowElement(element) {
  if (!element || element.nodeType !== 1) return false;
  if (isCanvasEditorChromeElement(element)) return false;
  if (isPageBreakElement(element)) return false;
  if (isAtomicCanvasObject(element)) return false;
  if (element.tagName === "TABLE") return false;
  if (
    ["HR", "IMG", "VIDEO", "AUDIO", "SVG", "CANVAS", "IFRAME", "OBJECT", "EMBED", "TEMPLATE"].includes(
      element.tagName
    )
  ) {
    return false;
  }
  if (String(element.getAttribute("contenteditable") || "").toLowerCase() === "false") {
    return false;
  }
  return true;
}

function normalizeDirectCanvasProseChildren(container) {
  if (!container?.ownerDocument) return false;

  let changed = false;
  let activeParagraph = null;
  const nodes = Array.from(container.childNodes || []);

  const ensureParagraphBefore = (node) => {
    if (activeParagraph?.parentNode === container) return activeParagraph;
    activeParagraph = createCanvasProseParagraph(container.ownerDocument, { empty: false });
    container.insertBefore(activeParagraph, node || null);
    changed = true;
    return activeParagraph;
  };

  nodes.forEach((node) => {
    if (!node || node.parentNode !== container) return;

    if (node.nodeType === 3) {
      const raw = String(node.nodeValue || "");
      if (!raw.replace(/\u00a0/g, " ").trim()) {
        if (activeParagraph) {
          activeParagraph.appendChild(node);
        } else {
          node.remove();
          changed = true;
        }
        return;
      }

      ensureParagraphBefore(node).appendChild(node);
      changed = true;
      return;
    }

    if (node.nodeType !== 1) {
      activeParagraph = null;
      return;
    }

    const element = node;
    if (isCanvasEditorChromeElement(element) || isAtomicCanvasObject(element)) {
      activeParagraph = null;
      return;
    }

    if (element.tagName === "BR") {
      ensureParagraphBefore(element).appendChild(element);
      changed = true;
      return;
    }

    if (
      DIRECT_CANVAS_INLINE_PROSE_TAGS.has(element.tagName) &&
      String(element.getAttribute("contenteditable") || "").toLowerCase() !== "false"
    ) {
      ensureParagraphBefore(element).appendChild(element);
      changed = true;
      return;
    }

    activeParagraph = null;
  });

  return changed;
}


const INGRESS_PAGE_FRAME_TAGS = new Set(["DIV", "SECTION", "ARTICLE", "MAIN"]);
const INGRESS_BASELINE_FLOW_TAGS = new Set([
  "ADDRESS",
  "DIV",
  "SECTION",
  "ARTICLE",
  "MAIN",
  "HEADER",
  "FOOTER",
  "P",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
]);

function setImportantStyle(element, property, value) {
  if (!element?.style) return;
  try {
    element.style.setProperty(property, value, "important");
  } catch (_) {}
}

function neutralizeIngressPageFrame(element, { vertical = false } = {}) {
  if (!element?.style) return false;

  [
    "margin-left",
    "margin-right",
    "padding-left",
    "padding-right",
  ].forEach((property) => setImportantStyle(element, property, "0"));

  setImportantStyle(element, "width", "auto");
  setImportantStyle(element, "min-width", "0");
  setImportantStyle(element, "max-width", "none");
  setImportantStyle(element, "left", "auto");
  setImportantStyle(element, "right", "auto");
  setImportantStyle(element, "transform", "none");

  if (vertical) {
    [
      "margin-top",
      "margin-bottom",
      "padding-top",
      "padding-bottom",
    ].forEach((property) => setImportantStyle(element, property, "0"));
  }

  const position = String(element.style.position || "").trim().toLowerCase();
  if (["absolute", "fixed", "sticky"].includes(position)) {
    setImportantStyle(element, "position", "static");
    setImportantStyle(element, "top", "auto");
    setImportantStyle(element, "bottom", "auto");
  }

  return true;
}

function normalizeCanvasIngressLayout(root) {
  if (!root?.children) return false;
  let changed = false;

  const meaningfulChildren = (parent) =>
    Array.from(parent?.children || []).filter(
      (child) =>
        !isCanvasEditorChromeElement(child) &&
        !isPageBreakElement(child) &&
        !isAtomicCanvasObject(child) &&
        child.tagName !== "TEMPLATE"
    );

  // AI/converted HTML often arrives inside one or more page/body wrappers that
  // already carry their own margin/padding/width. The Canvas page itself owns the
  // physical 0.75in Letter margin, so a second document frame must be neutralized.
  let shellParent = root;
  for (let depth = 0; depth < 4; depth += 1) {
    const children = meaningfulChildren(shellParent);
    if (children.length !== 1) break;

    const candidate = children[0];
    if (!INGRESS_PAGE_FRAME_TAGS.has(candidate.tagName)) break;

    changed =
      neutralizeIngressPageFrame(candidate, { vertical: true }) || changed;
    shellParent = candidate;
  }

  // Some generators repeat the same horizontal page inset on every top-level
  // paragraph/heading instead of using one wrapper. Remove only a shared baseline
  // inset; lists, blockquotes, tables, visuals, and one-off indentation are left alone.
  const baselineBlocks = Array.from(shellParent?.children || []).filter((element) => {
    if (!INGRESS_BASELINE_FLOW_TAGS.has(element.tagName)) return false;
    if (isPageBreakElement(element) || isAtomicCanvasObject(element)) return false;
    if (element.closest?.("blockquote, ul, ol, table")) return false;
    return true;
  });

  const normalizedInset = (value) => {
    const raw = String(value || "").trim().toLowerCase();
    if (!raw || /^(?:0|0px|0pt|0em|0rem|0in|auto)$/.test(raw)) return "";
    return raw;
  };

  const normalizeSharedSide = (marginProperty, paddingProperty) => {
    if (baselineBlocks.length < 2) return;

    const groups = new Map();
    baselineBlocks.forEach((element) => {
      const margin = normalizedInset(element.style?.[marginProperty]);
      const padding = normalizedInset(element.style?.[paddingProperty]);
      if (!margin && !padding) return;
      const signature = `${margin}|${padding}`;
      const current = groups.get(signature) || [];
      current.push(element);
      groups.set(signature, current);
    });

    const winner = Array.from(groups.values()).sort(
      (a, b) => b.length - a.length
    )[0];
    const minimumShared = Math.max(2, Math.ceil(baselineBlocks.length * 0.6));
    if (!winner || winner.length < minimumShared) return;

    const cssMargin =
      marginProperty === "marginLeft" ? "margin-left" : "margin-right";
    const cssPadding =
      paddingProperty === "paddingLeft" ? "padding-left" : "padding-right";

    winner.forEach((element) => {
      setImportantStyle(element, cssMargin, "0");
      setImportantStyle(element, cssPadding, "0");
    });
    changed = true;
  };

  normalizeSharedSide("marginLeft", "paddingLeft");
  normalizeSharedSide("marginRight", "paddingRight");

  return changed;
}

function pageHasMeaningfulContent(page) {
  if (!page) return false;

  const visualElements = Array.from(
    page.querySelectorAll(
      `img, table, figure, [${DOC_PROTECTED_BLOCK_ATTR}="true"], video, audio, svg, canvas, hr, blockquote, pre, ul, ol`
    )
  ).filter((element) => !isPageBreakElement(element));

  if (visualElements.length) return true;

  return Boolean(
    String(page.textContent || "")
      .replace(/\u00a0/g, " ")
      .trim()
  );
}

function pageIsOverflowing(page) {
  if (!page) return false;

  return (
    Number(page.scrollHeight || 0) -
      Number(page.clientHeight || 0) >
    PAGE_OVERFLOW_TOLERANCE_PX
  );
}

function pageContentViewportRect(page) {
  if (!page?.getBoundingClientRect) return null;
  const rect = page.getBoundingClientRect();
  let paddingTop = 0;
  let paddingRight = 0;
  let paddingBottom = 0;
  let paddingLeft = 0;
  try {
    const style = page.ownerDocument?.defaultView?.getComputedStyle?.(page);
    paddingTop = Number.parseFloat(style?.paddingTop || "0") || 0;
    paddingRight = Number.parseFloat(style?.paddingRight || "0") || 0;
    paddingBottom = Number.parseFloat(style?.paddingBottom || "0") || 0;
    paddingLeft = Number.parseFloat(style?.paddingLeft || "0") || 0;
  } catch (_) {}

  // The live Canvas is intentionally rendered at 1:1 CSS geometry. Page padding
  // and getBoundingClientRect() therefore share the same coordinate space; no
  // transform/zoom correction is allowed here.
  return {
    top: rect.top + paddingTop,
    right: rect.right - paddingRight,
    bottom: rect.bottom - paddingBottom,
    left: rect.left + paddingLeft,
  };
}

function atomicNodeEscapesPageContent(node, page) {
  if (!node || !page || node.nodeType !== 1 || !isAtomicCanvasObject(node)) {
    return false;
  }
  const contentRect = pageContentViewportRect(page);
  const rect = node.getBoundingClientRect?.();
  if (!contentRect || !rect || !rect.width || !rect.height) return false;
  const tolerance = 2;
  return (
    rect.bottom > contentRect.bottom + tolerance ||
    rect.top < contentRect.top - tolerance ||
    rect.right > contentRect.right + tolerance ||
    rect.left < contentRect.left - tolerance
  );
}

function pageHasAtomicOverflow(page) {
  if (!page?.querySelectorAll) return false;
  const candidates = [
    ...Array.from(page.children || []).filter((child) => isAtomicCanvasObject(child)),
    ...Array.from(
      page.querySelectorAll(`table, [${DOC_PROTECTED_BLOCK_ATTR}="true"]`)
    ),
  ];
  return Array.from(new Set(candidates)).some((node) =>
    atomicNodeEscapesPageContent(node, page)
  );
}

function captureOuterScrollState(node, excluded = null) {
  const entries = [];
  let current = node?.parentElement || null;
  while (current && current !== document.body && current !== document.documentElement) {
    if (current !== excluded) {
      try {
        const style = current.ownerDocument?.defaultView?.getComputedStyle?.(current);
        const overflowY = String(style?.overflowY || "");
        const overflowX = String(style?.overflowX || "");
        const scrollableY = /(auto|scroll|overlay)/.test(overflowY) && current.scrollHeight > current.clientHeight + 1;
        const scrollableX = /(auto|scroll|overlay)/.test(overflowX) && current.scrollWidth > current.clientWidth + 1;
        if (scrollableY || scrollableX) {
          entries.push({ element: current, top: current.scrollTop, left: current.scrollLeft });
        }
      } catch (_) {}
    }
    current = current.parentElement;
  }
  return {
    entries,
    windowX: typeof window !== "undefined" ? window.scrollX || 0 : 0,
    windowY: typeof window !== "undefined" ? window.scrollY || 0 : 0,
  };
}

function restoreOuterScrollState(snapshot) {
  if (!snapshot) return;
  (snapshot.entries || []).forEach((entry) => {
    if (!entry?.element?.isConnected) return;
    entry.element.scrollTop = Number(entry.top || 0);
    entry.element.scrollLeft = Number(entry.left || 0);
  });
  if (typeof window !== "undefined") {
    try { window.scrollTo(Number(snapshot.windowX || 0), Number(snapshot.windowY || 0)); } catch (_) {}
  }
}

function canvasNeedsRepagination(editor) {
  if (!editor) return false;

  const pages = Array.from(
    editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
  );

  if (!pages.length) return true;
  if (pages.some((page) => pageIsOverflowing(page) || pageHasAtomicOverflow(page))) {
    return true;
  }

  const hasUnsettledHardBreak = pages.some((page) => {
    let passedBreak = false;
    return Array.from(page.childNodes || []).some((node) => {
      if (node.nodeType === 1 && isPageBreakElement(node)) {
        passedBreak = true;
        return false;
      }
      return passedBreak && nodeHasRenderableContent(node);
    });
  });
  if (hasUnsettledHardBreak) return true;

  if (
    pages.length > 1 &&
    !pageHasMeaningfulContent(pages[pages.length - 1]) &&
    !isIntentionalBlankPageAfterBreak(pages[pages.length - 1], pages)
  ) {
    return true;
  }

  // Do not probe backflow by cloning the next page into the live page. That
  // temporary DOM mutation is observable by chart renderers and was a major
  // source of left/right and up/down visual oscillation. Deletions and explicit
  // structural edits request a deliberate compaction pass instead.
  return false;
}

function nodeHasRenderableContent(node) {
  if (!node) return false;
  if (node.nodeType === 3) {
    return Boolean(String(node.nodeValue || "").replace(/\u00a0/g, " ").trim());
  }
  if (node.nodeType !== 1) return false;
  if (isPageBreakElement(node)) return true;
  return !isBoundaryEmpty(node);
}

function getMeaningfulChildNodes(element) {
  return Array.from(element?.childNodes || []).filter((child) => {
    if (child.nodeType === 3) {
      return Boolean(String(child.nodeValue || "").replace(/\u00a0/g, " ").trim());
    }
    return child.nodeType === 1;
  });
}

function canSplitStructuralElement(element) {
  if (!element || element.nodeType !== 1) return false;
  if (isProtectedVisualElement(element)) return false;
  if (!STRUCTURAL_CONTAINER_TAGS.has(element.tagName)) return false;

  // IMPORTANT: an ordinary DIV/SECTION is still a document-flow wrapper even
  // when it contains a table, chart, math block, or image somewhere below it.
  // Treating that whole wrapper as atomic collapses a multi-page document into
  // one oversized Page 1. The protected CHILD remains atomic; the wrapper does not.
  const inlineStyle = String(element.getAttribute("style") || "");
  if (/display\s*:\s*(?:flex|inline-flex|grid|inline-grid|table)/i.test(inlineStyle)) {
    return false;
  }

  const children = getMeaningfulChildNodes(element);
  return children.some(
    (child) =>
      child.nodeType === 1 &&
      (FLOW_BLOCK_TAGS.has(child.tagName) ||
        isPageBreakElement(child) ||
        isAtomicCanvasObject(child))
  );
}

function splitStructuralElementIntoFragments(element, depth = 0) {
  if (!canSplitStructuralElement(element) || depth > 12) return null;

  // This splitter intentionally MOVES existing child nodes into shallow wrapper
  // shells instead of cloneNode(true). Moving preserves mounted React roots and
  // DocWriterTable DOM identity while still allowing an ordinary document wrapper
  // to flow across Letter pages. Chart/Table/Math/Image are later placed atomically
  // by placeNode(), so they can never be cut between pages.
  const children = getMeaningfulChildNodes(element);
  const fragments = [];

  children.forEach((child, index) => {
    if (child.nodeType === 1 && isPageBreakElement(child)) {
      fragments.push(child);
      return;
    }

    const nested =
      child.nodeType === 1 && !isAtomicCanvasObject(child)
        ? splitStructuralElementIntoFragments(child, depth + 1)
        : null;
    const childFragments = nested?.length ? nested : [child];

    childFragments.forEach((childFragment, childIndex) => {
      const shell = element.cloneNode(false);
      if (index > 0 || childIndex > 0 || fragments.length > 0) {
        shell.removeAttribute("id");
      }
      shell.appendChild(childFragment);
      fragments.push(shell);
    });
  });

  return fragments.length > 1 ? fragments : null;
}

function splitListIntoFragments(list) {
  if (!list || !["UL", "OL"].includes(list.tagName)) return null;
  if (list.querySelector?.(`table, [${DOC_PROTECTED_BLOCK_ATTR}="true"]`)) {
    return null;
  }

  const items = Array.from(list.children || []).filter(
    (child) => child.tagName === "LI"
  );

  if (items.length <= 1) return null;

  const originalStart = Math.max(
    1,
    Number.parseInt(list.getAttribute("start") || "1", 10) || 1
  );

  return items.map((item, index) => {
    const fragment = list.cloneNode(false);
    if (index > 0) fragment.removeAttribute("id");
    if (fragment.tagName === "OL") {
      fragment.setAttribute("start", String(originalStart + index));
    }
    fragment.appendChild(item.cloneNode(true));
    return fragment;
  });
}

// Tables are intentionally not split by DocCanvasEditor. DocWriterTable owns
// table structure, while Canvas pagination treats each logical table as one atomic
// document object and moves it intact to the next page when necessary.

function resolveTextOffsetWithin(root, requestedOffset) {
  if (!root) return null;

  const documentValue = root.ownerDocument;
  const nodeFilter =
    documentValue.defaultView?.NodeFilter ||
    (typeof window !== "undefined" ? window.NodeFilter : null);

  if (!nodeFilter) return null;

  const walker = documentValue.createTreeWalker(
    root,
    nodeFilter.SHOW_TEXT
  );

  const target = Math.max(0, Number(requestedOffset || 0));
  let consumed = 0;
  let current = walker.nextNode();
  let last = null;

  while (current) {
    const length = String(current.nodeValue || "").length;
    if (consumed + length >= target) {
      return {
        node: current,
        offset: Math.max(0, Math.min(length, target - consumed)),
      };
    }
    consumed += length;
    last = current;
    current = walker.nextNode();
  }

  return last
    ? { node: last, offset: String(last.nodeValue || "").length }
    : null;
}

function cloneTextSlice(element, startOffset, endOffset, keepId) {
  const start = resolveTextOffsetWithin(element, startOffset);
  const end = resolveTextOffsetWithin(element, endOffset);
  if (!start || !end) return null;

  try {
    const range = element.ownerDocument.createRange();
    range.setStart(start.node, start.offset);
    range.setEnd(end.node, end.offset);

    const shell = element.cloneNode(false);
    if (!keepId) shell.removeAttribute("id");
    shell.appendChild(range.cloneContents());
    return shell;
  } catch (_) {
    return null;
  }
}

function canSplitElementByText(element) {
  if (!element || element.nodeType !== 1) return false;
  if (isProtectedVisualElement(element)) return false;
  if (!TEXT_SPLITTABLE_TAGS.has(element.tagName)) return false;
  if (
    element.querySelector(
      `img, table, ul, ol, video, audio, svg, canvas, iframe, object, embed, [${DOC_PROTECTED_BLOCK_ATTR}="true"]`
    )
  ) {
    return false;
  }

  return String(element.textContent || "").length > 1;
}

function chooseReadableTextBreak(text, start, best, total) {
  if (best >= total) return total;

  const minimum = Math.max(start + 1, best - 120);
  const slice = text.slice(minimum, best + 1);
  const matches = Array.from(slice.matchAll(/[\s,.;:!?\-–—)]/g));
  const last = matches[matches.length - 1];

  if (!last) return best;

  const candidate = minimum + Number(last.index || 0) + 1;
  return candidate > start ? candidate : best;
}

function isPersistedCanvasPageWrapper(node) {
  if (!node || node.nodeType !== 1) return false;
  return (
    node.getAttribute?.(DOCWRITER_EXPORT_PAGE_ATTR) === "true" ||
    node.classList?.contains(DOCWRITER_EXPORT_PAGE_CLASS)
  );
}

function restorePersistedCanvasPageMapIntoEditor(
  editor,
  value,
  { reusableVisualNodes = null, normalizeIngressLayout = false } = {}
) {
  if (!editor) return 0;

  const documentValue = editor.ownerDocument;
  const source = documentValue.createElement("div");
  source.innerHTML = String(value || "");

  const initialPages = Array.from(source.children || []).filter(
    (child) => isPersistedCanvasPageWrapper(child)
  );
  if (!initialPages.length) return 0;

  // A persisted Canvas page map is an explicit document-layout contract.
  // Clean runtime/editor chrome, but never flatten, merge, compact, or repaginate
  // across these wrappers. In particular, an empty wrapper is a real blank page.
  removeUnsafeMarkup(source);
  stripDocWriterEditorOnlyMarkup(source);
  stripCanvasOnlyMarkup(source, { preserveCaretMarkers: true });

  const persistedPages = Array.from(source.children || []).filter(
    (child) => isPersistedCanvasPageWrapper(child)
  );
  if (!persistedPages.length) return 0;

  if (normalizeIngressLayout) {
    persistedPages.forEach((page) => normalizeCanvasIngressLayout(page));
  }
  persistedPages.forEach((page) => normalizeManualPageBreaks(page));

  // Normalize each page independently so a table/visual on Page N can never be
  // reassembled with or pulled into Page N-1 merely because that page has room.
  persistedPages.forEach((page) => {
    normalizeDocumentObjects(page);
    ensureCanvasTableVisibility(page);
    normalizeDirectCanvasProseChildren(page);
  });

  // Preserve mounted Chart/Math/Image roots when the parent echoes a saved,
  // previewed, or finalized page-aware document back into the live Canvas.
  reuseLiveVisualNodesInSource(source, reusableVisualNodes);

  editor.replaceChildren();

  persistedPages.forEach((persistedPage, index) => {
    const canvasPage = createCanvasPage(documentValue, index + 1);
    canvasPage.setAttribute(
      "data-placeholder",
      editor.getAttribute("data-placeholder") || ""
    );

    while (persistedPage.firstChild) {
      canvasPage.appendChild(persistedPage.firstChild);
    }

    normalizeDirectCanvasProseChildren(canvasPage);
    ensureCanvasTableVisibility(canvasPage);

    if (pageIsOverflowing(canvasPage) || pageHasAtomicOverflow(canvasPage)) {
      canvasPage.setAttribute(CANVAS_OVERFLOW_ATTR, "true");
    } else {
      canvasPage.removeAttribute(CANVAS_OVERFLOW_ATTR);
    }

    editor.appendChild(canvasPage);
  });

  if (!editor.querySelector(':scope > [data-doc-canvas-page="true"]')) {
    const page = createCanvasPage(documentValue, 1);
    page.setAttribute(
      "data-placeholder",
      editor.getAttribute("data-placeholder") || ""
    );
    editor.appendChild(page);
  }

  return Math.max(
    1,
    editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]').length
  );
}


function paginateHtmlIntoEditor(
  editor,
  value,
  { reusableVisualNodes = null, normalizeIngressLayout = false } = {}
) {
  if (!editor) return 1;

  const restoredPageCount = restorePersistedCanvasPageMapIntoEditor(
    editor,
    value,
    { reusableVisualNodes, normalizeIngressLayout }
  );
  if (restoredPageCount > 0) return restoredPageCount;

  const normalized = normalizeDocumentHtml(value, {
    preserveCaretMarkers: true,
    preserveEditingBoundaries: true,
  });
  const documentValue = editor.ownerDocument;
  const source = documentValue.createElement("div");
  source.innerHTML = normalized;

  // Serialized/reopened HTML must never carry a second set of object controls
  // back into the live Canvas. Chart/Table/Math/Image render their own controls
  // exactly once after hydration.
  stripDocWriterEditorOnlyMarkup(source);

  // Table-only hardening: normalize the pagination source itself before any
  // table-preservation bookkeeping. This gives AI-generated native/Markdown
  // tables canonical IDs and visible presentation even when the incoming page
  // contains nothing except the table.
  try {
    reassembleAllTablesWithDocWriterTable(source, {
      removeFragments: true,
      normalize: true,
    });
  } catch (_) {}
  try {
    normalizeTablesWithDocWriterTable(source, {
      source: "reopened",
      inferWidths: true,
    });
  } catch (_) {}
  ensureCanvasTableVisibility(source);

  stripCanvasOnlyMarkup(source, { preserveCaretMarkers: true });

  if (normalizeIngressLayout) normalizeCanvasIngressLayout(source);
  normalizeManualPageBreaks(source);

  // Keep references to every logical table before pagination starts. Normal
  // placement should preserve them naturally; this map is a final data-loss
  // guard so a paginator edge case can never make an existing/manual table
  // vanish from the Canvas.
  const sourceTablesById = new Map();
  source
    .querySelectorAll(`table[${DOC_TABLE_ID_ATTR}]`)
    .forEach((table) => {
      const tableId = String(table.getAttribute(DOC_TABLE_ID_ATTR) || "");
      if (tableId && !sourceTablesById.has(tableId)) {
        sourceTablesById.set(tableId, table);
      }
    });

  // Ordinary prose must never paginate as a bare direct text/inline node. Browsers
  // can create that shape when the caret sits beside a contenteditable=false object;
  // wrapping it here keeps every line inside the Letter page's 0.75in content box.
  normalizeDirectCanvasProseChildren(source);

  let groupSequence = 0;
  source.querySelectorAll("*").forEach((element) => {
    if (!isPageBreakElement(element)) {
      groupSequence += 1;
      element.setAttribute(CANVAS_GROUP_ATTR, `canvas-${groupSequence}`);
    }
  });

  // Move already-mounted chart/math blocks into the new pagination source rather
  // than cloning and remounting them. React roots remain alive, their measured
  // geometry remains authoritative, and pagination cannot make a chart collapse
  // to a different temporary height. New/changed visuals are left as normal
  // source nodes and the Editor's direct DocWriterChart/DocWriterMath mount pass
  // handles only genuinely new or changed visual blocks after pagination.
  reuseLiveVisualNodesInSource(source, reusableVisualNodes);

  const nodes = Array.from(source.childNodes || []);
  editor.replaceChildren();

  let pageNumber = 1;
  let currentPage = createCanvasPage(documentValue, pageNumber);
  currentPage.setAttribute(
    "data-placeholder",
    editor.getAttribute("data-placeholder") || ""
  );
  editor.appendChild(currentPage);

  const startNewPage = () => {
    pageNumber += 1;
    currentPage = createCanvasPage(documentValue, pageNumber);
    editor.appendChild(currentPage);
    return currentPage;
  };

  const appendAndFits = (node) => {
    currentPage.appendChild(node);
    const fits = !pageIsOverflowing(currentPage);
    if (!fits) node.remove();
    return fits;
  };

  const splitTextAcrossPages = (element) => {
    if (!canSplitElementByText(element)) return false;

    const text = String(element.textContent || "");
    const total = text.length;
    let start = 0;
    let chunkIndex = 0;
    let safety = 0;

    while (start < total && safety < 500) {
      safety += 1;
      let low = start + 1;
      let high = total;
      let best = start;

      while (low <= high) {
        const middle = Math.floor((low + high) / 2);
        const candidate = cloneTextSlice(
          element,
          start,
          middle,
          chunkIndex === 0
        );

        if (!candidate) {
          high = middle - 1;
          continue;
        }

        currentPage.appendChild(candidate);
        const fits = !pageIsOverflowing(currentPage);
        candidate.remove();

        if (fits) {
          best = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }

      if (best <= start) {
        if (pageHasMeaningfulContent(currentPage)) {
          startNewPage();
          continue;
        }
        return false;
      }

      const end = chooseReadableTextBreak(text, start, best, total);
      const finalChunk = cloneTextSlice(
        element,
        start,
        end,
        chunkIndex === 0
      );

      if (!finalChunk) return false;
      currentPage.appendChild(finalChunk);
      start = end;
      chunkIndex += 1;

      if (start < total) startNewPage();
    }

    return start >= total;
  };

  const placeNode = (node, depth = 0) => {
    if (!node || depth > 18) return;

    if (node.nodeType === 3 && !nodeHasRenderableContent(node)) return;

    if (node.nodeType === 1 && isPageBreakElement(node)) {
      // A manual page break is an unconditional hard boundary. Even on an
      // otherwise blank page, Ctrl/Cmd+Enter must create the following page.
      currentPage.appendChild(node);
      startNewPage();
      return;
    }

    if (appendAndFits(node)) return;

    // Chart/Table/Math/Image are atomic document objects. The Editor never
    // slices them into page fragments. If an object does not fit the remaining
    // printable area, move the entire object to the next Letter page.
    if (node.nodeType === 1 && isAtomicCanvasObject(node)) {
      if (pageHasMeaningfulContent(currentPage)) startNewPage();
      currentPage.appendChild(node);

      // An object larger than a whole printable page is left intact on its own
      // page. Do not duplicate it, split it, or push a second copy into the
      // gutter. The child component remains responsible for its own sizing.
      if (pageIsOverflowing(currentPage)) {
        node.setAttribute("data-doc-atomic-oversize", "true");
        currentPage.setAttribute(CANVAS_OVERFLOW_ATTR, "true");
      } else {
        node.removeAttribute("data-doc-atomic-oversize");
      }
      return;
    }

    if (node.nodeType === 1) {
      const structuralFragments = splitStructuralElementIntoFragments(node);
      if (structuralFragments?.length) {
        structuralFragments.forEach((fragment) => placeNode(fragment, depth + 1));
        return;
      }

      const listFragments = splitListIntoFragments(node);
      if (listFragments?.length) {
        listFragments.forEach((fragment) => placeNode(fragment, depth + 1));
        return;
      }

      if (splitTextAcrossPages(node)) return;
    }

    if (pageHasMeaningfulContent(currentPage)) startNewPage();

    if (!appendAndFits(node)) {
      currentPage.appendChild(node);
      currentPage.setAttribute(CANVAS_OVERFLOW_ATTR, "true");
    }
  };

  nodes.forEach((node) => placeNode(node));

  // A logical table must never disappear as a side effect of pagination. In
  // the normal path the table (or one of its row fragments) is already present.
  // If an edge case dropped every fragment, move the original source table back
  // into the last live page rather than losing user/AI content.
  sourceTablesById.forEach((sourceTable, tableId) => {
    if (
      editor.querySelector(
        `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
      )
    ) {
      return;
    }

    const livePages = Array.from(
      editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
    );
    const fallbackPage = livePages[livePages.length - 1] || currentPage;
    if (!fallbackPage || !sourceTable) return;

    fallbackPage.appendChild(sourceTable);
    if (pageIsOverflowing(fallbackPage)) {
      fallbackPage.setAttribute(CANVAS_OVERFLOW_ATTR, "true");
    }
  });

  const pages = Array.from(
    editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
  );

  while (
    pages.length > 1 &&
    !pageHasMeaningfulContent(pages[pages.length - 1]) &&
    !isIntentionalBlankPageAfterBreak(pages[pages.length - 1], pages)
  ) {
    const emptyPage = pages.pop();
    emptyPage?.remove();
  }

  const finalPages = Array.from(
    editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
  );

  finalPages.forEach((page, index) => {
    page.setAttribute("data-page-number", String(index + 1));
    page.setAttribute("aria-label", `Document page ${index + 1}`);
  });

  return Math.max(1, finalPages.length);
}

function captureIngressTableSnapshots(root) {
  if (!root?.querySelectorAll) return [];

  // Normalize/import at the very first Canvas boundary. This converts AI Markdown
  // tables and gives every native table a stable logical ID before generic editor
  // cleanup gets a chance to remove surrounding legacy chrome/wrappers.
  try {
    normalizeTablesWithDocWriterTable(root, {
      source: "reopened",
      inferWidths: true,
    });
  } catch (_) {}

  const snapshots = [];
  const seen = new Set();

  Array.from(root.querySelectorAll(`table[${DOC_TABLE_ID_ATTR}]`)).forEach((table) => {
    // Never promote a transient Edit-dialog preview table into the document.
    if (
      table.closest?.(
        [
          '[data-doc-writer-table-ui="edit-overlay"]',
          '[data-doc-writer-table-ui="preview-viewport"]',
          '[data-doc-writer-table-ui^="preview-"]',
        ].join(",")
      )
    ) {
      return;
    }

    const tableId = String(table.getAttribute(DOC_TABLE_ID_ATTR) || "").trim();
    if (!tableId || seen.has(tableId)) return;
    seen.add(tableId);

    snapshots.push({
      tableId,
      html: String(table.outerHTML || "").trim(),
    });
  });

  return snapshots;
}

function restoreMissingIngressTables(root, snapshots = []) {
  if (!root?.querySelectorAll || !Array.isArray(snapshots) || !snapshots.length) {
    return 0;
  }

  let restored = 0;

  snapshots.forEach((snapshot) => {
    const tableId = String(snapshot?.tableId || "").trim();
    const html = String(snapshot?.html || "").trim();
    if (!tableId || !html) return;

    if (
      root.querySelector(
        `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
      )
    ) {
      return;
    }

    try {
      const holder = root.ownerDocument.createElement("div");
      holder.innerHTML = html;
      const table = holder.querySelector("table");
      if (!table) return;

      root.appendChild(table);
      restored += 1;
    } catch (_) {}
  });

  if (restored > 0) {
    try {
      normalizeTablesWithDocWriterTable(root, {
        source: "reopened",
        inferWidths: true,
      });
    } catch (_) {}
    ensureCanvasTableVisibility(root);
  }

  return restored;
}


function normalizeDocumentHtml(
  value,
  { preserveCaretMarkers = false, preserveEditingBoundaries = false } = {}
) {
  const raw = String(value || "").trim();

  if (!raw) return "";

  if (
    typeof window === "undefined" ||
    typeof window.DOMParser !== "function"
  ) {
    const bodyMatch = raw.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i);
    const bodyHtml = String(bodyMatch ? bodyMatch[1] : raw);
    if (preserveEditingBoundaries) return bodyHtml.trim();
    return bodyHtml
      .replace(
        /^(?:\s|&nbsp;|<br\s*\/?>|<(?:p|div)[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/(?:p|div)>)+/gi,
        ""
      )
      .replace(
        /(?:\s|&nbsp;|<br\s*\/?>|<(?:p|div)[^>]*>(?:\s|&nbsp;|<br\s*\/?>)*<\/(?:p|div)>)+$/gi,
        ""
      )
      .trim();
  }

  try {
    const parser = new window.DOMParser();
    const documentValue = parser.parseFromString(raw, "text/html");
    const body = documentValue.body;

    removeUnsafeMarkup(body);
    unwrapCanvasPages(body);

    // IMPORTANT TABLE OWNERSHIP BOUNDARY:
    // Capture logical tables BEFORE editor-only cleanup. Previous code first ran
    // generic cleanup and only afterward normalized document objects. If an older
    // AI/table wrapper was classified as editor chrome, the wrapper and its table
    // could disappear before DocWriterTable ever saw it. PDF Preview still worked
    // because the parent retained the original HTML, while Canvas became an empty
    // Letter page. Normalize/capture first, then cleanup, then restore only a table
    // that is genuinely missing.
    const ingressTableSnapshots = captureIngressTableSnapshots(body);

    try {
      reassembleAllTablesWithDocWriterTable(body, {
        removeFragments: true,
        normalize: true,
      });
    } catch (_) {}

    stripDocWriterEditorOnlyMarkup(body);
    stripCanvasOnlyMarkup(body, { preserveCaretMarkers });
    normalizeDocumentObjects(body);

    restoreMissingIngressTables(body, ingressTableSnapshots);
    trimDocumentBoundaries(body, { preserveEditingBoundaries });

    return body.innerHTML.trim();
  } catch (_) {
    return raw;
  }
}

const DocCanvasEditor = forwardRef(
  (
    {
      html = "",
      onChange,
      readOnly = false,
      redline = false,
      placeholder = "Start writing here…",
      showToolbar = true,
      onInsertPageBreak,
      onImageUpload = null,
    },
    ref
  ) => {
    const wrapRef = useRef(null);
    const workspaceRef = useRef(null);
    const editorRef = useRef(null);
    const visualRootsRef = useRef(new Map());
    const visualMountFrameRef = useRef(null);
    const objectDragRef = useRef(null);
    const paginationTimerRef = useRef(null);
    const paginationFrameRef = useRef(null);
    const paginatingRef = useRef(false);
    const toolbarInteractionRef = useRef(false);
    const objectEditingKeysRef = useRef(new Set());
    const toolbarSelectionRef = useRef(null);
    const toolbarLiveRangeRef = useRef(null);
    const objectResizeRef = useRef(null);
    const objectResizeFrameRef = useRef(null);
    const pendingResizePointRef = useRef(null);
    const selectedTableIdRef = useRef("");
    const selectedCellIdRef = useRef("");
    const selectedVisualIdRef = useRef("");
    const selectedPageBreakRef = useRef(null);
    const composingRef = useRef(false);
    const pendingParagraphInputRef = useRef(false);
    const visualRepaginationFrameRef = useRef(null);
    const visualRepaginationTimerRef = useRef(null);
    const tableDragRef = useRef(null);
    const dragAutoScrollFrameRef = useRef(null);
    const dragAutoScrollPointerRef = useRef(null);
    const editorUndoStackRef = useRef([]);
    const editorRedoStackRef = useRef([]);
    const historyRestoringRef = useRef(false);
    const latestEditorHistoryKindRef = useRef("native");
    const pendingTableTextEditRef = useRef(null);
    const pendingNativeEditRef = useRef(null);
    const nativeTypingGroupRef = useRef(null);
    const editorFocusedRef = useRef(false);
    const lastLocalMutationAtRef = useRef(0);
    const deferredExternalHtmlRef = useRef("");
    // While a toolbar-created table is settling, keep its logical ID as a
    // pagination invariant. The live Canvas remains authoritative until the
    // insertion is stable.
    const pendingManualTableIdRef = useRef("");
    const pendingToolbarObjectLayoutRef = useRef(false);
    const objectViewportAnchorRef = useRef(new Map());
    const reflowingLiveCanvasRef = useRef(false);
    const outerScrollGuardRef = useRef(null);

    const normalizedHtml = useMemo(
      () =>
        normalizeDocumentHtml(html, {
          preserveEditingBoundaries: true,
        }),
      [html]
    );

    // Track the last HTML emitted by this editor. Parent state echoes should
    // never rebuild the contentEditable DOM or destroy the live caret.
    const lastEmittedHtmlRef = useRef(normalizedHtml);

    const markLocalMutation = () => {
      lastLocalMutationAtRef.current = Date.now();
    };

    // Toolbar floating state remains here because DocCanvasToolbar measures the
    // complete editor workspace through wrapRef.
    const [floatToolbar, setFloatToolbar] = useState(false);
    const [autoFloat, setAutoFloat] = useState(false);
    const [isFloatingNow, setIsFloatingNow] = useState(false);

    // Find/Replace state is rendered and controlled by DocCanvasToolbar.
    const [findOpen, setFindOpen] = useState(false);
    const [findQuery, setFindQuery] = useState("");
    const [findReplace, setFindReplace] = useState("");
    const [findCaseSensitive, setFindCaseSensitive] = useState(false);
    const [lastFindEnd, setLastFindEnd] = useState(-1);
    const [findFixedPos, setFindFixedPos] = useState({
      top: 0,
      right: 0,
    });

    const [counts, setCounts] = useState({
      words: 0,
      chars: 0,
    });
    const [pageCount, setPageCount] = useState(1);

    const [toolbarMetrics, setToolbarMetrics] = useState({
      height: 0,
      width: 0,
      left: 0,
    });

    const effectiveReadOnly = !!readOnly;


    const getSelectionElement = () => {
      const selection = window.getSelection?.();
      if (!selection || selection.rangeCount === 0) return null;
      return elementFromNode(selection.anchorNode);
    };

    const activeCell = () => {
      const editor = editorRef.current;
      if (!editor) return null;

      const fromSelection = closestWithin(
        editor,
        getSelectionElement(),
        "th, td"
      );
      if (fromSelection) return fromSelection;

      const selectedId = selectedCellIdRef.current;
      if (!selectedId) return null;
      return editor.querySelector(
        `[${DOC_CELL_ID_ATTR}="${escapeCssValue(selectedId)}"]`
      );
    };

    const activeTable = () => {
      const editor = editorRef.current;
      if (!editor) return null;

      const cell = activeCell();
      if (cell) return closestWithin(editor, cell, "table");

      const selectedId = selectedTableIdRef.current;
      if (!selectedId) return null;
      return editor.querySelector(
        `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(selectedId)}"]`
      );
    };

    const activeProtectedBlock = () => {
      const editor = editorRef.current;
      if (!editor) return null;

      const fromSelection = closestWithin(
        editor,
        getSelectionElement(),
        `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
      );
      if (fromSelection) return fromSelection;

      const selectedId = selectedVisualIdRef.current;
      if (!selectedId) return null;
      return editor.querySelector(
        `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(selectedId)}"]`
      );
    };

    const selectCanvasTable = (table, cell = null) => {
      const editor = editorRef.current;
      if (!editor || !table) return;

      clearCanvasObjectSelection(editor);
      clearCanvasPageBreakSelection(editor);
      selectedPageBreakRef.current = null;
      const tableId = table.getAttribute(DOC_TABLE_ID_ATTR) || "";
      const cellId = cell?.getAttribute(DOC_CELL_ID_ATTR) || "";
      const fragments = getLogicalTableFragments(editor, tableId);

      fragments.forEach((fragment) => {
        fragment.setAttribute(CANVAS_SELECTED_TABLE_ATTR, "true");
      });
      if (cell) cell.setAttribute(CANVAS_SELECTED_CELL_ATTR, "true");

      // Selection state stays in the Editor; DocWriterTable renders the
      // non-printing grab/Edit/resize controls beside the active logical table.
      selectedTableIdRef.current = tableId;
      selectedCellIdRef.current = cellId;
      selectedVisualIdRef.current = "";
      window.requestAnimationFrame(() => docWriterTable?.refreshControls?.());
    };

    const selectProtectedBlock = (block) => {
      const editor = editorRef.current;
      if (!editor || !block) return;

      clearCanvasObjectSelection(editor);
      clearCanvasPageBreakSelection(editor);
      selectedPageBreakRef.current = null;
      selectedVisualIdRef.current =
        block.getAttribute(DOC_BLOCK_ID_ATTR) || "";
      selectedTableIdRef.current = "";
      selectedCellIdRef.current = "";
    };

    const clearSelectedObjects = () => {
      clearCanvasObjectSelection(editorRef.current);
      clearCanvasPageBreakSelection(editorRef.current);
      selectedTableIdRef.current = "";
      selectedCellIdRef.current = "";
      selectedVisualIdRef.current = "";
      selectedPageBreakRef.current = null;
    };

    const selectManualPageBreak = (breakNode) => {
      const editor = editorRef.current;
      if (!editor || !breakNode || !editor.contains(breakNode)) return false;

      clearCanvasObjectSelection(editor);
      clearCanvasPageBreakSelection(editor);
      breakNode.setAttribute(CANVAS_SELECTED_PAGE_BREAK_ATTR, "true");
      selectedPageBreakRef.current = breakNode;
      selectedTableIdRef.current = "";
      selectedCellIdRef.current = "";
      selectedVisualIdRef.current = "";
      docWriterTable.closeEditPanel?.();
      return true;
    };

    const focusCellById = (cellId, atEnd = false) => {
      const editor = editorRef.current;
      if (!editor || !cellId) return false;
      const cell = editor.querySelector(
        `[${DOC_CELL_ID_ATTR}="${escapeCssValue(cellId)}"]`
      );
      if (!cell) return false;
      const table = cell.closest("table");
      if (table) selectCanvasTable(table, cell);
      return placeCaretInElement(cell, atEnd);
    };

    const documentPageContentWidth = (element) => {
      const page = element?.closest?.('[data-doc-canvas-page="true"]');
      if (!page) return 672;
      const style = window.getComputedStyle(page);
      return Math.max(
        MIN_TABLE_WIDTH_PX,
        page.clientWidth -
          (Number.parseFloat(style.paddingLeft || "0") || 0) -
          (Number.parseFloat(style.paddingRight || "0") || 0)
      );
    };

    const documentPageContentHeight = (element) => {
      const page = element?.closest?.('[data-doc-canvas-page="true"]');
      if (!page) return 760;
      const style = window.getComputedStyle(page);
      return Math.max(
        MIN_VISUAL_HEIGHT_PX,
        page.clientHeight -
          (Number.parseFloat(style.paddingTop || "0") || 0) -
          (Number.parseFloat(style.paddingBottom || "0") || 0)
      );
    };

    const viewportIdentityForNode = (node) => {
      if (!node) return null;
      const table = node.tagName === "TABLE" ? node : node.closest?.("table");
      const tableId = table?.getAttribute?.(DOC_TABLE_ID_ATTR) || "";
      if (tableId) return { kind: "table", id: tableId };
      const block = node.getAttribute?.(DOC_PROTECTED_BLOCK_ATTR) === "true"
        ? node
        : node.closest?.(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`);
      const blockId = block?.getAttribute?.(DOC_BLOCK_ID_ATTR) || "";
      if (blockId) return { kind: "visual", id: blockId };
      const page = node.getAttribute?.("data-doc-canvas-page") === "true"
        ? node
        : node.closest?.('[data-doc-canvas-page="true"]');
      const pageNumber = page?.getAttribute?.("data-page-number") || "";
      return pageNumber ? { kind: "page", id: pageNumber } : null;
    };

    const resolveViewportIdentityNode = (identity) => {
      const editor = editorRef.current;
      if (!editor || !identity?.id) return null;
      if (identity.kind === "table") {
        return editor.querySelector(
          `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(identity.id)}"]`
        );
      }
      if (identity.kind === "visual") {
        return editor.querySelector(
          `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(identity.id)}"]`
        );
      }
      if (identity.kind === "page") {
        return editor.querySelector(
          `[data-doc-canvas-page="true"][data-page-number="${escapeCssValue(identity.id)}"]`
        );
      }
      return null;
    };

    const captureCanvasViewport = (preferredNode = null) => {
      const workspace = workspaceRef.current;
      if (!workspace) return null;
      const workspaceRect = workspace.getBoundingClientRect();
      let anchorNode = preferredNode;
      if (!anchorNode || !anchorNode.isConnected) {
        const pages = Array.from(
          editorRef.current?.querySelectorAll?.(':scope > [data-doc-canvas-page="true"]') || []
        );
        anchorNode = pages.find((page) => {
          const rect = page.getBoundingClientRect();
          return rect.bottom > workspaceRect.top + 8 && rect.top < workspaceRect.bottom - 8;
        }) || pages[0] || null;
      }
      const identity = viewportIdentityForNode(anchorNode);
      const anchorRect = anchorNode?.getBoundingClientRect?.();
      return {
        identity,
        anchorOffsetY: anchorRect ? anchorRect.top - workspaceRect.top : null,
        workspaceTop: workspace.scrollTop,
        workspaceLeft: workspace.scrollLeft,
        outerScroll: captureOuterScrollState(wrapRef.current || workspace, workspace),
      };
    };

    const restoreCanvasViewport = (snapshot, { useAnchor = true } = {}) => {
      const workspace = workspaceRef.current;
      if (!workspace || !snapshot) return;
      const apply = () => {
        if (!workspaceRef.current) return;
        restoreOuterScrollState(snapshot.outerScroll);
        workspace.scrollLeft = Number(snapshot.workspaceLeft || 0);
        let restoredByAnchor = false;
        if (useAnchor && snapshot.identity && Number.isFinite(snapshot.anchorOffsetY)) {
          const anchorNode = resolveViewportIdentityNode(snapshot.identity);
          if (anchorNode?.isConnected) {
            const workspaceRect = workspace.getBoundingClientRect();
            const currentOffset = anchorNode.getBoundingClientRect().top - workspaceRect.top;
            workspace.scrollTop += currentOffset - snapshot.anchorOffsetY;
            restoredByAnchor = true;
          }
        }
        if (!restoredByAnchor) workspace.scrollTop = Number(snapshot.workspaceTop || 0);
      };
      apply();
      window.requestAnimationFrame(() => {
        apply();
        window.requestAnimationFrame(apply);
      });
    };

    const focusEditorWithoutOuterScroll = () => {
      const editor = editorRef.current;
      if (!editor) return false;
      const workspace = workspaceRef.current;
      const outerScroll = captureOuterScrollState(wrapRef.current || editor, workspace);
      const workspaceTop = workspace?.scrollTop || 0;
      const workspaceLeft = workspace?.scrollLeft || 0;
      try {
        editor.focus({ preventScroll: true });
      } catch (_) {
        try { editor.focus(); } catch (_) { return false; }
      }
      restoreOuterScrollState(outerScroll);
      if (workspace) {
        workspace.scrollTop = workspaceTop;
        workspace.scrollLeft = workspaceLeft;
      }
      window.requestAnimationFrame(() => {
        restoreOuterScrollState(outerScroll);
        if (workspace) {
          workspace.scrollTop = workspaceTop;
          workspace.scrollLeft = workspaceLeft;
        }
      });
      return true;
    };

    const applyColumnWidthsToFragments = (tableId, widths) => {
      const editor = editorRef.current;
      if (!editor || !tableId || !Array.isArray(widths)) return;

      getLogicalTableFragments(editor, tableId).forEach((fragment) => {
        const colgroup = ensureTableColgroup(fragment, widths.length);
        const cols = Array.from(colgroup?.children || []).filter(
          (child) => child.tagName === "COL"
        );

        cols.forEach((col, index) => {
          col.style.width = `${Math.max(
            MIN_TABLE_COLUMN_PX,
            Number(widths[index] || MIN_TABLE_COLUMN_PX)
          )}px`;
        });

        fragment.style.tableLayout = "fixed";
        fragment.setAttribute("data-doc-table-resized", "true");
      });
    };

    const finishObjectResize = () => {
      const resize = objectResizeRef.current;
      objectResizeRef.current = null;
      pendingResizePointRef.current = null;

      if (objectResizeFrameRef.current) {
        window.cancelAnimationFrame(objectResizeFrameRef.current);
        objectResizeFrameRef.current = null;
      }

      window.removeEventListener("pointermove", handleObjectResizeMove, true);
      window.removeEventListener("pointerup", finishObjectResize, true);
      window.removeEventListener("pointercancel", finishObjectResize, true);

      const editor = editorRef.current;
      if (editor) editor.style.cursor = "";

      if (resize) {
        const isTableResize = ["column", "table", "row"].includes(resize.kind);
        const currentHtml = serializeEditorHtml(editor, {
          preserveEditingBoundaries: true,
        });
        lastEmittedHtmlRef.current = currentHtml;
        markLocalMutation();
        onChange?.(currentHtml);

        if (isTableResize && resize.beforeHtml) {
          recordEditorTransaction(
            {
              transactionType: "table",
              transactionId: resize.transactionId,
              operation: `resize-${resize.kind}`,
              tableId: resize.tableId || "",
              focusCellId: resize.cellId || "",
              beforeHtml: resize.beforeHtml,
              afterHtml: currentHtml,
            },
            currentHtml
          );
        }

        scheduleRepagination({
          immediate: true,
          preserveSelection: true,
          force: false,
        });
      }
    };

    const flushObjectResize = () => {
      objectResizeFrameRef.current = null;
      const point = pendingResizePointRef.current;
      const resize = objectResizeRef.current;
      pendingResizePointRef.current = null;
      if (!point || !resize) return;

      const deltaX = point.clientX - resize.startX;
      const deltaY = point.clientY - resize.startY;

      if (resize.kind === "column") {
        const next = [...resize.widths];
        const current = Math.max(
          MIN_TABLE_COLUMN_PX,
          resize.widths[resize.columnIndex] + deltaX
        );
        const adjacent = Math.max(
          MIN_TABLE_COLUMN_PX,
          resize.widths[resize.columnIndex + 1] - deltaX
        );
        next[resize.columnIndex] = current;
        next[resize.columnIndex + 1] = adjacent;
        applyColumnWidthsToFragments(resize.tableId, next);
      } else if (resize.kind === "table") {
        const width = Math.max(
          MIN_TABLE_WIDTH_PX,
          Math.min(resize.maxWidth, resize.startWidth + deltaX)
        );
        getLogicalTableFragments(editorRef.current, resize.tableId).forEach(
          (fragment) => {
            fragment.style.width = `${width}px`;
            fragment.style.maxWidth = "100%";
            fragment.setAttribute("data-doc-table-resized", "true");
          }
        );
      } else if (resize.kind === "row") {
        const height = Math.max(20, resize.startHeight + deltaY);
        editorRef.current
          ?.querySelectorAll(
            `tr[${DOC_ROW_ID_ATTR}="${escapeCssValue(resize.rowId)}"]`
          )
          .forEach((row) => {
            row.style.height = `${height}px`;
          });
      } else if (resize.kind === "visual") {
        const width = Math.max(
          MIN_VISUAL_WIDTH_PX,
          Math.min(resize.maxWidth, resize.startWidth + deltaX)
        );
        const height = Math.max(
          MIN_VISUAL_HEIGHT_PX,
          Math.min(resize.maxHeight, resize.startHeight + deltaY)
        );
        resize.block.style.width = `${width}px`;
        resize.block.style.height = `${height}px`;
        resize.block.style.maxWidth = "100%";
        resize.block.style.overflow = "visible";
        resize.block.setAttribute("data-doc-block-resized", "true");
      }
    };

    const handleObjectResizeMove = (event) => {
      if (!objectResizeRef.current) return;
      event.preventDefault();
      pendingResizePointRef.current = {
        clientX: event.clientX,
        clientY: event.clientY,
      };
      if (!objectResizeFrameRef.current) {
        objectResizeFrameRef.current =
          window.requestAnimationFrame(flushObjectResize);
      }
    };

    const beginObjectResize = (event, resize) => {
      if (effectiveReadOnly || !resize) return false;

      event.preventDefault();
      event.stopPropagation();
      captureToolbarSelection();
      const isTableResize = ["column", "table", "row"].includes(resize.kind);
      const beforeHtml = isTableResize
        ? serializeEditorHtml(editorRef.current, {
            preserveEditingBoundaries: true,
          })
        : "";
      objectResizeRef.current = {
        ...resize,
        startX: event.clientX,
        startY: event.clientY,
        beforeHtml,
        transactionId: isTableResize
          ? createDocumentObjectId("editortx")
          : "",
      };

      window.addEventListener("pointermove", handleObjectResizeMove, true);
      window.addEventListener("pointerup", finishObjectResize, true);
      window.addEventListener("pointercancel", finishObjectResize, true);
      return true;
    };

    const pointerResizeTarget = (event) => {
      const editor = editorRef.current;
      if (!editor) return null;

      const visual = closestWithin(
        editor,
        event.target,
        `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
      );
      if (visual) {
        // Child visual components own Drag/Edit/Resize chrome. The Editor only
        // tracks logical selection and responds to explicit child callbacks.
        return { kind: "visual-select", block: visual, cursor: "" };
      }

      const cell = closestWithin(editor, event.target, "th, td");
      if (!cell) return null;
      const table = cell.closest("table");
      if (!table) return null;

      // DocWriterTable owns column/row/whole-table resize hit testing. Never
      // infer an Editor resize zone from the edge of a cell.
      return { kind: "table-select", table, cell, cursor: "text" };
    };

    const handleCanvasPointerMove = (event) => {
      const editor = editorRef.current;
      if (!editor || effectiveReadOnly || objectResizeRef.current) return;
      const target = pointerResizeTarget(event);
      editor.style.cursor = target?.cursor || "";
    };

    const handleCanvasPointerLeave = () => {
      if (!objectResizeRef.current && editorRef.current) {
        editorRef.current.style.cursor = "";
      }
    };

    const removeSelectedVisual = () => {
      const block = activeProtectedBlock();
      if (!block) return false;
      const blockId = block.getAttribute(DOC_BLOCK_ID_ATTR) || "";
      const family = normalizeVisualFamily(
        block.getAttribute(DOC_BLOCK_TYPE_ATTR) || "visual"
      );
      return deleteDocWriterVisual(blockId, family);
    };

    const canvasPageFromNode = (node) => {
      const editor = editorRef.current;
      if (!editor || !node) return null;
      let element = node.nodeType === 1 ? node : node.parentElement;
      const page = element?.closest?.('[data-doc-canvas-page="true"]');
      return page && editor.contains(page) ? page : null;
    };

    const directDocumentChildren = (page) =>
      Array.from(page?.children || []).filter(
        (child) => !isCanvasEditorChromeElement(child)
      );

    const directChildWithinPage = (page, node) => {
      if (!page || !node) return null;
      let element = elementFromNode(node);
      if (!element || element === page) return null;
      while (element && element.parentElement !== page) {
        element = element.parentElement;
      }
      return element?.parentElement === page ? element : null;
    };

    const ensureBoundaryParagraph = (page, beforeNode = null) => {
      if (!page?.ownerDocument) return null;
      normalizeDirectCanvasProseChildren(page);

      const children = directDocumentChildren(page);
      const reference =
        beforeNode && beforeNode.parentNode === page && !isCanvasEditorChromeElement(beforeNode)
          ? beforeNode
          : null;
      const referenceIndex = reference ? children.indexOf(reference) : children.length;
      const previous =
        referenceIndex > 0 ? children[referenceIndex - 1] : null;

      if (
        reference?.tagName === "P" &&
        isCanvasEditableFlowElement(reference) &&
        isBoundaryEmpty(reference)
      ) {
        return reference;
      }
      if (
        previous?.tagName === "P" &&
        isCanvasEditableFlowElement(previous) &&
        isBoundaryEmpty(previous)
      ) {
        return previous;
      }

      const paragraph = createCanvasProseParagraph(page.ownerDocument);
      page.insertBefore(paragraph, reference || null);
      return paragraph;
    };

    const placeCaretInBoundaryParagraph = (page, beforeNode = null) => {
      const paragraph = ensureBoundaryParagraph(page, beforeNode);
      if (!paragraph) return false;
      clearSelectedObjects();
      const placed = placeCaretInElement(paragraph, true);
      if (placed) {
        toolbarSelectionRef.current =
          captureEditorSelectionState(editorRef.current) ||
          captureSelectionOffsets(editorRef.current);
      }
      return placed;
    };

    const placeCaretInsideCanvasPage = (page, atEnd = true) => {
      const editor = editorRef.current;
      if (!editor || !page || !editor.contains(page)) return false;

      try {
        normalizeDirectCanvasProseChildren(page);
        const children = directDocumentChildren(page);

        if (!children.length) {
          return placeCaretInBoundaryParagraph(page, null);
        }

        const edge = atEnd ? children[children.length - 1] : children[0];
        if (isCanvasEditableFlowElement(edge)) {
          const placed = placeCaretInElement(edge, atEnd);
          if (placed) {
            toolbarSelectionRef.current =
              captureEditorSelectionState(editor) ||
              captureSelectionOffsets(editor);
          }
          return placed;
        }

        return placeCaretInBoundaryParagraph(
          page,
          atEnd ? null : edge
        );
      } catch (_) {
        return false;
      }
    };

    const placeCaretAtCanvasPoint = (page, clientX, clientY) => {
      const editor = editorRef.current;
      if (!editor || !page || !editor.contains(page)) return false;

      normalizeDirectCanvasProseChildren(page);
      const children = directDocumentChildren(page);
      if (!children.length) return placeCaretInBoundaryParagraph(page, null);

      // Clamp the hit-test to the printable content rectangle. A click in the
      // visible white page margin still creates a normal paragraph inside the
      // existing 0.75in margin; prose can never be positioned outside it.
      const contentRect = pageContentViewportRect(page);
      const y = contentRect
        ? Math.max(contentRect.top, Math.min(Number(clientY || 0), contentRect.bottom))
        : Number(clientY || 0);

      for (let index = 0; index < children.length; index += 1) {
        const child = children[index];
        const rect = child.getBoundingClientRect?.();
        if (!rect || (!rect.height && !rect.width)) continue;

        if (y < rect.top) {
          return placeCaretInBoundaryParagraph(page, child);
        }

        if (y <= rect.bottom) {
          if (isCanvasEditableFlowElement(child)) {
            const placed = placeCaretInElement(
              child,
              y >= rect.top + rect.height / 2
            );
            if (placed) {
              clearSelectedObjects();
              toolbarSelectionRef.current =
                captureEditorSelectionState(editor) ||
                captureSelectionOffsets(editor);
            }
            return placed;
          }

          const beforeNode =
            y < rect.top + rect.height / 2
              ? child
              : children[index + 1] || null;
          return placeCaretInBoundaryParagraph(page, beforeNode);
        }
      }

      return placeCaretInBoundaryParagraph(page, null);
    };

    const ensureCaretInsideCanvasPage = () => {
      const editor = editorRef.current;
      if (!editor) return false;

      const selection = window.getSelection();
      const anchorPage =
        selection?.rangeCount > 0 ? canvasPageFromNode(selection.anchorNode) : null;
      const focusPage =
        selection?.rangeCount > 0 ? canvasPageFromNode(selection.focusNode) : null;

      if (selection && selection.rangeCount > 0 && anchorPage && focusPage) {
        // A Range directly on the page wrapper is not a safe typing host: the
        // browser may create a bare text node that ignores paragraph wrapping.
        // Repair only that invalid boundary shape. Table cells and component-owned
        // protected objects keep their existing selection/keyboard semantics.
        if (
          selection.anchorNode === anchorPage ||
          selection.focusNode === focusPage ||
          (selection.anchorNode?.nodeType === 3 &&
            selection.anchorNode.parentNode === anchorPage) ||
          (selection.focusNode?.nodeType === 3 &&
            selection.focusNode.parentNode === focusPage)
        ) {
          const range = selection.getRangeAt(0);
          const page = anchorPage;
          normalizeDirectCanvasProseChildren(page);

          if (
            page.contains(range.startContainer) &&
            range.startContainer !== page
          ) {
            toolbarSelectionRef.current =
              captureEditorSelectionState(editor) ||
              captureSelectionOffsets(editor);
            return true;
          }

          const offset =
            range.startContainer === page
              ? Math.max(0, Math.min(page.childNodes.length, range.startOffset))
              : page.childNodes.length;
          const beforeNode = page.childNodes[offset] || null;
          return placeCaretInBoundaryParagraph(page, beforeNode);
        }

        return true;
      }

      const pages = Array.from(
        editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
      );
      const page = pages[pages.length - 1] || pages[0];
      return placeCaretInsideCanvasPage(page, true);
    };

    const nearestCanvasPage = (clientY) => {
      const editor = editorRef.current;
      if (!editor) return null;
      const pages = Array.from(
        editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
      );
      if (!pages.length) return null;

      return pages.reduce((best, page) => {
        const rect = page.getBoundingClientRect();
        const distance =
          clientY < rect.top
            ? rect.top - clientY
            : clientY > rect.bottom
            ? clientY - rect.bottom
            : 0;
        return !best || distance < best.distance
          ? { page, rect, distance }
          : best;
      }, null);
    };

    const clearTableDropIndicator = () => {
      const editor = editorRef.current;
      if (!editor?.querySelectorAll) return;
      editor
        .querySelectorAll(`[${CANVAS_DRAG_OVER_ATTR}]`)
        .forEach((element) => element.removeAttribute(CANVAS_DRAG_OVER_ATTR));
    };

    const resolveVerticalDragScrollHost = () => {
      // The Canvas workspace is the ONLY drag-scroll host. Never fall back to the
      // browser window/Status page; doing so is exactly the jump-out-of-editor
      // behavior this component must prevent.
      return workspaceRef.current;
    };

    const dragAutoScrollDelta = (pointerY, rect) => {
      const top = Number(rect?.top || 0);
      const bottom = Number(rect?.bottom || 0);
      if (!Number.isFinite(pointerY) || !Number.isFinite(top) || !Number.isFinite(bottom)) {
        return 0;
      }
      const edge = Math.min(
        DRAG_AUTOSCROLL_EDGE_PX,
        Math.max(36, (bottom - top) * 0.22)
      );
      if (pointerY < top + edge) {
        const strength = Math.max(0, Math.min(1, (top + edge - pointerY) / edge));
        return -Math.max(1, Math.round(DRAG_AUTOSCROLL_MAX_STEP_PX * strength * strength));
      }
      if (pointerY > bottom - edge) {
        const strength = Math.max(0, Math.min(1, (pointerY - (bottom - edge)) / edge));
        return Math.max(1, Math.round(DRAG_AUTOSCROLL_MAX_STEP_PX * strength * strength));
      }
      return 0;
    };

    const stopObjectDragAutoScroll = () => {
      dragAutoScrollPointerRef.current = null;
      if (dragAutoScrollFrameRef.current) {
        window.cancelAnimationFrame(dragAutoScrollFrameRef.current);
        dragAutoScrollFrameRef.current = null;
      }
      const workspace = workspaceRef.current;
      if (workspace?.dataset?.docDragScrollBehavior) {
        workspace.style.scrollBehavior = workspace.dataset.docDragScrollBehavior;
        delete workspace.dataset.docDragScrollBehavior;
      }
    };

    const sourceContainsNode = (drag, node) => {
      const editor = editorRef.current;
      if (!drag || !editor || !node) return false;
      if (drag.kind === "table") {
        return getLogicalTableFragments(editor, drag.tableId).some(
          (fragment) => fragment === node || fragment.contains?.(node)
        );
      }
      if (drag.kind === "visual") {
        const block = editor.querySelector(
          `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(drag.blockId)}"]`
        );
        return Boolean(block && (block === node || block.contains?.(node)));
      }
      return false;
    };

    const resolveGenericDropPlacement = (clientX, clientY, drag = objectDragRef.current) => {
      const editor = editorRef.current;
      if (!editor || !drag) return null;
      let hit = editor.ownerDocument.elementFromPoint?.(clientX, clientY) || null;
      if (hit && sourceContainsNode(drag, hit)) return null;

      let page = canvasPageFromNode(hit);
      if (!page) page = nearestCanvasPage(clientY)?.page || null;
      if (!page) return null;

      let target = hit;
      while (target && target.parentElement !== page && target !== page) {
        target = target.parentElement;
      }
      if (!target || target === editor || sourceContainsNode(drag, target)) {
        target = page;
      }

      const rect = target.getBoundingClientRect?.() || page.getBoundingClientRect();
      const belowMidpoint = clientY >= rect.top + rect.height / 2;
      return {
        targetNode: target,
        page,
        position:
          target === page
            ? belowMidpoint
              ? "inside-end"
              : "inside-start"
            : belowMidpoint
            ? "after"
            : "before",
      };
    };

    const resolveObjectDropPlacement = (clientX, clientY) => {
      const drag = objectDragRef.current;
      if (!drag) return null;
      if (drag.kind === "table") {
        let resolved = docWriterTable.resolveDropTargetAtPoint?.(
          clientX,
          clientY,
          { tableId: drag.tableId }
        );
        if (resolved?.targetNode && !sourceContainsNode(drag, resolved.targetNode)) {
          const rect = resolved.targetNode.getBoundingClientRect?.();
          const isPage =
            resolved.targetNode.getAttribute?.("data-doc-canvas-page") === "true";
          const belowMidpoint = rect
            ? clientY >= rect.top + rect.height / 2
            : true;
          return {
            ...resolved,
            position: isPage
              ? belowMidpoint
                ? "inside-end"
                : "inside-start"
              : belowMidpoint
              ? "after"
              : "before",
          };
        }
      }
      return resolveGenericDropPlacement(clientX, clientY, drag);
    };

    const updateObjectDropIndicator = (clientX, clientY) => {
      const drag = objectDragRef.current;
      if (!drag) return null;
      clearTableDropIndicator();
      const placement = resolveObjectDropPlacement(clientX, clientY);
      if (placement?.targetNode) {
        placement.targetNode.setAttribute(
          CANVAS_DRAG_OVER_ATTR,
          placement.position || "before"
        );
      }
      drag.dropPlacement = placement;
      drag.pointerX = clientX;
      drag.pointerY = clientY;
      return placement;
    };

    const runObjectDragAutoScrollFrame = () => {
      dragAutoScrollFrameRef.current = null;
      const point = dragAutoScrollPointerRef.current;
      const drag = objectDragRef.current;
      if (!point || !drag) return;

      const host = resolveVerticalDragScrollHost();
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const delta = dragAutoScrollDelta(point.clientY, rect);

      if (delta) {
        host.scrollTop += delta;
        updateObjectDropIndicator(point.clientX, point.clientY);
      }

      // Keep the rAF loop alive for the entire held drag, even when the pointer
      // briefly leaves the edge zone. This removes the old freeze where another
      // dragover event was required before scrolling could resume on the next page.
      if (objectDragRef.current) {
        dragAutoScrollFrameRef.current =
          window.requestAnimationFrame(runObjectDragAutoScrollFrame);
      }
    };

    const scheduleObjectDragAutoScroll = (clientX, clientY) => {
      dragAutoScrollPointerRef.current = { clientX, clientY };
      const workspace = workspaceRef.current;
      if (workspace && !workspace.dataset.docDragScrollBehavior) {
        workspace.dataset.docDragScrollBehavior = workspace.style.scrollBehavior || "smooth";
        workspace.style.scrollBehavior = "auto";
      }
      if (!dragAutoScrollFrameRef.current) {
        dragAutoScrollFrameRef.current =
          window.requestAnimationFrame(runObjectDragAutoScrollFrame);
      }
    };

    const dragSourceNodes = (drag) => {
      const editor = editorRef.current;
      if (!editor || !drag) return [];
      if (drag.kind === "table") return getLogicalTableFragments(editor, drag.tableId);
      const block = editor.querySelector(
        `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(drag.blockId)}"]`
      );
      return block ? [block] : [];
    };

    const createObjectDragGhost = (drag, event) => {
      if (!drag || !event) return null;
      const nodes = dragSourceNodes(drag);
      if (!nodes.length) return null;
      const firstRect = nodes[0].getBoundingClientRect();
      const documentValue = editorRef.current?.ownerDocument || document;
      const ghost = documentValue.createElement("div");
      ghost.setAttribute("data-doc-editor-drag-ghost", "true");
      Object.assign(ghost.style, {
        position: "fixed",
        left: `${firstRect.left}px`,
        top: `${firstRect.top}px`,
        width: `${Math.max(80, firstRect.width)}px`,
        maxWidth: "min(80vw, 760px)",
        maxHeight: "72vh",
        overflow: "hidden",
        pointerEvents: "none",
        zIndex: "2147482600",
        opacity: "0.94",
        background: "rgba(255,255,255,0.98)",
        boxShadow: "0 20px 55px rgba(15,23,42,0.28)",
        border: "1px solid rgba(30,42,120,0.35)",
        borderRadius: "8px",
        transform: "translate3d(0,0,0)",
        willChange: "left, top",
      });
      nodes.forEach((node) => {
        const clone = node.cloneNode(true);
        stripDocWriterEditorOnlyMarkup(clone);
        stripCanvasOnlyMarkup(clone);
        clone.removeAttribute(CANVAS_SELECTED_TABLE_ATTR);
        clone.removeAttribute(CANVAS_SELECTED_VISUAL_ATTR);
        clone.style.margin = "0";
        clone.style.maxWidth = "100%";
        ghost.appendChild(clone);
      });
      documentValue.body.appendChild(ghost);
      drag.ghost = ghost;
      drag.grabOffsetX = Math.max(0, event.clientX - firstRect.left);
      drag.grabOffsetY = Math.max(0, event.clientY - firstRect.top);
      drag.sourceStyles = nodes.map((node) => ({
        node,
        opacity: node.style.opacity,
        pointerEvents: node.style.pointerEvents,
      }));
      drag.sourceStyles.forEach(({ node }) => {
        node.style.opacity = "0.18";
        node.style.pointerEvents = "none";
      });
      return ghost;
    };

    const updateObjectDragGhost = (drag, clientX, clientY) => {
      if (!drag?.ghost) return;
      drag.ghost.style.left = `${Math.round(clientX - Number(drag.grabOffsetX || 0))}px`;
      drag.ghost.style.top = `${Math.round(clientY - Number(drag.grabOffsetY || 0))}px`;
    };

    const destroyObjectDragGhost = (drag) => {
      if (!drag) return;
      try { drag.ghost?.remove?.(); } catch (_) {}
      (drag.sourceStyles || []).forEach(({ node, opacity, pointerEvents }) => {
        if (!node?.isConnected) return;
        node.style.opacity = opacity || "";
        node.style.pointerEvents = pointerEvents || "";
      });
      drag.ghost = null;
      drag.sourceStyles = [];
    };

    const clearObjectDraggingState = (drag = null) => {
      const editor = editorRef.current;
      destroyObjectDragGhost(drag);
      clearTableDropIndicator();
      editor
        ?.querySelectorAll(
          `[${CANVAS_TABLE_DRAGGING_ATTR}="true"], [${CANVAS_VISUAL_DRAGGING_ATTR}="true"], [data-doc-dragging="true"]`
        )
        .forEach((element) => {
          element.removeAttribute(CANVAS_TABLE_DRAGGING_ATTR);
          element.removeAttribute(CANVAS_VISUAL_DRAGGING_ATTR);
          element.removeAttribute("data-doc-dragging");
        });
      if (editor) editor.style.cursor = "";
    };

    const placeVisualAtDropTarget = (block, placement) => {
      if (!block || !placement?.targetNode) return false;
      const target = placement.targetNode;
      const position = placement.position || "before";
      if (target === block || block.contains?.(target)) return false;
      if (target.getAttribute?.("data-doc-canvas-page") === "true") {
        if (["inside-start", "start", "before"].includes(position)) {
          target.insertBefore(block, target.firstChild || null);
        } else {
          target.appendChild(block);
        }
        return true;
      }
      if (!target.parentNode) return false;
      if (["after", "below"].includes(position)) {
        target.parentNode.insertBefore(block, target.nextSibling || null);
      } else {
        target.parentNode.insertBefore(block, target);
      }
      return true;
    };

    const finishUnifiedObjectDrag = ({ cancel = false } = {}) => {
      const drag = objectDragRef.current;
      objectDragRef.current = null;
      tableDragRef.current = null;
      stopObjectDragAutoScroll();
      clearObjectDraggingState(drag);
      window.removeEventListener("pointermove", handleUnifiedObjectDragMove, true);
      window.removeEventListener("pointerup", handleUnifiedObjectDragEnd, true);
      window.removeEventListener("pointercancel", handleUnifiedObjectDragCancel, true);
      if (!drag || cancel) return false;

      const placement = drag.dropPlacement;
      if (!placement?.targetNode) return false;

      if (drag.kind === "table") {
        const result = docWriterTable.moveTable?.({
          tableId: drag.tableId,
          targetNode: placement.targetNode,
          position: placement.position || "before",
          source: "doc-writer-table-drag",
        });
        window.requestAnimationFrame(() => docWriterTable.refreshControls?.());
        return Boolean(result?.changed);
      }

      const editor = editorRef.current;
      const block = editor?.querySelector(
        `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(drag.blockId)}"]`
      );
      if (!editor || !block) return false;
      if (!placeVisualAtDropTarget(block, placement)) return false;

      if (drag.family === "math" && drag.mathState) {
        // Moving Math changes only document order/position. Reassert the exact
        // component source/meta captured at pointer-down so drag can never mutate
        // alignment, dimensions, font, offsets, or appearance as a side effect.
        block.setAttribute(DOC_BLOCK_SOURCE_ATTR, drag.mathState.source);
        block.setAttribute(
          DOC_BLOCK_CANONICAL_SOURCE_ATTR,
          drag.mathState.canonicalSource
        );
        applyDocWriterMathOuterContract(
          block,
          drag.mathState.source,
          drag.mathState.alignment
        );
      }

      const afterHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      lastEmittedHtmlRef.current = afterHtml;
      markLocalMutation();
      onChange?.(afterHtml);
      recordEditorTransaction(
        {
          transactionType: drag.family || "visual",
          transactionId: drag.transactionId,
          operation: `${drag.family || "visual"}-move`,
          blockId: drag.blockId,
          beforeHtml: drag.beforeHtml,
          afterHtml,
        },
        afterHtml
      );
      selectedVisualIdRef.current = drag.blockId;
      scheduleRepagination({ immediate: true, preserveSelection: false, force: false });
      return true;
    };

    const handleUnifiedObjectDragMove = (event) => {
      const drag = objectDragRef.current;
      if (!drag) return;
      event.preventDefault();
      drag.pointerX = event.clientX;
      drag.pointerY = event.clientY;
      updateObjectDragGhost(drag, event.clientX, event.clientY);
      updateObjectDropIndicator(event.clientX, event.clientY);
      scheduleObjectDragAutoScroll(event.clientX, event.clientY);
    };

    const handleUnifiedObjectDragEnd = (event) => {
      if (objectDragRef.current) {
        objectDragRef.current.pointerX = event.clientX;
        objectDragRef.current.pointerY = event.clientY;
        updateObjectDragGhost(objectDragRef.current, event.clientX, event.clientY);
        updateObjectDropIndicator(event.clientX, event.clientY);
      }
      finishUnifiedObjectDrag({ cancel: false });
    };

    const handleUnifiedObjectDragCancel = () => {
      finishUnifiedObjectDrag({ cancel: true });
    };

    const beginUnifiedObjectDrag = (event, detail = {}) => {
      if (effectiveReadOnly || !event) return false;
      const editor = editorRef.current;
      if (!editor) return false;
      const kind = detail.kind === "table" ? "table" : "visual";
      const tableId = String(detail.tableId || "");
      const blockId = String(detail.blockId || "");
      if ((kind === "table" && !tableId) || (kind === "visual" && !blockId)) {
        return false;
      }

      event.preventDefault?.();
      event.stopPropagation?.();
      finishUnifiedObjectDrag({ cancel: true });

      const visualBlockAtStart =
        kind === "visual"
          ? editor.querySelector(
              `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(blockId)}"]`
            )
          : null;
      const dragFamily =
        kind === "visual"
          ? normalizeVisualFamily(
              detail.family ||
                visualBlockAtStart?.getAttribute(DOC_BLOCK_TYPE_ATTR) ||
                "visual"
            )
          : detail.family || "table";
      const mathState =
        dragFamily === "math" && visualBlockAtStart
          ? {
              source: String(
                visualBlockAtStart.getAttribute(DOC_BLOCK_SOURCE_ATTR) || ""
              ),
              canonicalSource: String(
                visualBlockAtStart.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
                  ""
              ),
              alignment: normalizeDocWriterMathAlignment(
                detail.componentMeta?.alignment,
                visualBlockAtStart.getAttribute(DOC_VISUAL_ALIGNMENT_ATTR) ||
                  "center"
              ),
            }
          : null;

      const beforeHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      objectDragRef.current = {
        kind,
        tableId,
        blockId,
        family: dragFamily,
        beforeHtml,
        transactionId: createDocumentObjectId("editortx"),
        pointerX: event.clientX,
        pointerY: event.clientY,
        dropPlacement: null,
        mathState,
      };

      if (kind === "table") {
        tableDragRef.current = objectDragRef.current;
        getLogicalTableFragments(editor, tableId).forEach((fragment) => {
          fragment.setAttribute(CANVAS_TABLE_DRAGGING_ATTR, "true");
          fragment.setAttribute("data-doc-dragging", "true");
        });
      } else {
        const block = editor.querySelector(
          `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(blockId)}"]`
        );
        block?.setAttribute(CANVAS_VISUAL_DRAGGING_ATTR, "true");
        block?.setAttribute("data-doc-dragging", "true");
        selectedVisualIdRef.current = blockId;
      }

      createObjectDragGhost(objectDragRef.current, event);
      editor.style.cursor = "grabbing";
      updateObjectDragGhost(objectDragRef.current, event.clientX, event.clientY);
      updateObjectDropIndicator(event.clientX, event.clientY);
      scheduleObjectDragAutoScroll(event.clientX, event.clientY);
      window.addEventListener("pointermove", handleUnifiedObjectDragMove, true);
      window.addEventListener("pointerup", handleUnifiedObjectDragEnd, true);
      window.addEventListener("pointercancel", handleUnifiedObjectDragCancel, true);
      return true;
    };

    const finishTableDragState = () => finishUnifiedObjectDrag({ cancel: true });

    const handleCanvasDragStart = (event) => {
      // DocWriterChart exposes a draggable-looking handle for accessibility, but
      // Canvas movement is pointer-driven by the parent Editor for stable cross-page
      // auto-scroll. Suppress browser HTML5 DnD so it cannot fight that system.
      if (
        closestWithin(editorRef.current, event.target, '[data-doc-visual-drag-handle="true"]')
      ) {
        event.preventDefault();
      }
    };

    const handleCanvasDragOver = (event) => {
      if (!objectDragRef.current) return;
      event.preventDefault();
      updateObjectDropIndicator(event.clientX, event.clientY);
      scheduleObjectDragAutoScroll(event.clientX, event.clientY);
    };

    const handleCanvasDragEnd = () => {
      if (objectDragRef.current) finishUnifiedObjectDrag({ cancel: false });
    };

    const handleCanvasPointerDownCapture = (event) => {
      const editor = editorRef.current;
      if (!editor || effectiveReadOnly) return;
      const table = closestWithin(editor, event.target, "table");
      const visual = closestWithin(
        editor,
        event.target,
        `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
      );
      if (visual) {
        selectedTableIdRef.current = "";
        selectedCellIdRef.current = "";
        docWriterTable.closeEditPanel?.();
      } else if (table) {
        selectedVisualIdRef.current = "";
      } else {
        docWriterTable.closeEditPanel?.();
      }
    };

    const handleCanvasPointerDown = (event) => {
      const editor = editorRef.current;
      if (!editor || effectiveReadOnly) return;

      const pageBreak = closestWithin(editor, event.target, PAGE_BREAK_SELECTOR);
      if (pageBreak) {
        event.preventDefault();
        event.stopPropagation();
        selectManualPageBreak(pageBreak);
        focusEditorWithoutOuterScroll();
        return;
      }

      const page = canvasPageFromNode(event.target);
      if (page) {
        // Clicking inside the Canvas must never re-import/rewrite tables or
        // protected objects. Child components already own their live state.
        const resizeTarget = pointerResizeTarget(event);

        if (resizeTarget?.kind === "visual-select") {
          event.preventDefault();
          selectProtectedBlock(resizeTarget.block);
          focusEditorWithoutOuterScroll();
          return;
        }

        if (resizeTarget?.kind === "table-select") {
          selectCanvasTable(resizeTarget.table, resizeTarget.cell);
          return;
        }

        const table = closestWithin(editor, event.target, "table");
        const visual = closestWithin(
          editor,
          event.target,
          `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
        );
        if (!table && !visual) {
          clearSelectedObjects();

          // A click on unused white Letter-page space is an explicit intent to
          // type. Do not leave caret placement to the browser at a table/visual
          // boundary: immediately establish a real paragraph inside page flow.
          if (event.target === page) {
            event.preventDefault();
            focusEditorWithoutOuterScroll();
            placeCaretAtCanvasPoint(
              page,
              event.clientX,
              event.clientY
            );
          }
        }
        return;
      }

      // The flex gap around/between pages is presentation-only. Redirect a
      // gutter click into the nearest Letter page before the browser can place
      // a caret beside a page wrapper.
      const nearest = nearestCanvasPage(event.clientY);
      if (!nearest?.page) return;
      event.preventDefault();
      focusEditorWithoutOuterScroll();
      const atEnd =
        event.clientY >= nearest.rect.top + nearest.rect.height / 2;
      placeCaretInsideCanvasPage(nearest.page, atEnd);
    };

    const handleBeforeInput = (event) => {
      if (effectiveReadOnly) return;
      const editor = editorRef.current;
      if (!editor) return;

      // Capture the outer Status-page scroll before the browser applies the edit.
      // contentEditable can otherwise make an ancestor scroll while inserting or
      // deleting near a page boundary. The Canvas workspace may move; its parent may not.
      outerScrollGuardRef.current = captureOuterScrollState(
        wrapRef.current || editor,
        workspaceRef.current
      );

      // Second safety layer: every edit must begin inside an actual page, never
      // on the outer contentEditable flex gutter.
      if (!ensureCaretInsideCanvasPage()) {
        event.preventDefault();
        return;
      }

      const inputType = String(
        event.nativeEvent?.inputType || event.inputType || ""
      );
      const typingColor = editor.getAttribute("data-doc-typing-color") || "";
      const tableContext = docWriterTable.getActiveContext?.() || null;

      if (inputType.startsWith("delete") && tableContext?.active) {
        const selection = window.getSelection?.();
        const anchorCell = closestWithin(editor, selection?.anchorNode, "th, td");
        const focusCell = closestWithin(editor, selection?.focusNode, "th, td");

        // Structural deletion must always be explicit. A browser/mobile delete
        // selection that is not wholly inside one cell is blocked so it cannot
        // merge cells, remove rows, or delete the whole table.
        if (!anchorCell || !focusCell || anchorCell !== focusCell) {
          event.preventDefault();
          pendingTableTextEditRef.current = null;
          return;
        }

        // Mobile/context-menu deletion can bypass keydown. Protect the exact
        // beginning/end of a cell here as well so Backspace/Delete can never
        // escape the cell and become a structural table deletion.
        if (selection?.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          if (range.collapsed) {
            let cellOffset = 0;
            try {
              const probe = editor.ownerDocument.createRange();
              probe.selectNodeContents(anchorCell);
              probe.setEnd(range.startContainer, range.startOffset);
              cellOffset = probe.toString().length;
            } catch (_) {
              cellOffset = 0;
            }

            const totalCellText = String(anchorCell.textContent || "").length;
            const backwardDelete =
              inputType === "deleteContentBackward" || /Backward$/i.test(inputType);
            const forwardDelete =
              inputType === "deleteContentForward" || /Forward$/i.test(inputType);

            if (
              (backwardDelete && cellOffset <= 0) ||
              (forwardDelete && cellOffset >= totalCellText)
            ) {
              event.preventDefault();
              pendingTableTextEditRef.current = null;
              return;
            }
          }
        }
      }

      if (tableContext?.active && tableContext.cell) {
        pendingTableTextEditRef.current = {
          transactionId: createDocumentObjectId("editortx"),
          tableId: tableContext.tableId || "",
          cellId: tableContext.cellId || "",
          inputType,
          beforeHtml: serializeEditorHtml(editor, {
            preserveEditingBoundaries: true,
          }),
        };
      } else {
        pendingTableTextEditRef.current = null;

        // Native browser Undo becomes unreliable after pagination moves DOM
        // between Letter pages. Capture ordinary text mutations in the Editor's
        // own history so Toolbar Undo/Redo remains authoritative.
        if (
          inputType &&
          !["historyUndo", "historyRedo", "insertCompositionText"].includes(inputType) &&
          !historyRestoringRef.current
        ) {
          const now = Date.now();
          const family = inputType.startsWith("delete")
            ? "delete"
            : ["insertParagraph", "insertLineBreak"].includes(inputType)
            ? "paragraph"
            : "typing";
          const previousGroup = nativeTypingGroupRef.current;
          const reuseGroup = Boolean(
            previousGroup &&
            previousGroup.family === family &&
            now - Number(previousGroup.lastAt || 0) < 900
          );
          const group = reuseGroup
            ? previousGroup
            : {
                family,
                transactionId: createDocumentObjectId("editortx"),
                beforeHtml: serializeEditorHtml(editor, {
                  preserveEditingBoundaries: true,
                }),
                lastAt: now,
              };
          group.lastAt = now;
          nativeTypingGroupRef.current = group;
          pendingNativeEditRef.current = {
            transactionId: group.transactionId,
            beforeHtml: group.beforeHtml,
            inputType,
          };
        } else {
          pendingNativeEditRef.current = null;
        }
      }

      if (["insertParagraph", "insertLineBreak"].includes(inputType)) {
        pendingParagraphInputRef.current = true;
      }

      if (
        typingColor &&
        [
          "insertText",
          "insertCompositionText",
          "insertParagraph",
          "insertLineBreak",
        ].includes(inputType)
      ) {
        try {
          document.execCommand("styleWithCSS", false, "true");
          document.execCommand("foreColor", false, typingColor);
        } catch (_) {
          // Native insertion continues even if a browser rejects the command.
        }
      }
    };

    const handleCanvasDrop = (event) => {
      if (objectDragRef.current) {
        event.preventDefault();
        event.stopPropagation();
        updateObjectDropIndicator(event.clientX, event.clientY);
        finishUnifiedObjectDrag({ cancel: false });
        return;
      }

      if (!canvasPageFromNode(event.target)) {
        event.preventDefault();
      }
    };

    const handleCompositionStart = () => {
      composingRef.current = true;
    };

    const handleCompositionEnd = () => {
      composingRef.current = false;
      handleInput();
    };

    const containStrayCanvasNodes = () => {
      const editor = editorRef.current;
      if (!editor) return;
      const pages = Array.from(
        editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
      );
      const page = pages[pages.length - 1] || pages[0];
      if (!page) return;

      Array.from(editor.childNodes || []).forEach((node) => {
        const isPage =
          node.nodeType === 1 &&
          node.getAttribute?.("data-doc-canvas-page") === "true";
        if (!isPage) page.appendChild(node);
      });

      // If the browser ever emits prose directly under a Letter page (especially
      // beside a table/contenteditable=false visual), immediately put that prose
      // back into a normal wrapping paragraph. Existing tables/visuals are untouched.
      pages.forEach((pageNode) =>
        normalizeDirectCanvasProseChildren(pageNode)
      );
    };

    // Toolbar controls and dialogs temporarily take focus away from contentEditable.
    // Keep BOTH a live cloned Range and a serializable snapshot. While the toolbar
    // interaction is active pagination is paused, so the live Range is the most
    // exact insertion point. The snapshot is the durable fallback after a DOM
    // rebuild, page move, or parent render.
    const captureToolbarSelection = () => {
      const editor = editorRef.current;
      if (!editor) return toolbarSelectionRef.current;

      try {
        const selection = window.getSelection?.();
        if (
          selection &&
          selection.rangeCount > 0 &&
          editor.contains(selection.anchorNode) &&
          editor.contains(selection.focusNode)
        ) {
          toolbarLiveRangeRef.current = selection.getRangeAt(0).cloneRange();
        }
      } catch (_) {}

      const snapshot = captureEditorSelectionState(editor);
      if (snapshot) toolbarSelectionRef.current = snapshot;
      return snapshot || toolbarSelectionRef.current;
    };

    const restoreToolbarSelection = (
      snapshot = toolbarSelectionRef.current
    ) => {
      const editor = editorRef.current;
      if (!editor) return false;

      focusEditorWithoutOuterScroll();

      // First restore the exact live Range when its DOM nodes are still attached.
      // This is especially important for Table insertion because clicking the
      // toolbar dialog must not move the insertion point to the end of the page.
      const liveRange = toolbarLiveRangeRef.current;
      if (liveRange) {
        try {
          const common =
            liveRange.commonAncestorContainer?.nodeType === 1
              ? liveRange.commonAncestorContainer
              : liveRange.commonAncestorContainer?.parentElement;
          if (
            common &&
            common.isConnected &&
            editor.contains(common) &&
            liveRange.startContainer?.isConnected &&
            liveRange.endContainer?.isConnected
          ) {
            const selection = window.getSelection?.();
            selection?.removeAllRanges?.();
            selection?.addRange?.(liveRange.cloneRange());
            const restoredSnapshot = captureEditorSelectionState(editor);
            if (restoredSnapshot) toolbarSelectionRef.current = restoredSnapshot;
            return true;
          }
        } catch (_) {}
      }

      if (!snapshot) return false;
      restoreEditorSelectionState(editor, snapshot);
      const restored = captureEditorSelectionState(editor);
      if (restored) {
        toolbarSelectionRef.current = restored;
        try {
          const selection = window.getSelection?.();
          if (
            selection &&
            selection.rangeCount > 0 &&
            editor.contains(selection.anchorNode) &&
            editor.contains(selection.focusNode)
          ) {
            toolbarLiveRangeRef.current = selection.getRangeAt(0).cloneRange();
          }
        } catch (_) {}
      }
      return Boolean(restored);
    };

    const beginToolbarInteraction = () => {
      captureToolbarSelection();
      toolbarInteractionRef.current = true;

      if (paginationTimerRef.current) {
        window.clearTimeout(paginationTimerRef.current);
        paginationTimerRef.current = null;
      }
      if (paginationFrameRef.current) {
        window.cancelAnimationFrame(paginationFrameRef.current);
        paginationFrameRef.current = null;
      }
    };

    const endToolbarInteraction = ({
      restore = true,
      snapshot = null,
    } = {}) => {
      const pendingTableId = pendingManualTableIdRef.current;
      toolbarInteractionRef.current = false;

      if (restore) {
        restoreToolbarSelection(snapshot || toolbarSelectionRef.current);
      }

      // Manual table insertion is a toolbar-owned transaction. Do not let blur,
      // prop synchronization, or pagination race the table while the dialog is
      // still closing. Once the Toolbar explicitly ends the interaction, inspect
      // the surviving live table and paginate only when the Letter page actually
      // overflows. This is the single post-insert settle point.
      if (pendingTableId && !restore) {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            const editor = editorRef.current;
            const liveTable = editor
              ? editor.querySelector(
                  `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(
                    pendingTableId
                  )}"]`
                )
              : null;

            if (!editor || !liveTable) {
              if (pendingManualTableIdRef.current === pendingTableId) {
                pendingManualTableIdRef.current = "";
              }
              return;
            }

            if (canvasNeedsRepagination(editor)) {
              scheduleRepagination({
                immediate: false,
                preserveSelection: true,
                force: false,
              });
            } else if (pendingManualTableIdRef.current === pendingTableId) {
              pendingManualTableIdRef.current = "";
            }
          });
        });
      }

      if (pendingToolbarObjectLayoutRef.current && !restore) {
        pendingToolbarObjectLayoutRef.current = false;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scheduleDocWriterVisualScan({ force: false });
            scheduleRepagination({
              immediate: false,
              preserveSelection: true,
              force: false,
            });
          });
        });
      }
    };

    const updateCounts = () => {
      try {
        const text = editorRef.current?.innerText || "";
        const words = text.trim().length
          ? text.trim().split(/\s+/).filter(Boolean).length
          : 0;

        setCounts({
          words,
          chars: text.length,
        });
      } catch (_) {
        // Counting should never interrupt editing.
      }
    };

    const renderPaginatedHtml = (
      nextHtml,
      {
        preserveSelection = false,
        keepViewport = true,
        caretMarkerId = "",
        selectionSnapshot: suppliedSelectionSnapshot = null,
        normalizeIngressLayout = false,
      } = {}
    ) => {
      const editor = editorRef.current;
      if (!editor) return "";

      // During a manual table transaction, never rebuild the live Canvas from an
      // HTML snapshot that does not contain the table that is visibly present.
      // This is a final stale-prop / blur guard around every direct pagination
      // call site, not only scheduleRepagination().
      const pendingTableId = pendingManualTableIdRef.current;
      const pendingLiveTable =
        pendingTableId
          ? editor.querySelector(
              `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(pendingTableId)}"]`
            )
          : null;
      if (
        pendingLiveTable &&
        !htmlContainsLogicalTableId(editor.ownerDocument, nextHtml, pendingTableId)
      ) {
        return serializeEditorHtml(editor, {
          preserveEditingBoundaries: true,
        });
      }

      const requestedPageMap = (() => {
        try {
          const holder = editor.ownerDocument.createElement("div");
          holder.innerHTML = String(nextHtml || "");
          return Array.from(holder.children || []).some((child) =>
            isPersistedCanvasPageWrapper(child)
          );
        } catch (_) {
          return false;
        }
      })();

      const cleaned = normalizeDocumentHtml(nextHtml, {
        preserveCaretMarkers: Boolean(caretMarkerId),
        preserveEditingBoundaries: true,
      });
      const selectionSnapshot = preserveSelection
        ? suppliedSelectionSnapshot || captureEditorSelectionState(editor)
        : suppliedSelectionSnapshot;
      const selectedTableId = selectedTableIdRef.current;
      const selectedCellId = selectedCellIdRef.current;
      const selectedVisualId = selectedVisualIdRef.current;
      const reusableVisualNodes = captureReusableVisualNodes(editor);
      const workspace = workspaceRef.current;
      const viewportSnapshot = keepViewport ? captureCanvasViewport() : null;

      // Pagination happens synchronously in the real Letter-size editor so its
      // measurements are identical to the visible document. Existing protected
      // visual DOM nodes are moved through the pagination source and reused, so
      // charts are not destroyed/recreated merely because text crossed a page.
      paginatingRef.current = true;
      try {
        const pages = paginateHtmlIntoEditor(editor, cleaned, {
          reusableVisualNodes,
          normalizeIngressLayout,
        });
        normalizeTablesWithDocWriterTable(editor, {
          source: "reopened",
          inferWidths: false,
        });
        ensureCanvasTableVisibility(editor);
        normalizeProtectedBlocks(editor);
        setPageCount(pages);
        updateCounts();

        let markerRestored = false;
        if (caretMarkerId) {
          markerRestored = restoreCaretFromMarker(editor, caretMarkerId);
        }

        if (!markerRestored && selectionSnapshot) {
          restoreEditorSelectionState(editor, selectionSnapshot);
        }
        if (selectionSnapshot || markerRestored) {
          toolbarSelectionRef.current =
            captureEditorSelectionState(editor) || selectionSnapshot;
        }

        if (selectedTableId) {
          const fragments = getLogicalTableFragments(editor, selectedTableId);
          fragments.forEach((fragmentNode) => {
            fragmentNode.setAttribute(CANVAS_SELECTED_TABLE_ATTR, "true");
          });
          if (selectedCellId) {
            editor
              .querySelector(
                `[${DOC_CELL_ID_ATTR}="${escapeCssValue(selectedCellId)}"]`
              )
              ?.setAttribute(CANVAS_SELECTED_CELL_ATTR, "true");
          }
        } else if (selectedVisualId) {
          // Keep the Editor's logical visual selection without exposing a second
          // contextual toolbar; DocWriterChart/DocWriterMath own their Edit UI.
          selectedVisualIdRef.current = selectedVisualId;
        }
      } finally {
        paginatingRef.current = false;
      }

      // Non-forced scan: unchanged DocWriterChart/DocWriterMath hosts keep their
      // existing React roots. Only new or changed protected blocks mount.
      scheduleDocWriterVisualScan({ force: false });

      if (viewportSnapshot) {
        restoreCanvasViewport(viewportSnapshot, { useAnchor: false });
        window.requestAnimationFrame(() => {
          // During ordinary text pagination, keep caret navigation inside the
          // Canvas workspace only. Never ask the browser/Status page to reveal it.
          if (objectViewportAnchorRef.current.size > 0 || !workspace) return;
          const activeSelection = window.getSelection?.();
          let caretRect = null;
          try {
            if (activeSelection?.rangeCount && editor.contains(activeSelection.anchorNode)) {
              caretRect = activeSelection.getRangeAt(0).getBoundingClientRect();
            }
          } catch (_) {}
          const workspaceRect = workspace.getBoundingClientRect();
          if (!caretRect || !workspaceRect) return;
          if (caretRect.bottom > workspaceRect.bottom - 18) {
            workspace.scrollTop += caretRect.bottom - workspaceRect.bottom + 28;
          } else if (caretRect.top < workspaceRect.top + 18) {
            workspace.scrollTop -= workspaceRect.top - caretRect.top + 28;
          }
          restoreOuterScrollState(viewportSnapshot.outerScroll);
        });
      }

      return requestedPageMap
        ? serializeEditorHtmlWithCanvasPages(editor, {
            preserveCaretMarkers: Boolean(caretMarkerId),
          })
        : serializeEditorHtml(editor, {
            preserveEditingBoundaries: true,
          });
    };

    const rebalanceLiveCanvasPages = ({
      preserveSelection = true,
      compact = false,
      keepViewport = true,
    } = {}) => {
      const editor = editorRef.current;
      if (!editor || reflowingLiveCanvasRef.current) return false;

      const selectionSnapshot = preserveSelection
        ? captureEditorSelectionState(editor)
        : null;
      const viewportSnapshot = keepViewport ? captureCanvasViewport() : null;
      const outerSnapshot = captureOuterScrollState(
        wrapRef.current || editor,
        workspaceRef.current
      );
      let changed = false;
      let safety = 0;

      const pages = () =>
        Array.from(
          editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
        );

      const ensurePageAfter = (page) => {
        const sibling = page?.nextElementSibling;
        if (sibling?.getAttribute?.("data-doc-canvas-page") === "true") {
          return sibling;
        }
        const nextPage = createCanvasPage(editor.ownerDocument, pages().length + 1);
        nextPage.setAttribute(
          "data-placeholder",
          editor.getAttribute("data-placeholder") || ""
        );
        page?.parentNode?.insertBefore(nextPage, page.nextSibling || null);
        changed = true;
        return nextPage;
      };

      const meaningfulDirectNodes = (page) =>
        Array.from(page?.childNodes || []).filter((node) =>
          nodeHasRenderableContent(node)
        );

      const hardBreakInPage = (page) =>
        Array.from(page?.children || []).find((child) => isPageBreakElement(child)) || null;

      const moveAfterHardBreak = (page) => {
        const hardBreak = hardBreakInPage(page);
        if (!hardBreak) return;
        const trailing = [];
        let cursor = hardBreak.nextSibling;
        while (cursor) {
          const next = cursor.nextSibling;
          trailing.push(cursor);
          cursor = next;
        }
        if (!trailing.length) return;
        const nextPage = ensurePageAfter(page);
        const reference = nextPage.firstChild || null;
        trailing.forEach((node) => nextPage.insertBefore(node, reference));
        changed = true;
      };

      const pageNeedsForwardFlow = (page) =>
        pageIsOverflowing(page) || pageHasAtomicOverflow(page);

      const lastMovableNode = (page) => {
        const nodes = meaningfulDirectNodes(page);
        for (let index = nodes.length - 1; index >= 0; index -= 1) {
          if (!isPageBreakElement(nodes[index])) return nodes[index];
        }
        return null;
      };

      const ensureCaretVisibleInWorkspace = () => {
        const workspace = workspaceRef.current;
        if (!workspace) return;
        const selection = window.getSelection?.();
        if (!selection?.rangeCount || !editor.contains(selection.anchorNode)) return;
        let rect = null;
        try { rect = selection.getRangeAt(0).getBoundingClientRect(); } catch (_) {}
        if (!rect) return;
        const workspaceRect = workspace.getBoundingClientRect();
        if (rect.bottom > workspaceRect.bottom - 18) {
          workspace.scrollTop += rect.bottom - workspaceRect.bottom + 28;
        } else if (rect.top < workspaceRect.top + 18) {
          workspace.scrollTop -= workspaceRect.top - rect.top + 28;
        }
      };

      reflowingLiveCanvasRef.current = true;
      paginatingRef.current = true;
      try {
        if (!pages().length) {
          const page = createCanvasPage(editor.ownerDocument, 1);
          page.setAttribute(
            "data-placeholder",
            editor.getAttribute("data-placeholder") || ""
          );
          editor.appendChild(page);
          changed = true;
        }

        // Manual page breaks are hard document boundaries. Move anything after a
        // break to the following page without cloning or serializing live objects.
        pages().forEach((page) => moveAfterHardBreak(page));

        // Forward flow: move existing DOM nodes, never clone Chart/Table/Math/Image.
        // This keeps React roots, table identity, selection, and object controls alive.
        let index = 0;
        while (index < pages().length && safety < 1200) {
          safety += 1;
          const page = pages()[index];
          if (!page) break;
          moveAfterHardBreak(page);

          let pageSafety = 0;
          while (pageNeedsForwardFlow(page) && pageSafety < 200) {
            pageSafety += 1;
            const movable = lastMovableNode(page);
            if (!movable) break;
            const meaningful = meaningfulDirectNodes(page).filter(
              (node) => !isPageBreakElement(node)
            );

            // If one object/paragraph is taller than an entire printable page,
            // there is nowhere valid to move it. Keep one copy on its own page and
            // mark the condition rather than duplicating/slicing it into the gutter.
            if (meaningful.length <= 1) {
              page.setAttribute(CANVAS_OVERFLOW_ATTR, "true");
              if (movable.nodeType === 1 && isAtomicCanvasObject(movable)) {
                movable.setAttribute("data-doc-atomic-oversize", "true");
              }
              break;
            }

            const nextPage = ensurePageAfter(page);
            const boundary = hardBreakInPage(page);
            if (boundary) {
              // Preserve document order when content immediately before a manual
              // break grows. Move the boundary with that content, then let the
              // hard-break pass push following content to the subsequent page.
              nextPage.insertBefore(boundary, nextPage.firstChild || null);
              nextPage.insertBefore(movable, boundary);
              moveAfterHardBreak(nextPage);
            } else {
              nextPage.insertBefore(movable, nextPage.firstChild || null);
            }
            movable.removeAttribute?.("data-doc-atomic-oversize");
            page.removeAttribute(CANVAS_OVERFLOW_ATTR);
            changed = true;
          }
          index += 1;
        }

        if (compact) {
          // Backflow after deletion/object removal. Try whole live nodes one at a
          // time and immediately undo the move if the previous page would overflow.
          let compactIndex = 0;
          while (compactIndex < pages().length - 1 && safety < 2000) {
            safety += 1;
            const livePages = pages();
            const page = livePages[compactIndex];
            const nextPage = livePages[compactIndex + 1];
            if (!page || !nextPage) break;
            if (pageEndsWithHardBreak(page)) {
              compactIndex += 1;
              continue;
            }

            let movedAny = false;
            let compactSafety = 0;
            while (nextPage.firstChild && compactSafety < 200) {
              compactSafety += 1;
              const candidate = nextPage.firstChild;
              if (candidate?.nodeType === 1 && isPageBreakElement(candidate)) break;
              page.appendChild(candidate);
              if (pageNeedsForwardFlow(page)) {
                nextPage.insertBefore(candidate, nextPage.firstChild || null);
                break;
              }
              movedAny = true;
              changed = true;
            }

            if (movedAny && !pageNeedsForwardFlow(page)) {
              page.removeAttribute(CANVAS_OVERFLOW_ATTR);
            }
            compactIndex += 1;
          }
        }

        let livePages = pages();
        for (let i = livePages.length - 1; i > 0; i -= 1) {
          const page = livePages[i];
          if (
            !pageHasMeaningfulContent(page) &&
            !isIntentionalBlankPageAfterBreak(page, livePages)
          ) {
            page.remove();
            changed = true;
            livePages = pages();
          }
        }

        livePages = pages();
        livePages.forEach((page, pageIndex) => {
          page.setAttribute("data-page-number", String(pageIndex + 1));
          page.setAttribute("aria-label", `Document page ${pageIndex + 1}`);
          if (!pageNeedsForwardFlow(page)) {
            page.removeAttribute(CANVAS_OVERFLOW_ATTR);
          }
        });
        setPageCount(Math.max(1, livePages.length));
        updateCounts();

        // Moving a live DOM node normally preserves Range endpoints. Restore only
        // when the browser actually lost the selection.
        if (selectionSnapshot) {
          const activeSelection = window.getSelection?.();
          const selectionStillLive = Boolean(
            activeSelection?.rangeCount &&
            editor.contains(activeSelection.anchorNode) &&
            editor.contains(activeSelection.focusNode)
          );
          if (!selectionStillLive) restoreEditorSelectionState(editor, selectionSnapshot);
        }
      } finally {
        paginatingRef.current = false;
        reflowingLiveCanvasRef.current = false;
      }

      if (viewportSnapshot) restoreCanvasViewport(viewportSnapshot, { useAnchor: false });
      restoreOuterScrollState(outerSnapshot);
      window.requestAnimationFrame(() => {
        restoreOuterScrollState(outerSnapshot);
        ensureCaretVisibleInWorkspace();
        docWriterTable.refreshControls?.();
      });
      return changed;
    };

    const scheduleRepagination = ({
      immediate = false,
      preserveSelection = true,
      force = false,
    } = {}) => {
      if (paginationTimerRef.current) {
        window.clearTimeout(paginationTimerRef.current);
        paginationTimerRef.current = null;
      }

      const run = () => {
        paginationTimerRef.current = null;
        if (paginationFrameRef.current) {
          window.cancelAnimationFrame(paginationFrameRef.current);
        }

        paginationFrameRef.current = window.requestAnimationFrame(() => {
          paginationFrameRef.current = null;
          const editor = editorRef.current;
          if (
            !editor ||
            toolbarInteractionRef.current ||
            objectEditingKeysRef.current.size > 0 ||
            objectResizeRef.current ||
            composingRef.current ||
            canvasInteractionIsMoving(editor) ||
            reflowingLiveCanvasRef.current
          ) {
            return;
          }

          if (!force && !canvasNeedsRepagination(editor)) return;

          // Routine pagination is now a live-DOM operation. It moves existing
          // nodes between Letter pages and never serializes/recreates the entire
          // Canvas. This is the central protection against table disappearance,
          // React visual remounts, flicker, and outer Status-page jumping.
          rebalanceLiveCanvasPages({
            preserveSelection,
            compact: force,
            keepViewport: true,
          });
        });
      };

      const editor = editorRef.current;
      const needsImmediateForwardFlow = Boolean(
        editor && canvasNeedsRepagination(editor)
      );

      // Routine typing can remain lightly debounced while the page has room, but
      // once the printable area overflows (or a hard break has trailing content),
      // flow on the very next animation frame. Continuous typing must never appear
      // to continue inside the bottom margin while a debounce timer keeps resetting.
      if (immediate || needsImmediateForwardFlow) run();
      else {
        paginationTimerRef.current = window.setTimeout(run, PAGINATION_DELAY_MS);
      }
    };

    const settleCanvasPaginationNow = ({
      compact = true,
      preserveSelection = true,
    } = {}) => {
      const editor = editorRef.current;
      if (!editor || reflowingLiveCanvasRef.current) return false;

      if (paginationTimerRef.current) {
        window.clearTimeout(paginationTimerRef.current);
        paginationTimerRef.current = null;
      }
      if (paginationFrameRef.current) {
        window.cancelAnimationFrame(paginationFrameRef.current);
        paginationFrameRef.current = null;
      }

      let changed = false;
      let pass = 0;
      let requestCompaction = compact;

      while (pass < 4) {
        const needsFlow = canvasNeedsRepagination(editor);
        if (!needsFlow && !requestCompaction) break;

        const passChanged = rebalanceLiveCanvasPages({
          preserveSelection,
          compact: requestCompaction,
          keepViewport: true,
        });
        changed = passChanged || changed;
        requestCompaction = false;
        pass += 1;

        if (!passChanged && !canvasNeedsRepagination(editor)) break;
      }

      return changed;
    };

    const scheduleVisualCompletionRepagination = () => {
      // Chart libraries can report several tiny geometry changes while a visual
      // settles. Wait for a quiet window, then paginate only if document flow
      // actually requires it. This prevents chart render -> pagination -> chart
      // resize feedback loops.
      if (visualRepaginationTimerRef.current) {
        window.clearTimeout(visualRepaginationTimerRef.current);
      }

      visualRepaginationTimerRef.current = window.setTimeout(() => {
        visualRepaginationTimerRef.current = null;
        if (visualRepaginationFrameRef.current) {
          window.cancelAnimationFrame(visualRepaginationFrameRef.current);
        }

        visualRepaginationFrameRef.current = window.requestAnimationFrame(() => {
          visualRepaginationFrameRef.current = null;
          const editor = editorRef.current;
          if (
            !editor ||
            paginatingRef.current ||
            objectResizeRef.current ||
            canvasInteractionIsMoving(editor)
          ) {
            return;
          }
          scheduleRepagination({
            immediate: true,
            preserveSelection: true,
            force: false,
          });
        });
      }, VISUAL_LAYOUT_SETTLE_MS);
    };


    const recordEditorTransaction = (details = {}, committedHtml = "") => {
      if (historyRestoringRef.current) return false;
      const transactionType = String(details.transactionType || "").toLowerCase();
      if (!["text", "table", "chart", "math", "image", "visual"].includes(transactionType)) {
        return false;
      }

      const beforeHtml = normalizeDocumentHtml(details.beforeHtml || "", {
        preserveEditingBoundaries: true,
      });
      const afterHtml = normalizeDocumentHtml(
        committedHtml || details.afterHtml || "",
        { preserveEditingBoundaries: true }
      );
      if (beforeHtml === afterHtml) return false;

      const entry = {
        transactionId:
          String(details.transactionId || "") || createDocumentObjectId("editortx"),
        transactionType,
        operation: String(details.operation || `${transactionType}-mutation`),
        tableId: String(details.tableId || ""),
        focusCellId: String(details.focusCellId || ""),
        blockId: String(details.blockId || details.chartId || ""),
        beforeHtml,
        afterHtml,
      };

      const stack = editorUndoStackRef.current;
      const last = stack[stack.length - 1];
      if (last?.transactionId === entry.transactionId) {
        stack[stack.length - 1] = {
          ...last,
          ...entry,
          beforeHtml: last.beforeHtml || entry.beforeHtml,
        };
      } else {
        stack.push(entry);
        if (stack.length > EDITOR_HISTORY_LIMIT) stack.shift();
      }
      editorRedoStackRef.current = [];
      latestEditorHistoryKindRef.current = transactionType;
      return true;
    };

    const restoreEditorTransactionEntry = (entry, direction = "undo") => {
      const editor = editorRef.current;
      if (!editor || !entry) return false;
      const targetHtml = direction === "redo" ? entry.afterHtml : entry.beforeHtml;
      if (targetHtml == null) return false;

      historyRestoringRef.current = true;
      pendingManualTableIdRef.current = "";
      pendingTableTextEditRef.current = null;
      try {
        const cleaned = renderPaginatedHtml(targetHtml, {
          preserveSelection: false,
          keepViewport: true,
        });
        lastEmittedHtmlRef.current = cleaned;
        onChange?.(cleaned);
        updateCounts();

        window.requestAnimationFrame(() => {
          const table = entry.tableId
            ? editorRef.current?.querySelector(
                `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(entry.tableId)}"]`
              )
            : null;
          const cell = entry.focusCellId
            ? editorRef.current?.querySelector(
                `[${DOC_CELL_ID_ATTR}="${escapeCssValue(entry.focusCellId)}"]`
              )
            : null;
          if (table) {
            selectCanvasTable(table, cell || null);
          } else if (entry.blockId) {
            const block = editorRef.current?.querySelector(
              `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(entry.blockId)}"]`
            );
            if (block) selectProtectedBlock(block);
            else clearSelectedObjects();
          } else {
            clearSelectedObjects();
          }
        });
        return true;
      } finally {
        historyRestoringRef.current = false;
      }
    };

    const undoEditorTransaction = () => {
      const stack = editorUndoStackRef.current;
      const entry = stack[stack.length - 1];
      if (!entry) return false;
      stack.pop();
      const restored = restoreEditorTransactionEntry(entry, "undo");
      if (restored) {
        editorRedoStackRef.current.push(entry);
        latestEditorHistoryKindRef.current = entry.transactionType || "table";
        return true;
      }
      stack.push(entry);
      return false;
    };

    const redoEditorTransaction = () => {
      const stack = editorRedoStackRef.current;
      const entry = stack[stack.length - 1];
      if (!entry) return false;
      stack.pop();
      const restored = restoreEditorTransactionEntry(entry, "redo");
      if (restored) {
        editorUndoStackRef.current.push(entry);
        latestEditorHistoryKindRef.current = entry.transactionType || "table";
        return true;
      }
      stack.push(entry);
      return false;
    };

    const getEditorHistoryState = () => ({
      canUndo: editorUndoStackRef.current.length > 0,
      canRedo: editorRedoStackRef.current.length > 0,
      latestKind: latestEditorHistoryKindRef.current,
    });


    // DocWriterTable follows the same ownership boundary as DocWriterMath:
    //
    //   - child component/module owns the TABLE object and its interaction UI;
    //   - Canvas owns page placement, document history, and the complete document HTML;
    //   - a table transaction may provide HTML only as an object-change envelope;
    //   - Canvas extracts/replaces that one logical table, then serializes the live
    //     Canvas itself. Table HTML is never accepted as a competing document source.
    const docWriterTable = useMemo(() => createDocWriterTable({
      getRoot: () => editorRef.current,

      // DocWriterTable uses this only to take before/after transaction snapshots.
      // The host serializer is the Canvas serializer, so even those snapshots begin
      // from the same document source of truth used by normal text and Math.
      serializeDocument: () =>
        serializeEditorHtml(editorRef.current, {
          preserveEditingBoundaries: true,
        }),

      commitDocument: (nextHtml, details = {}) => {
        const editor = editorRef.current;
        if (!editor) return "";

        const operation = String(details.operation || "").toLowerCase();
        const tableId = String(details.tableId || "");
        const focusCellId = String(details.focusCellId || "");
        const viewportKey = tableId ? `table:${tableId}` : "";
        const liveTableBefore = tableId
          ? editor.querySelector(
              `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
            )
          : null;
        const viewportAnchor = viewportKey
          ? objectViewportAnchorRef.current.get(viewportKey) ||
            captureCanvasViewport(liveTableBefore)
          : captureCanvasViewport();

        const existingFragments = tableId
          ? getLogicalTableFragments(editor, tableId)
          : [];

        // Insert/import is already a live DOM operation inside DocWriterTable.
        // Keep that exact object. Do not rebuild the Canvas from transaction HTML.
        const insertedLiveTable =
          tableId &&
          ["insert-table", "import-table"].includes(operation)
            ? editor.querySelector(
                `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
              )
            : null;

        if (operation === "delete-table" && tableId) {
          existingFragments.forEach((fragment) => fragment.remove());
        } else if (!insertedLiveTable && tableId && existingFragments.length) {
          // Detached row/column/merge/edit transactions are allowed to describe
          // the changed logical table. Apply ONLY that object back to the Canvas.
          // The surrounding document never comes from DocWriterTable.
          const committedTable = extractLogicalTableFromHtml(
            editor.ownerDocument,
            nextHtml,
            tableId
          );

          if (committedTable) {
            const first = existingFragments[0];
            const replacement = committedTable.cloneNode(true);
            first.replaceWith(replacement);
            existingFragments.slice(1).forEach((fragment) => fragment.remove());

            normalizeTablesWithDocWriterTable(
              replacement.parentElement || editor,
              {
                source: "reopened",
                inferWidths: false,
              }
            );
            ensureCanvasTableVisibility(replacement.parentElement || editor);
          }
        }

        // One document owner. Always serialize what is actually in the live Canvas.
        const cleaned = serializeEditorHtml(editor, {
          preserveEditingBoundaries: true,
        });
        lastEmittedHtmlRef.current = cleaned;

        if (tableId) selectedTableIdRef.current = tableId;
        if (focusCellId) selectedCellIdRef.current = focusCellId;
        selectedVisualIdRef.current = "";

        markLocalMutation();
        if (typeof onChange === "function") {
          onChange(cleaned);
        }

        // Table may still provide its pre-transaction snapshot for Undo history,
        // but the committed/after document is always the Canvas-owned serialization.
        const beforeHtml = String(details.beforeHtml || "");
        recordEditorTransaction(
          {
            ...details,
            beforeHtml,
            afterHtml: cleaned,
          },
          cleaned
        );

        updateCounts();
        setPageCount(
          Math.max(
            1,
            editor.querySelectorAll(
              ':scope > [data-doc-canvas-page="true"]'
            ).length
          )
        );

        restoreCanvasViewport(viewportAnchor, {
          useAnchor: operation !== "delete-table",
        });

        window.requestAnimationFrame(() => {
          docWriterTable.refreshControls?.();
        });

        if (viewportKey) objectViewportAnchorRef.current.delete(viewportKey);

        if (operation === "delete-table") {
          selectedTableIdRef.current = "";
          selectedCellIdRef.current = "";
        }

        if (focusCellId) {
          window.requestAnimationFrame(() => {
            focusCellById(focusCellId, details.focusAtEnd === true);
            captureToolbarSelection();
          });
        }

        return cleaned;
      },

      requestRepagination: () => {
        // Child requests layout; Canvas performs page flow from its live DOM.
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            scheduleRepagination({
              immediate: true,
              preserveSelection: true,
              force: true,
            });
          });
        });
      },

      focusCell: focusCellById,
      getSelection: () => window.getSelection?.() || null,
    }), [onChange, effectiveReadOnly]);

    const tableResultChanged = (result) =>
      Boolean(result && result.changed === true);

    const insertTableThroughDocWriter = (options = {}) => {
      if (effectiveReadOnly) return false;

      const rows = Math.max(
        1,
        Number.parseInt(String(options.rows ?? 3), 10) || 1
      );
      const columns = Math.max(
        1,
        Number.parseInt(String(options.columns ?? 3), 10) || 1
      );

      // The Toolbar may present a compact 3 × 3 default picker, but the Editor
      // does not impose a document-size ceiling. The DocWriterTable owns the safety
      // limit and all later row/column growth.

      // The table dialog temporarily owns focus. Restore the last valid Canvas
      // selection exactly once immediately before calling the DocWriterTable. The
      // engine then owns construction, insertion, normalization, serialization,
      // commit, and first-cell focus. No editor-side table fallback is allowed.
      const insertionSnapshot =
        captureToolbarSelection() || toolbarSelectionRef.current;
      restoreToolbarSelection(insertionSnapshot);

      let result = null;
      try {
        result = docWriterTable.insertTable({
          ...options,
          rows,
          columns,
          source: "toolbar",
        });
      } catch (_) {
        result = null;
      }

      const changed = tableResultChanged(result);
      if (!changed) return false;

      // DocWriterTable v4 historically could report changed=true by falling back to
      // its pre-commit table object even when the committed Canvas no longer held
      // that table. Treat the live Canvas as the final authority before reporting
      // success to the Toolbar.
      const tableId = String(result?.tableId || "");
      const liveCommittedTable =
        tableId && editorRef.current
          ? editorRef.current.querySelector(
              `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(tableId)}"]`
            )
          : null;
      if (!tableId || !liveCommittedTable) return false;

      // Keep the toolbar-interaction guard active until DocCanvasToolbar closes
      // the dialog and explicitly calls endEditorInteraction({ restore: false }).
      // Releasing it here creates a blur/pagination race where the new table can
      // appear for one frame and then be rebuilt away.

      const focusCellId = String(
        result?.focusCellId || result?.firstCellId || ""
      );
      if (tableId) selectedTableIdRef.current = tableId;
      if (focusCellId) selectedCellIdRef.current = focusCellId;
      selectedVisualIdRef.current = "";

      // Post-insert layout is intentionally NOT scheduled here. The Toolbar is
      // still in the middle of its click/dialog lifecycle. endToolbarInteraction()
      // owns the single settle check after the Toolbar has actually closed.
      return true;
    };

    const importTableThroughDocWriter = (value, options = {}) => {
      if (effectiveReadOnly) return { changed: false, tables: [] };
      return docWriterTable.importTable(value, {
        ...options,
        source: options.source || "ai",
      });
    };

    const visualDescriptorFromBlock = (block) => {
      if (!block) return null;
      const family = normalizeVisualFamily(block.getAttribute(DOC_BLOCK_TYPE_ATTR));
      const source = String(
        block.getAttribute(DOC_BLOCK_SOURCE_ATTR) ||
          block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) ||
          ""
      ).trim();
      const canonicalSource = String(
        block.getAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR) || source
      ).trim();
      let width = Number.parseFloat(
        block.getAttribute(DOC_VISUAL_WIDTH_ATTR) || block.style.width || ""
      );
      let height = Number.parseFloat(
        block.getAttribute(DOC_VISUAL_HEIGHT_ATTR) || block.style.height || ""
      );
      let alignment =
        block.getAttribute(DOC_VISUAL_ALIGNMENT_ATTR) ||
        (String(block.style.marginLeft || "").toLowerCase() === "auto" &&
        String(block.style.marginRight || "").toLowerCase() === "auto"
          ? "center"
          : String(block.style.marginLeft || "").toLowerCase() === "auto"
          ? "right"
          : "center");

      if (family === "math") {
        const mathMeta = docWriterMathMetaFromValue(source);
        alignment = normalizeDocWriterMathAlignment(
          mathMeta?.alignment,
          alignment
        );
        const mathWidth = Number.parseFloat(String(mathMeta?.widthPx ?? ""));
        const mathHeight = Number.parseFloat(
          String(mathMeta?.heightPx ?? mathMeta?.minHeightPx ?? "")
        );
        width = Number.isFinite(mathWidth) && mathWidth > 0 ? mathWidth : NaN;
        height = Number.isFinite(mathHeight) && mathHeight > 0 ? mathHeight : NaN;
      }

      return {
        id: block.getAttribute(DOC_BLOCK_ID_ATTR) || "",
        family,
        source,
        canonicalSource,
        width: Number.isFinite(width) && width > 0 ? width : null,
        height: Number.isFinite(height) && height > 0 ? height : null,
        alignment,
      };
    };

    const captureDocWriterVisualSnapshot = (block, suppliedHtml = "") => {
      if (!block?.isConnected) return false;
      const { host, template } = ensureDocWriterVisualStructure(block);
      if (!host || !template) return false;

      let snapshotNode = null;
      if (suppliedHtml) {
        const holder = block.ownerDocument.createElement("div");
        holder.innerHTML = String(suppliedHtml || "");
        snapshotNode = holder;
      } else {
        const printable = host.querySelector?.(
          '.doc-writer-chart-print-surface, [data-docwriter-math-print-surface="true"], [data-doc-writer-image-print-surface="true"]'
        );
        snapshotNode = printable ? printable.cloneNode(true) : host.cloneNode(true);
      }

      stripDocWriterEditorOnlyMarkup(snapshotNode);
      stripReactOnlyTemporaryMarkup(snapshotNode);
      // Defensive Math snapshot cleanup. DocWriterMath emits a clean
      // printable surface, but older/reopened snapshots can still contain KaTeX's
      // parallel accessibility MathML representation. Keep only the visible HTML
      // layer so PDF/preview never prints each equation twice.
      snapshotNode.querySelectorAll?.(".katex-mathml").forEach((node) => node.remove());

      let html = "";
      if (suppliedHtml) {
        html = String(snapshotNode.innerHTML || "").trim();
      } else if (snapshotNode.nodeType === 1 && snapshotNode !== host) {
        html = String(snapshotNode.outerHTML || snapshotNode.innerHTML || "").trim();
      } else {
        html = String(snapshotNode.innerHTML || "").trim();
      }

      if (!html) return false;
      if (template.innerHTML !== html) template.innerHTML = html;
      block.setAttribute(DOC_VISUAL_STATIC_READY_ATTR, "true");
      return true;
    };

    const setDocWriterObjectEditing = (open, detail = {}) => {
      const editor = editorRef.current;
      const blockId = String(detail.blockId || detail.chartId || "");
      const tableId = String(detail.tableId || "");
      const key = tableId ? `table:${tableId}` : blockId ? `visual:${blockId}` : "";
      if (!key) return;

      const operation = String(detail.operation || "").trim().toLowerCase();
      const visualBlock =
        blockId && editor
          ? editor.querySelector(
              `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(blockId)}"]`
            )
          : null;
      const visualFamily = visualBlock
        ? normalizeVisualFamily(visualBlock.getAttribute(DOC_BLOCK_TYPE_ATTR))
        : "";
      const isMathMove = visualFamily === "math" && operation === "move";

      if (open) {
        objectEditingKeysRef.current.add(key);
        const targetNode = tableId
          ? getLogicalTableFragments(editor, tableId)[0] || null
          : visualBlock;

        // Edit/resize operations preserve the viewport. A Math move is different:
        // the user is deliberately changing document position, so restoring the old
        // anchor after pointer-up would visibly fight the drag and jump backward.
        if (isMathMove) {
          objectViewportAnchorRef.current.delete(key);
        } else {
          objectViewportAnchorRef.current.set(key, captureCanvasViewport(targetNode));
        }

        if (paginationTimerRef.current) {
          window.clearTimeout(paginationTimerRef.current);
          paginationTimerRef.current = null;
        }
        if (paginationFrameRef.current) {
          window.cancelAnimationFrame(paginationFrameRef.current);
          paginationFrameRef.current = null;
        }
        if (visualMountFrameRef.current) {
          window.cancelAnimationFrame(visualMountFrameRef.current);
          visualMountFrameRef.current = null;
        }
      } else {
        objectEditingKeysRef.current.delete(key);
      }

      if (editor) {
        if (tableId) {
          getLogicalTableFragments(editor, tableId).forEach((table) => {
            if (open) table.setAttribute(DOC_WRITER_OBJECT_EDITING_ATTR, "true");
            else table.removeAttribute(DOC_WRITER_OBJECT_EDITING_ATTR);
          });
        } else if (visualBlock) {
          if (open) visualBlock.setAttribute(DOC_WRITER_OBJECT_EDITING_ATTR, "true");
          else visualBlock.removeAttribute(DOC_WRITER_OBJECT_EDITING_ATTR);
        }
      }

      if (!open) {
        if (isMathMove) {
          // The current viewport and the newly dropped Math position are both user
          // choices. Do not restore an anchor captured before the drag.
          objectViewportAnchorRef.current.delete(key);
          return;
        }

        // Closing a child dialog is not itself a layout transaction. Chart/Table/Math/Image
        // commit exactly once through their own onChange/commit path. Repaginating here
        // races that commit and is the primary cause of the old jump-to-top/flicker.
        const savedAnchor = objectViewportAnchorRef.current.get(key);
        if (savedAnchor) restoreCanvasViewport(savedAnchor, { useAnchor: true });
        window.setTimeout(() => {
          if (!objectEditingKeysRef.current.has(key)) {
            objectViewportAnchorRef.current.delete(key);
          }
        }, 600);
      }
    };

    const unmountDocWriterVisual = (blockId) => {
      const entry = visualRootsRef.current.get(blockId);
      if (!entry) return;
      try {
        entry.root?.unmount?.();
      } catch (_) {}
      visualRootsRef.current.delete(blockId);
    };

    const persistDocWriterImageFile = async (file, context = {}) => {
      if (!file) return String(context.dataUrl || "");
      if (typeof onImageUpload === "function") {
        try {
          const uploaded = await onImageUpload(file, context);
          if (typeof uploaded === "string" && uploaded.trim()) return uploaded.trim();
          if (uploaded && typeof uploaded === "object") {
            const candidate = String(uploaded.src || uploaded.url || uploaded.dataUrl || "").trim();
            if (candidate) return candidate;
          }
        } catch (_) {}
      }
      if (context.dataUrl) return String(context.dataUrl);
      try { return await readImageFileAsDataUrl(file); } catch (_) { return ""; }
    };

    const sourceFromMathValue = (nextValue) => {
      if (nextValue && typeof nextValue === "object") {
        try {
          return JSON.stringify(nextValue);
        } catch (_) {
          return String(nextValue.raw || nextValue.value || "");
        }
      }
      return String(nextValue || "");
    };

    const canonicalMathSource = (nextValue) => {
      if (nextValue && typeof nextValue === "object") {
        const raw = String(nextValue.raw ?? nextValue.value ?? nextValue.latex ?? "").trim();
        if (/^\s*\[(?:math|formula|equation|latex|calculation)\]/i.test(raw)) {
          return raw;
        }
        return raw ? `[math]\n${raw}` : "[math]";
      }
      const raw = String(nextValue || "").trim();
      if (/^\s*\[(?:math|formula|equation|latex|calculation)\]/i.test(raw)) {
        return raw;
      }
      return raw ? `[math]\n${raw}` : "[math]";
    };

    const commitDocWriterVisualChange = (blockId, family, payload = {}) => {
      const editor = editorRef.current;
      const block = editor?.querySelector(
        `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(blockId)}"]`
      );
      if (!editor || !block) return false;

      const beforeHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      const transactionId = createDocumentObjectId("editortx");
      const normalizedFamily = normalizeVisualFamily(family);

      const viewportKey = `visual:${blockId}`;
      const viewportAnchor = objectViewportAnchorRef.current.get(viewportKey) || captureCanvasViewport(block);

      if (normalizedFamily === "math") {
        const nextValue = payload?.value ?? payload;
        const source = sourceFromMathValue(nextValue);
        block.setAttribute(DOC_BLOCK_SOURCE_ATTR, source);
        block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, canonicalMathSource(nextValue));

        // Save the component value exactly and keep the Editor wrapper neutral.
        // Left/center/right, width, height, font, offsets, and appearance live in
        // DocWriterMath's serialized meta and must never be re-derived here.
        applyDocWriterMathOuterContract(
          block,
          nextValue,
          nextValue?.meta?.alignment || payload?.meta?.alignment || "center"
        );
      } else if (normalizedFamily === "image") {
        const nextValue = normalizeDocWriterImageValue(payload?.value ?? payload, {
          width: payload?.width,
          height: payload?.height,
          alignment: payload?.alignment,
        });
        const source = JSON.stringify(nextValue);
        block.setAttribute(DOC_BLOCK_SOURCE_ATTR, source);
        block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, source);
        const width = Number(payload?.width ?? nextValue.meta?.width);
        const height = Number(payload?.height ?? nextValue.meta?.height);
        if (Number.isFinite(width) && width > 0) {
          block.setAttribute(DOC_VISUAL_WIDTH_ATTR, String(Math.round(width)));
          block.style.width = `${Math.round(width)}px`;
          block.style.maxWidth = "100%";
        }
        if (Number.isFinite(height) && height > 0) {
          block.setAttribute(DOC_VISUAL_HEIGHT_ATTR, String(Math.round(height)));
        }
        applyDocWriterBlockAlignment(block, payload?.alignment || nextValue.meta?.alignment || "center");
      } else {
        const source = String(payload?.source || payload?.value || "").trim();
        if (source) {
          block.setAttribute(DOC_BLOCK_SOURCE_ATTR, source);
          block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, source);
        }
        const width = Number(payload?.width);
        const height = Number(payload?.height);
        if (Number.isFinite(width) && width > 0) {
          block.setAttribute(DOC_VISUAL_WIDTH_ATTR, String(Math.round(width)));
          block.style.width = `${Math.round(width)}px`;
          block.style.maxWidth = "100%";
        }
        if (Number.isFinite(height) && height > 0) {
          block.setAttribute(DOC_VISUAL_HEIGHT_ATTR, String(Math.round(height)));
        }
        applyDocWriterBlockAlignment(block, payload?.alignment || "center");
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          captureDocWriterVisualSnapshot(block);
          const afterHtml = serializeEditorHtml(editor, {
            preserveEditingBoundaries: true,
          });
          lastEmittedHtmlRef.current = afterHtml;
          markLocalMutation();
          onChange?.(afterHtml);
          recordEditorTransaction(
            {
              transactionType: normalizedFamily,
              transactionId,
              operation: `${normalizedFamily}-edit`,
              blockId,
              beforeHtml,
              afterHtml,
            },
            afterHtml
          );
          restoreCanvasViewport(viewportAnchor, { useAnchor: true });
          objectViewportAnchorRef.current.delete(viewportKey);
          scheduleVisualCompletionRepagination();
        });
      });
      return true;
    };

    const deleteDocWriterVisual = (blockId, family = "visual") => {
      const editor = editorRef.current;
      const block = editor?.querySelector(
        `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(blockId)}"]`
      );
      if (!editor || !block) return false;
      const beforeHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      const transactionId = createDocumentObjectId("editortx");
      const normalizedFamily = normalizeVisualFamily(family);
      const viewportAnchor = captureCanvasViewport(block);

      unmountDocWriterVisual(blockId);
      block.remove();
      selectedVisualIdRef.current = "";
      const afterHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      lastEmittedHtmlRef.current = afterHtml;
      markLocalMutation();
      onChange?.(afterHtml);
      recordEditorTransaction(
        {
          transactionType: normalizedFamily,
          transactionId,
          operation: `${normalizedFamily}-delete`,
          blockId,
          beforeHtml,
          afterHtml,
        },
        afterHtml
      );
      updateCounts();
      restoreCanvasViewport(viewportAnchor, { useAnchor: false });
      scheduleRepagination({ immediate: true, preserveSelection: true, force: true });
      return true;
    };

    const mountDocWriterVisualBlock = (block, { force = false } = {}) => {
      if (!block?.isConnected) return null;
      normalizeProtectedBlocks(block.parentElement || editorRef.current);
      const descriptor = visualDescriptorFromBlock(block);
      if (!descriptor?.id || !descriptor.source) {
        const { host, template } = ensureDocWriterVisualStructure(block);
        if (host && template?.innerHTML && !host.childNodes.length) {
          host.innerHTML = template.innerHTML;
        }
        return block;
      }

      const { host } = ensureDocWriterVisualStructure(block);
      if (!host) return block;
      pruneProtectedBlockFallbackMarkup(block);
      const signature = JSON.stringify({
        family: descriptor.family,
        source: descriptor.source,
        width: descriptor.width,
        height: descriptor.height,
        alignment: descriptor.alignment,
        readOnly: effectiveReadOnly,
      });
      const previous = visualRootsRef.current.get(descriptor.id);
      if (
        previous &&
        previous.host === host &&
        block.getAttribute(DOC_WRITER_OBJECT_EDITING_ATTR) === "true"
      ) {
        return block;
      }
      if (previous && previous.host === host && previous.signature === signature && !force) {
        return block;
      }
      if (previous) unmountDocWriterVisual(descriptor.id);

      let root = null;
      // A serialized static snapshot is only a fallback for save/PDF/reopen.
      // Before mounting the live child component, clear that fallback so stale
      // Drag/Edit/Resize chrome or old Math markup can never sit underneath the
      // real React component.
      try { host.replaceChildren(); } catch (_) {}
      try {
        root = createRoot(host);
      } catch (_) {
        try { host.replaceChildren(); } catch (_) {}
        root = createRoot(host);
      }
      visualRootsRef.current.set(descriptor.id, {
        root,
        host,
        block,
        signature,
      });
      block.setAttribute(DOC_WRITER_VISUAL_MOUNTED_ATTR, "true");

      if (descriptor.family === "math") {
        // The host is only a Canvas placement/mount boundary. DocWriterMath renders
        // everything visible inside it; Editor CSS intentionally stops at the shell.
        host.setAttribute("data-docwriter-math-host", "true");
        host.style.lineHeight = "normal";
        const parsedMathValue = parseDocWriterMathValue(descriptor.source);
        const mathValueForMount = parsedMathValue
          ? descriptor.source
          : {
              type: "docwriter_math",
              raw: descriptor.source,
              meta: {
                alignment: normalizeDocWriterMathAlignment(
                  descriptor.alignment,
                  "center"
                ),
              },
            };
        root.render(
          <DocWriterMath
            value={mathValueForMount}
            readOnly={effectiveReadOnly}
            blockId={descriptor.id}
            onChange={(nextValue, detail = {}) =>
              commitDocWriterVisualChange(descriptor.id, "math", {
                value: nextValue,
                ...detail,
              })
            }
            onDelete={() => deleteDocWriterVisual(descriptor.id, "math")}
            onRequestDrag={(event, mathContext = {}) =>
              beginUnifiedObjectDrag(event, {
                kind: "visual",
                blockId: descriptor.id,
                family: "math",
                operation: mathContext.operation || "move",
                componentValue: mathContext.value,
                componentMeta: mathContext.meta,
              })
            }
            onSnapshotReady={(snapshot = {}) => {
              window.requestAnimationFrame(() =>
                captureDocWriterVisualSnapshot(block, snapshot.html || "")
              );
            }}
            onEditingChange={(open, detail = {}) =>
              setDocWriterObjectEditing(open, {
                ...detail,
                blockId: descriptor.id,
              })
            }
          />
        );
      } else if (descriptor.family === "image") {
        host.removeAttribute("data-docwriter-math-host");
        host.style.removeProperty("line-height");
        let imageValue = descriptor.source;
        try {
          imageValue = normalizeDocWriterImageValue(descriptor.source, {
            width: descriptor.width || undefined,
            height: descriptor.height || undefined,
            alignment: descriptor.alignment || "center",
          });
          const maxContentHeight = documentPageContentHeight(block);
          const maxContentWidth = documentPageContentWidth(block);
          const currentWidth = Number(imageValue.meta?.width || descriptor.width || 520);
          const currentHeight = Number(imageValue.meta?.height || descriptor.height || 320);
          const scale = Math.min(
            1,
            maxContentWidth / Math.max(1, currentWidth),
            maxContentHeight / Math.max(1, currentHeight)
          );
          if (scale < 1) {
            imageValue = {
              ...imageValue,
              meta: {
                ...imageValue.meta,
                width: Math.max(120, Math.round(currentWidth * scale)),
                height: Math.max(80, Math.round(currentHeight * scale)),
              },
            };
          }
        } catch (_) {}
        root.render(
          <DocWriterImage
            value={imageValue}
            imageId={descriptor.id}
            blockId={descriptor.id}
            editable={!effectiveReadOnly}
            readOnly={effectiveReadOnly}
            selected={false}
            maxWidth={documentPageContentWidth(block)}
            width={Number(imageValue?.meta?.width) || descriptor.width || undefined}
            height={Number(imageValue?.meta?.height) || descriptor.height || undefined}
            alignment={imageValue?.meta?.alignment || descriptor.alignment || "center"}
            onChange={(nextValue, detail = {}) =>
              commitDocWriterVisualChange(descriptor.id, "image", {
                value: nextValue,
                ...detail,
              })
            }
            onDelete={() => deleteDocWriterVisual(descriptor.id, "image")}
            onRequestDrag={(event) =>
              beginUnifiedObjectDrag(event, {
                kind: "visual",
                blockId: descriptor.id,
                family: "image",
              })
            }
            onEditingChange={(open, detail = {}) =>
              setDocWriterObjectEditing(open, {
                ...detail,
                blockId: descriptor.id,
              })
            }
            onImageSelected={(file, context = {}) =>
              persistDocWriterImageFile(file, {
                ...context,
                blockId: descriptor.id,
                imageId: descriptor.id,
              })
            }
            onSnapshot={(snapshot = {}) => {
              window.requestAnimationFrame(() =>
                captureDocWriterVisualSnapshot(block, snapshot.html || "")
              );
            }}
          />
        );
      } else {
        host.removeAttribute("data-docwriter-math-host");
        host.style.removeProperty("line-height");
        root.render(
          <DocWriterChart
            value={descriptor.source}
            chartId={descriptor.id}
            editable={!effectiveReadOnly}
            selected={false}
            maxWidth={documentPageContentWidth(block)}
            width={descriptor.width || undefined}
            height={descriptor.height || undefined}
            alignment={descriptor.alignment || "center"}
            onChange={(detail) =>
              commitDocWriterVisualChange(descriptor.id, "chart", detail)
            }
            onDelete={() => deleteDocWriterVisual(descriptor.id, "chart")}
            onRequestDrag={(event) =>
              beginUnifiedObjectDrag(event, {
                kind: "visual",
                blockId: descriptor.id,
                family: "chart",
              })
            }
            onEditingChange={(open, detail = {}) =>
              setDocWriterObjectEditing(open, {
                ...detail,
                blockId: descriptor.id,
              })
            }
            onSnapshot={(snapshot = {}) => {
              window.requestAnimationFrame(() =>
                captureDocWriterVisualSnapshot(block, snapshot.html || "")
              );
            }}
          />
        );
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          captureDocWriterVisualSnapshot(block);
          // The placeholder used during initial pagination may be shorter than the
          // final Chart/Math/Image render. Measure again only after the child has
          // painted, then move the whole atomic object if it crosses the printable
          // bottom edge. No full Canvas rebuild is involved.
          scheduleVisualCompletionRepagination();
        });
      });
      return block;
    };

    const cleanupDisconnectedVisualRoots = () => {
      visualRootsRef.current.forEach((entry, blockId) => {
        if (!entry?.block?.isConnected || !editorRef.current?.contains(entry.block)) {
          unmountDocWriterVisual(blockId);
        }
      });
    };

    const scanDocWriterVisuals = ({ force = false } = {}) => {
      const editor = editorRef.current;
      if (!editor) return [];
      const inserted = importDocWriterStructuredMarkers(editor);
      normalizeProtectedBlocks(editor);
      cleanupDisconnectedVisualRoots();
      const blocks = Array.from(
        editor.querySelectorAll(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)
      );
      blocks.forEach((block) => mountDocWriterVisualBlock(block, { force }));
      if (inserted.length) scheduleVisualCompletionRepagination();
      return blocks;
    };

    const scheduleDocWriterVisualScan = ({ force = false } = {}) => {
      if (visualMountFrameRef.current) {
        window.cancelAnimationFrame(visualMountFrameRef.current);
      }
      visualMountFrameRef.current = window.requestAnimationFrame(() => {
        visualMountFrameRef.current = null;
        scanDocWriterVisuals({ force });
      });
    };

    const insertDocWriterVisual = (descriptor = {}) => {
      if (effectiveReadOnly) return null;
      const editor = editorRef.current;
      if (!editor) return null;
      const block = createDocWriterVisualBlock(editor.ownerDocument, descriptor);
      if (!block) return null;

      const beforeHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      const transactionId = createDocumentObjectId("editortx");
      try {
        restoreToolbarSelection();
        const selection = window.getSelection?.();
        const range =
          selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)
            ? selection.getRangeAt(0)
            : null;
        const page = range ? canvasPageFromNode(range.startContainer) : null;
        if (range && page) {
          range.deleteContents();
          range.insertNode(block);
          const paragraph = editor.ownerDocument.createElement("p");
          paragraph.appendChild(editor.ownerDocument.createElement("br"));
          block.after(paragraph);
          placeCaretInElement(paragraph, true);
        } else {
          const pages = Array.from(
            editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
          );
          const targetPage = pages[pages.length - 1] || pages[0];
          if (!targetPage) return null;
          targetPage.appendChild(block);
        }
      } catch (_) {
        return null;
      }

      normalizeProtectedBlocks(editor);
      mountDocWriterVisualBlock(block, { force: true });
      const afterHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      lastEmittedHtmlRef.current = afterHtml;
      markLocalMutation();
      onChange?.(afterHtml);
      const family = normalizeVisualFamily(block.getAttribute(DOC_BLOCK_TYPE_ATTR));
      recordEditorTransaction(
        {
          transactionType: family,
          transactionId,
          operation: `${family}-insert`,
          blockId: block.getAttribute(DOC_BLOCK_ID_ATTR) || "",
          beforeHtml,
          afterHtml,
        },
        afterHtml
      );
      scheduleVisualCompletionRepagination();
      return block;
    };

    const insertCanvasImage = async (options = {}) => {
      if (effectiveReadOnly) return { changed: false, inserted: false };
      const editor = editorRef.current;
      const file = options.file || null;
      if (!editor || !file) return { changed: false, inserted: false };

      const accepted = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
      const mimeType = String(file.type || "").toLowerCase();
      const fileName = String(file.name || "").toLowerCase();
      if (
        !accepted.has(mimeType) &&
        !/\.(?:jpe?g|png|webp|gif)$/.test(fileName)
      ) {
        return { changed: false, inserted: false, reason: "unsupported-image-type" };
      }
      if (Number(file.size || 0) > 30 * 1024 * 1024) {
        return { changed: false, inserted: false, reason: "image-too-large" };
      }

      const viewportAnchor = captureCanvasViewport();
      const beforeHtml = serializeEditorHtml(editor, { preserveEditingBoundaries: true });
      const transactionId = createDocumentObjectId("editortx");
      let dataUrl = "";
      try { dataUrl = await readImageFileAsDataUrl(file); } catch (_) {}
      if (!dataUrl) return { changed: false, inserted: false, reason: "image-read-failed" };

      const dimensions = await readImageDimensions(dataUrl);
      const maxWidth = 672;
      const naturalWidth = Math.max(1, Number(dimensions.width || 0));
      const naturalHeight = Math.max(1, Number(dimensions.height || 0));
      const initialWidth = Math.max(120, Math.min(maxWidth, naturalWidth || 520, 520));
      const ratio = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : 520 / 320;
      const initialHeight = Math.max(80, Math.min(900, Math.round(initialWidth / Math.max(0.01, ratio))));

      let finalSrc = await persistDocWriterImageFile(file, {
        dataUrl,
        source: options.source || "toolbar",
        fileName: String(file.name || ""),
        mimeType: String(file.type || ""),
      });
      if (!finalSrc) finalSrc = dataUrl;

      const imageValue = serializeDocWriterImageValue(finalSrc, {
        width: initialWidth,
        height: initialHeight,
        alignment: "center",
        lockAspectRatio: true,
        naturalWidth: Number(dimensions.width || 0),
        naturalHeight: Number(dimensions.height || 0),
        fileName: String(file.name || ""),
        mimeType: String(file.type || ""),
        fit: "contain",
        showCaption: false,
      });
      const block = createDocWriterImageBlock(editor.ownerDocument, imageValue, {
        width: initialWidth,
        height: initialHeight,
        alignment: "center",
      });
      if (!block) return { changed: false, inserted: false, reason: "image-block-failed" };

      try {
        restoreToolbarSelection();
        const selection = window.getSelection?.();
        const range = selection && selection.rangeCount > 0 && editor.contains(selection.anchorNode)
          ? selection.getRangeAt(0)
          : null;
        const page = range ? canvasPageFromNode(range.startContainer) : null;
        if (range && page) {
          if (!range.collapsed) range.deleteContents();
          range.insertNode(block);
          const paragraph = editor.ownerDocument.createElement("p");
          paragraph.appendChild(editor.ownerDocument.createElement("br"));
          block.after(paragraph);
          placeCaretInElement(paragraph, true);
        } else {
          const pages = Array.from(
            editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
          );
          const targetPage = pages[pages.length - 1] || pages[0];
          if (!targetPage) return { changed: false, inserted: false, reason: "missing-page" };
          targetPage.appendChild(block);
        }
      } catch (_) {
        return { changed: false, inserted: false, reason: "image-insert-failed" };
      }

      normalizeProtectedBlocks(editor);
      mountDocWriterVisualBlock(block, { force: true });
      selectedVisualIdRef.current = block.getAttribute(DOC_BLOCK_ID_ATTR) || "";
      selectedTableIdRef.current = "";
      selectedCellIdRef.current = "";

      const afterHtml = serializeEditorHtml(editor, { preserveEditingBoundaries: true });
      lastEmittedHtmlRef.current = afterHtml;
      markLocalMutation();
      onChange?.(afterHtml);
      recordEditorTransaction({
        transactionType: "image",
        transactionId,
        operation: "image-insert",
        blockId: block.getAttribute(DOC_BLOCK_ID_ATTR) || "",
        beforeHtml,
        afterHtml,
      }, afterHtml);
      pendingToolbarObjectLayoutRef.current = true;
      updateCounts();
      restoreCanvasViewport(viewportAnchor, { useAnchor: false });

      return {
        changed: true,
        inserted: true,
        image: block,
        imageId: block.getAttribute(DOC_BLOCK_ID_ATTR) || "",
        blockId: block.getAttribute(DOC_BLOCK_ID_ATTR) || "",
      };
    };

    const importDocWriterStructuredContent = (value, options = {}) => {
      if (effectiveReadOnly) return [];
      if (Array.isArray(value)) {
        return value
          .map((item) => insertDocWriterVisual({ ...options, ...(item || {}) }))
          .filter(Boolean);
      }
      if (value && typeof value === "object") {
        const block = insertDocWriterVisual({ ...value, ...options });
        return block ? [block] : [];
      }

      const raw = String(value || "");
      if (!raw.trim()) return [];
      const descriptors = [];
      const regex = /```\s*(chart|graph|visual|diagram|geometry|flowchart|math|formula|equation|latex|calculation)\s*\n([\s\S]*?)\n```|^\s*\[(chart|graph|visual|diagram|geometry|flowchart|math|formula|equation|latex|calculation)\]\s*([\s\S]*?)(?=^\s*\[(?:chart|graph|visual|diagram|geometry|flowchart|math|formula|equation|latex|calculation)\]\s*|\s*$)/gim;
      let match = regex.exec(raw);
      while (match) {
        const type = match[1] || match[3] || options.type || "";
        const source = match[1]
          ? `[${match[1]}]\n${match[2] || ""}`
          : match[0].trim();
        const structured = explicitStructuredVisual(source, type);
        if (structured) descriptors.push({ ...structured, ...options });
        match = regex.exec(raw);
      }
      if (!descriptors.length) {
        const structured = explicitStructuredVisual(raw, options.type || "");
        if (structured) descriptors.push({ ...structured, ...options });
      }
      return descriptors.map(insertDocWriterVisual).filter(Boolean);
    };

    const updateDocWriterVisual = (detail = {}) => {
      const editor = editorRef.current;
      const blockId = String(detail.blockId || detail.chartId || "");
      const block = blockId
        ? editor?.querySelector(
            `[${DOC_BLOCK_ID_ATTR}="${escapeCssValue(blockId)}"]`
          )
        : null;
      if (!block) return null;
      const family = normalizeVisualFamily(
        detail.type || block.getAttribute(DOC_BLOCK_TYPE_ATTR) || "visual"
      );
      const source = detail.source ?? detail.value;
      if (family === "math") {
        let nextMathValue =
          source != null
            ? source
            : block.getAttribute(DOC_BLOCK_SOURCE_ATTR) || "";
        const parsedMathValue = parseDocWriterMathValue(nextMathValue);

        // Modern DocWriterMath values carry all user geometry in meta. If an
        // imperative Editor call supplies Math alignment/width/height, patch that
        // same value instead of creating a competing outer-wrapper layout.
        if (parsedMathValue) {
          const nextMeta = {
            ...(parsedMathValue.meta && typeof parsedMathValue.meta === "object"
              ? parsedMathValue.meta
              : {}),
          };
          if (detail.alignment) {
            nextMeta.alignment = normalizeDocWriterMathAlignment(
              detail.alignment,
              nextMeta.alignment || "center"
            );
          }
          if (detail.width != null && Number.isFinite(Number(detail.width))) {
            nextMeta.widthMode = "custom";
            nextMeta.widthPx = Number(detail.width);
          }
          if (detail.height != null && Number.isFinite(Number(detail.height))) {
            nextMeta.heightPx = Number(detail.height);
            nextMeta.minHeightPx = 0;
          }
          nextMathValue = {
            ...parsedMathValue,
            meta: nextMeta,
          };
        }

        block.setAttribute(DOC_BLOCK_SOURCE_ATTR, sourceFromMathValue(nextMathValue));
        block.setAttribute(
          DOC_BLOCK_CANONICAL_SOURCE_ATTR,
          canonicalMathSource(nextMathValue)
        );
        applyDocWriterMathOuterContract(
          block,
          nextMathValue,
          detail.alignment || block.getAttribute(DOC_VISUAL_ALIGNMENT_ATTR) || "center"
        );
      } else {
        if (source != null) {
          if (family === "image") {
            const normalizedImage = normalizeDocWriterImageValue(source, detail);
            const serializedImage = JSON.stringify(normalizedImage);
            block.setAttribute(DOC_BLOCK_SOURCE_ATTR, serializedImage);
            block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, serializedImage);
          } else {
            block.setAttribute(DOC_BLOCK_SOURCE_ATTR, String(source));
            block.setAttribute(DOC_BLOCK_CANONICAL_SOURCE_ATTR, String(source));
          }
        }
        if (detail.width != null) {
          block.setAttribute(DOC_VISUAL_WIDTH_ATTR, String(detail.width));
          block.style.width = `${Number(detail.width) || 620}px`;
        }
        if (detail.height != null) {
          block.setAttribute(DOC_VISUAL_HEIGHT_ATTR, String(detail.height));
        }
        if (detail.alignment) applyDocWriterBlockAlignment(block, detail.alignment);
      }
      mountDocWriterVisualBlock(block, { force: true });
      scheduleVisualCompletionRepagination();
      return block;
    };

    const commitCleanBoundaries = () => {
      const editor = editorRef.current;
      if (!editor) return "";
      return normalizeDocumentHtml(serializeEditorHtml(editor), {
        preserveEditingBoundaries: true,
      });
    };

    // DocWriterTable owns only its table presentation/edit UI. Canvas owns the
    // containing document, page flow, history, cross-page movement, and serialization.
    useEffect(() => {
      const detach = docWriterTable.attach?.({
        enableObjectControls: !effectiveReadOnly,
        onRequestTableMove: (event, context = {}) => {
          beginUnifiedObjectDrag(event, {
            kind: "table",
            tableId: context.tableId || "",
          });
        },
        onEditingChange: (open, detail = {}) =>
          setDocWriterObjectEditing(open, detail),
        // Whole-table/row/column resize is owned by DocWriterTable itself. The
        // Editor deliberately does not supply an onRequestTableResize override.
        // This keeps object UI/geometry inside the child component and leaves the
        // Editor responsible only for document placement and pagination.
      });
      return () => {
        try { detach?.(); } catch (_) {}
      };
    }, [docWriterTable, effectiveReadOnly]);

    // Reflect external HTML updates as paginated Letter-size pages without
    // overwriting the caret during normal user typing.
    useEffect(() => {
      const editor = editorRef.current;
      if (!editor) return;
      if (objectEditingKeysRef.current.size > 0) {
        updateCounts();
        return;
      }

      const renderedHtml = serializeEditorHtml(editor);
      const hasPages = Boolean(
        editor.querySelector(':scope > [data-doc-canvas-page="true"]')
      );

      const normalizedLastEmitted = normalizeDocumentHtml(
        lastEmittedHtmlRef.current,
        { preserveEditingBoundaries: true }
      );
      const isParentEcho = normalizedLastEmitted === normalizedHtml;
      const pendingTableId = pendingManualTableIdRef.current;
      const pendingLiveTable =
        pendingTableId
          ? editor.querySelector(
              `table[${DOC_TABLE_ID_ATTR}="${escapeCssValue(pendingTableId)}"]`
            )
          : null;

      // A parent render can arrive while the table dialog is closing. If the new
      // manual table is already live, do not replace the contentEditable DOM from
      // props until the Toolbar has ended the insertion transaction.
      if (pendingLiveTable) {
        updateCounts();
        setPageCount(
          Math.max(
            1,
            editor.querySelectorAll(
              ':scope > [data-doc-canvas-page="true"]'
            ).length
          )
        );
        return;
      }

      const liveInteraction = Boolean(
        editorFocusedRef.current ||
        toolbarInteractionRef.current ||
        objectEditingKeysRef.current.size > 0 ||
        objectResizeRef.current ||
        objectDragRef.current
      );
      const recentLocalMutation =
        Date.now() - Number(lastLocalMutationAtRef.current || 0) < 1500;

      // Focus by itself is NOT evidence of live document content. The editor can
      // already own/focus its initial empty Letter page while the parent is still
      // loading the real D1/R* HTML. In that state, deferring the incoming HTML
      // creates a permanent blank Canvas because there is nothing meaningful to
      // protect.
      const liveCanvasHasMeaningfulContent = Boolean(
        String(editor.innerText || editor.textContent || "")
          .replace(/\u00a0/g, " ")
          .trim() ||
          editor.querySelector(
            [
              "table",
              `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`,
              "img",
              "svg",
              "canvas",
              "video",
              "audio",
              "iframe",
              "object",
              "embed",
            ].join(",")
          )
      );

      const protectLiveCanvas =
        recentLocalMutation ||
        (liveInteraction && liveCanvasHasMeaningfulContent);

      if (!hasPages) {
        deferredExternalHtmlRef.current = "";
        renderPaginatedHtml(normalizedHtml, {
          preserveSelection: false,
          normalizeIngressLayout: true,
        });
      } else if (renderedHtml !== normalizedHtml && !isParentEcho) {
        // Protect real live edits, but never let an empty focused Canvas block
        // the first non-empty backend/revision document from hydrating.
        if (protectLiveCanvas) {
          deferredExternalHtmlRef.current = normalizedHtml;
          updateCounts();
          setPageCount(
            Math.max(
              1,
              editor.querySelectorAll(
                ':scope > [data-doc-canvas-page="true"]'
              ).length
            )
          );
          return;
        }
        deferredExternalHtmlRef.current = "";
        renderPaginatedHtml(normalizedHtml, {
          preserveSelection: false,
          keepViewport: true,
          normalizeIngressLayout: true,
        });
      } else {
        updateCounts();
        setPageCount(
          Math.max(
            1,
            editor.querySelectorAll(
              ':scope > [data-doc-canvas-page="true"]'
            ).length
          )
        );
      }
    }, [normalizedHtml]);

    // Keep the live document at true 1:1 Letter geometry and make the Canvas
    // viewport exactly one complete Letter page plus the 24px top/bottom gutter.
    // Do not shorten this viewport from window.innerHeight: the user should always
    // see page 1 from its top gutter through its bottom gutter, then use the
    // Canvas-owned right scrollbar to reach pages 2, 3, 4, and beyond.

    // Open the workspace with the Letter page centered inside the visible gutter.
    // The user can still move the horizontal scrollbar freely afterward.
    useEffect(() => {
      let firstFrame = 0;
      let secondFrame = 0;

      firstFrame = window.requestAnimationFrame(() => {
        secondFrame = window.requestAnimationFrame(() => {
          const workspace = workspaceRef.current;
          if (!workspace) return;

          const maximumScrollLeft = Math.max(
            0,
            workspace.scrollWidth - workspace.clientWidth
          );

          workspace.scrollLeft = maximumScrollLeft / 2;
          workspace.scrollTop = 0;
        });
      });

      return () => {
        if (firstFrame) window.cancelAnimationFrame(firstFrame);
        if (secondFrame) window.cancelAnimationFrame(secondFrame);
      };
    }, []);

    useEffect(() => {
      return () => {
        if (paginationTimerRef.current) {
          window.clearTimeout(paginationTimerRef.current);
        }

        if (paginationFrameRef.current) {
          window.cancelAnimationFrame(paginationFrameRef.current);
        }

        if (objectResizeFrameRef.current) {
          window.cancelAnimationFrame(objectResizeFrameRef.current);
        }

        if (visualRepaginationFrameRef.current) {
          window.cancelAnimationFrame(visualRepaginationFrameRef.current);
          visualRepaginationFrameRef.current = null;
        }

        if (visualRepaginationTimerRef.current) {
          window.clearTimeout(visualRepaginationTimerRef.current);
          visualRepaginationTimerRef.current = null;
        }

        if (visualMountFrameRef.current) {
          window.cancelAnimationFrame(visualMountFrameRef.current);
          visualMountFrameRef.current = null;
        }

        pendingManualTableIdRef.current = "";
        pendingTableTextEditRef.current = null;
        finishTableDragState();
        stopObjectDragAutoScroll();
        visualRootsRef.current.forEach((entry) => {
          try { entry.root?.unmount?.(); } catch (_) {}
        });
        visualRootsRef.current.clear();

        window.removeEventListener("pointermove", handleObjectResizeMove, true);
        window.removeEventListener("pointerup", finishObjectResize, true);
        window.removeEventListener("pointercancel", finishObjectResize, true);
      };
    }, []);


    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || typeof MutationObserver === "undefined") {
        return undefined;
      }

      // The Editor directly owns DocWriterChart/DocWriterMath hydration now.
      // Observe only genuinely introduced document objects and schedule one mount
      // pass; visual components themselves own their internal rendering/size UI.
      const mutationObserver = new MutationObserver((records) => {
        if (
          paginatingRef.current ||
          objectResizeRef.current ||
          objectEditingKeysRef.current.size > 0
        ) return;

        const hasDirectLooseContent = records.some((record) =>
          record.target === editor &&
          Array.from(record.addedNodes || []).some((node) => {
            if (node.nodeType === 3) {
              return Boolean(String(node.nodeValue || "").replace(/\u00a0/g, " ").trim());
            }
            if (node.nodeType !== 1) return false;
            return node.getAttribute?.("data-doc-canvas-page") !== "true" &&
              node.tagName !== "STYLE";
          })
        );

        const hasRelevantAddition = records.some((record) =>
          Array.from(record.addedNodes || []).some((node) => {
            if (node.nodeType !== 1) return false;
            return Boolean(
              node.matches?.(
                `table, [${DOC_PROTECTED_BLOCK_ATTR}="true"], [data-doc-block-type], [data-doc-visual-type]`
              ) ||
                node.querySelector?.(
                  `table, [${DOC_PROTECTED_BLOCK_ATTR}="true"], [data-doc-block-type], [data-doc-visual-type]`
                )
            );
          })
        );

        if (hasRelevantAddition) {
          // Do not normalize every live table whenever any object moves between
          // pages. That broad pass was able to fight DocWriterTable during row/
          // column edits. Normalize only genuinely raw newly-pasted tables.
          const rawTableAdded = records.some((record) =>
            Array.from(record.addedNodes || []).some((node) => {
              if (node.nodeType !== 1) return false;
              const candidates = [
                ...(node.matches?.("table") ? [node] : []),
                ...Array.from(node.querySelectorAll?.("table") || []),
              ];
              return candidates.some(
                (table) =>
                  !table.hasAttribute?.(DOC_TABLE_ATTR) ||
                  !table.hasAttribute?.(DOC_TABLE_ID_ATTR)
              );
            })
          );
          if (rawTableAdded) {
            normalizeTablesWithDocWriterTable(editor, {
              source: "pasted",
              inferWidths: true,
            });
          }
          normalizeProtectedBlocks(editor);
          scheduleDocWriterVisualScan({ force: false });
          docWriterTable.refreshControls?.();
        }

        // A toolbar/object insertion is allowed to land directly under the
        // contentEditable root for a moment. Convert that loose node into normal
        // Letter-page flow immediately instead of relying on CSS to hide it.
        if (hasDirectLooseContent) {
          scheduleRepagination({
            immediate: false,
            preserveSelection: true,
            force: true,
          });
        }
      });

      mutationObserver.observe(editor, {
        childList: true,
        subtree: true,
      });

      return () => {
        mutationObserver.disconnect();
      };
    }, []);

    useEffect(() => {
      const handleSelectionChange = () => {
        captureToolbarSelection();
        const cell = activeCell();
        const table = cell?.closest("table");
        if (table && cell) selectCanvasTable(table, cell);
      };

      document.addEventListener("selectionchange", handleSelectionChange);
      return () => {
        document.removeEventListener("selectionchange", handleSelectionChange);
      };
    }, []);

    const prepareEditorContent = (requestedMode = "serialization") => {
      const editor = editorRef.current;
      const mode = String(requestedMode || "serialization").toLowerCase();
      const revisionMode = ["revision", "worker", "r1", "r2"].includes(mode);
      const finalMode = [
        "preview",
        "final",
        "finalize",
        "final-export",
        "export",
      ].includes(mode);
      const preparationMode = revisionMode
        ? "revision"
        : finalMode
        ? "final-export"
        : "serialization";

      if (editor && finalMode) {
        // Preview/Finalize use the Canvas page map as the physical layout authority.
        // Flush any pending typing debounce and compact only soft page boundaries
        // before capturing that map; manual Page Breaks remain hard barriers.
        settleCanvasPaginationNow({
          compact: true,
          preserveSelection: true,
        });
      }

      if (editor) {
        scanDocWriterVisuals({ force: false });
        Array.from(
          editor.querySelectorAll(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`)
        ).forEach((block) => captureDocWriterVisualSnapshot(block));
      }

      // Exactly one document source of truth: the live Canvas DOM.
      //
      // This deliberately mirrors the working Math ownership model. Child objects
      // may own their internal presentation, but Save/Preview/Finalize never ask a
      // child component/module to serialize the complete document.
      const documentValue = editor?.ownerDocument || document;
      const liveTableCount = Number(
        editor?.querySelectorAll?.("table")?.length || 0
      );
      // Save/revision retain the semantic continuous document plus explicit hard
      // Page Break markers. Only Preview/Finalize serialize the resolved physical
      // Canvas page map. Reopening a saved document therefore recalculates soft page
      // boundaries from current Letter geometry instead of freezing stale wrappers.
      const preserveCurrentPageMap = finalMode;
      const liveCanvasHtml = preserveCurrentPageMap
        ? serializeEditorHtmlWithCanvasPages(editor)
        : serializeEditorHtml(editor, {
            preserveEditingBoundaries: true,
          });

      let stableHtml = stabilizePreparedDocumentHtml(liveCanvasHtml, {
        mode: preparationMode,
        documentValue,
      });

      // A TABLE in the live Canvas is document content. Preparation is never
      // allowed to turn that live object into an empty document.
      if (
        liveTableCount > 0 &&
        countTablesInHtml(stableHtml, documentValue) < 1
      ) {
        stableHtml = liveCanvasHtml;
      }

      let stableText = htmlToStableText(stableHtml, documentValue);

      if (liveTableCount > 0 && !String(stableText || "").trim()) {
        stableText = stableTableTextFromRoot(editor);
      }

      const blocks = Array.from(
        editor?.querySelectorAll?.(`[${DOC_PROTECTED_BLOCK_ATTR}="true"]`) || []
      ).map((block) => visualDescriptorFromBlock(block)).filter(Boolean);

      return {
        mode: preparationMode,
        html: stableHtml,
        text: stableText,
        blockCount: blocks.length,
        tableCount: countTablesInHtml(stableHtml, documentValue),
        chartBlockCount: blocks.filter((item) => item.family === "chart").length,
        mathBlockCount: blocks.filter((item) => item.family === "math").length,
        blocks,
      };
    };


    useImperativeHandle(
      ref,
      () => ({
        focus: () => focusEditorWithoutOuterScroll(),

        getHtml: (options = {}) => {
          const requestedMode =
            typeof options === "string" ? options : options?.mode;
          return prepareEditorContent(requestedMode || "serialization").html;
        },

        prepareForSave: () => prepareEditorContent("serialization"),
        prepareForRevision: () => prepareEditorContent("revision"),
        prepareForPreview: () => prepareEditorContent("preview"),
        prepareForFinalize: () => prepareEditorContent("finalize"),
        getRevisionHtml: () => prepareEditorContent("revision").html,
        getPreviewHtml: () => prepareEditorContent("preview").html,
        getFinalExportHtml: () => prepareEditorContent("finalize").html,

        setHtml: (next) => {
          const cleaned = renderPaginatedHtml(next, {
            preserveSelection: false,
            normalizeIngressLayout: true,
          });

          lastEmittedHtmlRef.current = cleaned;
          if (typeof onChange === "function") {
            onChange(cleaned);
          }

          window.requestAnimationFrame(() => {
            scanDocWriterVisuals({ force: true });
            docWriterTable.refreshControls?.();
          });
        },

        refreshLayout: () => {
          scheduleRepagination({
            immediate: true,
            preserveSelection: true,
            force: true,
          });
        },

        undoEditorTransaction,
        redoEditorTransaction,
        getEditorHistoryState,

        getActiveTableContext: () => docWriterTable.getActiveContext(),

        insertTable: insertTableThroughDocWriter,
        insertImage: insertCanvasImage,
        importTable: importTableThroughDocWriter,
        parseMarkdownTable: docWriterTable.parseMarkdownTable,
        recognizeTableInput: docWriterTable.recognizeTableInput,
        addTableRowAbove: () =>
          tableResultChanged(docWriterTable.addRowAbove()),
        addTableRowBelow: () =>
          tableResultChanged(docWriterTable.addRowBelow()),
        addTableColumnLeft: () =>
          tableResultChanged(docWriterTable.addColumnLeft()),
        addTableColumnRight: () =>
          tableResultChanged(docWriterTable.addColumnRight()),
        deleteTableRow: () =>
          tableResultChanged(docWriterTable.deleteRow()),
        deleteTableColumn: () =>
          tableResultChanged(docWriterTable.deleteColumn()),
        deleteTable: () =>
          tableResultChanged(docWriterTable.deleteTable()),
        distributeTableColumns: () =>
          tableResultChanged(docWriterTable.distributeColumns()),
        toggleTableHeaderRow: () =>
          tableResultChanged(docWriterTable.toggleHeaderRow()),
        getTableInteractionModel: (options = {}) =>
          docWriterTable.getInteractionModel?.(options) || null,
        getTableContextActions: (options = {}) =>
          docWriterTable.getContextActions?.(options) || [],
        splitTableCellColumns: (options = {}) =>
          tableResultChanged(
            docWriterTable.splitCell?.({ ...options, direction: "columns" })
          ),
        splitTableCellRows: (options = {}) =>
          tableResultChanged(
            docWriterTable.splitCell?.({ ...options, direction: "rows" })
          ),
        mergeTableCells: (options = {}) =>
          tableResultChanged(docWriterTable.mergeCells?.(options)),
        moveTable: (options = {}) =>
          tableResultChanged(docWriterTable.moveTable?.(options)),
        runTableCommand: (command, options = {}) =>
          docWriterTable.runCommand?.(command, options) || { changed: false },
        reassembleTables: (tableOrId = null) =>
          docWriterTable.reassembleAndRepaginate(tableOrId),

        hydrateStructuredContent: () =>
          scanDocWriterVisuals({ force: true }),
        importStructuredContent: (value, options = {}) =>
          importDocWriterStructuredContent(value, options),
        insertStructuredVisual: (descriptor = {}) =>
          insertDocWriterVisual(descriptor),
        updateStructuredVisual: (detail = {}) =>
          updateDocWriterVisual(detail),
        getStructuredVisuals: () =>
          Array.from(
            editorRef.current?.querySelectorAll?.(
              `[${DOC_PROTECTED_BLOCK_ATTR}="true"]`
            ) || []
          ).map((block) => visualDescriptorFromBlock(block)).filter(Boolean),

        insertProtectedBlockHtml: ({
          type = "visual",
          html: blockHtml = "",
          source = "",
        } = {}) => {
          if (effectiveReadOnly || !blockHtml) return false;
          try {
            focusEditorWithoutOuterScroll();
            restoreToolbarSelection();
            const documentValue = editorRef.current?.ownerDocument || document;
            const holder = documentValue.createElement("div");
            holder.innerHTML = String(blockHtml);
            const block = holder.firstElementChild;
            if (!block) return false;

            block.setAttribute(DOC_PROTECTED_BLOCK_ATTR, "true");
            block.setAttribute(
              DOC_BLOCK_ID_ATTR,
              block.getAttribute(DOC_BLOCK_ID_ATTR) ||
                createDocumentObjectId("block")
            );
            block.setAttribute("data-doc-block-type", String(type || "visual"));
            block.setAttribute("contenteditable", "false");
            if (source) {
              block.setAttribute("data-doc-block-source", String(source));
            }

            document.execCommand(
              "insertHTML",
              false,
              `${block.outerHTML}<p><br></p>`
            );
            scanDocWriterVisuals({ force: true });
            handleInput();
            return true;
          } catch (_) {
            return false;
          }
        },
      }),
      [onChange, effectiveReadOnly, onImageUpload]
    );


    const handleInput = (event = null) => {
      const editor = editorRef.current;
      if (!editor || paginatingRef.current) return;

      // Final containment guard: if a browser or legacy command ever creates
      // a direct child beside the page wrappers, move it into the active page
      // before saving. Do NOT renormalize every table/visual on every keystroke;
      // child components own their live DOM and the Editor must not fight them.
      containStrayCanvasNodes();

      // Table-cell typing follows the exact same document path as normal typing.
      // The table child owns its cells; Canvas owns the document containing them.
      const currentHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });

      lastEmittedHtmlRef.current = currentHtml;
      markLocalMutation();
      captureToolbarSelection();

      if (typeof onChange === "function") {
        onChange(currentHtml);
      }

      const pendingTableEdit = pendingTableTextEditRef.current;
      const pendingNativeEdit = pendingNativeEditRef.current;
      pendingTableTextEditRef.current = null;
      pendingNativeEditRef.current = null;
      if (pendingTableEdit?.beforeHtml) {
        recordEditorTransaction(
          {
            transactionType: "table",
            transactionId: pendingTableEdit.transactionId,
            operation: pendingTableEdit.inputType || "table-cell-edit",
            tableId: pendingTableEdit.tableId || "",
            focusCellId: pendingTableEdit.cellId || "",
            beforeHtml: pendingTableEdit.beforeHtml,
            afterHtml: currentHtml,
          },
          currentHtml
        );
      } else if (pendingNativeEdit?.beforeHtml) {
        recordEditorTransaction(
          {
            transactionType: "text",
            transactionId: pendingNativeEdit.transactionId,
            operation: pendingNativeEdit.inputType || "text-edit",
            beforeHtml: pendingNativeEdit.beforeHtml,
            afterHtml: currentHtml,
          },
          currentHtml
        );
      }

      const paragraphInput = pendingParagraphInputRef.current;
      pendingParagraphInputRef.current = false;
      const inputType = String(event?.nativeEvent?.inputType || event?.inputType || "");
      const deletionInput = inputType.startsWith("delete");
      updateCounts();
      scheduleRepagination({
        // Enter should cross a page boundary promptly. Deletions get one
        // deliberate compaction pass; routine typing never probes/mutates the
        // next page just to see whether content could move backward.
        immediate: paragraphInput,
        preserveSelection: true,
        force: deletionInput,
      });

      const outerGuard = outerScrollGuardRef.current;
      outerScrollGuardRef.current = null;
      if (outerGuard) {
        restoreOuterScrollState(outerGuard);
        window.requestAnimationFrame(() => restoreOuterScrollState(outerGuard));
      }
    };


    const handleFocus = () => {
      editorFocusedRef.current = true;

      // Do not erase a queued parent/revision document merely because the
      // contentEditable received focus. Blank-Canvas hydration is resolved in
      // the normalizedHtml effect above; real live edits remain authoritative.
    };

    const handleBlur = () => {
      editorFocusedRef.current = false;

      if (paginationTimerRef.current) {
        window.clearTimeout(paginationTimerRef.current);
        paginationTimerRef.current = null;
      }

      // Blur is never a pagination trigger. Clicking the toolbar, an object Edit
      // dialog, or the surrounding Status page must not rebuild the Canvas or
      // move the browser viewport. Persist the live document only.
      if (
        objectEditingKeysRef.current.size > 0 ||
        objectResizeRef.current ||
        objectDragRef.current ||
        toolbarInteractionRef.current
      ) {
        updateCounts();
        return;
      }

      const cleaned = commitCleanBoundaries();
      lastEmittedHtmlRef.current = cleaned;
      if (
        typeof onChange === "function" &&
        cleaned !==
          normalizeDocumentHtml(String(html || ""), {
            preserveEditingBoundaries: true,
          })
      ) {
        onChange(cleaned);
      }
      updateCounts();
    };

    const insertPageBreak = () => {
      if (effectiveReadOnly) return false;

      const editor = editorRef.current;
      if (!editor) return false;

      try {
        focusEditorWithoutOuterScroll();

        if (!ensureCaretInsideCanvasPage()) return false;
        const selection = window.getSelection?.();
        if (!selection || selection.rangeCount === 0) return false;

        const beforeHtml = serializeEditorHtml(editor, {
          preserveEditingBoundaries: true,
        });
        const transactionId = createDocumentObjectId("editortx");

        const range = selection.getRangeAt(0);
        if (!range.collapsed) range.deleteContents();
        const page = canvasPageFromNode(range.startContainer);
        if (!page) return false;

        const documentValue = editor.ownerDocument;
        const breakNode = documentValue.createElement("hr");
        breakNode.className = "page-break";
        breakNode.setAttribute("data-page-break", "true");
        breakNode.setAttribute("data-doc-page-break", "true");
        breakNode.setAttribute("data-doc-manual-page-break", "true");
        breakNode.setAttribute("contenteditable", "false");
        breakNode.setAttribute("draggable", "false");
        breakNode.setAttribute("role", "separator");
        breakNode.setAttribute(
          "aria-label",
          "Page break. Click to select, then press Delete or Backspace to remove."
        );

        const markerId = createDocumentObjectId("page-break-caret");
        const marker = createCaretMarker(documentValue, markerId);

        let startElement = elementFromNode(range.startContainer);
        let directChild = startElement;
        while (directChild && directChild.parentElement !== page) {
          directChild = directChild.parentElement;
        }
        if (directChild === page) directChild = null;

        let caretHost = null;
        if (
          directChild &&
          !isPageBreakElement(directChild) &&
          !isProtectedVisualElement(directChild) &&
          directChild.tagName !== "TABLE"
        ) {
          const afterRange = documentValue.createRange();
          afterRange.selectNodeContents(directChild);
          afterRange.setStart(range.startContainer, range.startOffset);
          const afterContents = afterRange.extractContents();
          const afterBlock = directChild.cloneNode(false);
          afterBlock.removeAttribute("id");
          afterBlock.removeAttribute(CANVAS_GROUP_ATTR);
          afterBlock.appendChild(afterContents);

          if (isBoundaryEmpty(directChild)) {
            directChild.replaceChildren(documentValue.createElement("br"));
          }
          if (isBoundaryEmpty(afterBlock)) {
            afterBlock.replaceChildren(documentValue.createElement("br"));
          }

          directChild.after(breakNode, afterBlock);
          caretHost = afterBlock;
        } else {
          const reference =
            range.startContainer === page
              ? page.childNodes[range.startOffset] || null
              : directChild;

          if (reference && reference.parentNode === page) {
            page.insertBefore(breakNode, reference);
            caretHost = reference;
          } else {
            page.appendChild(breakNode);
            const paragraph = documentValue.createElement("p");
            paragraph.appendChild(documentValue.createElement("br"));
            page.appendChild(paragraph);
            caretHost = paragraph;
          }
        }

        if (!caretHost || isProtectedVisualElement(caretHost)) {
          const paragraph = documentValue.createElement("p");
          paragraph.appendChild(documentValue.createElement("br"));
          breakNode.after(paragraph);
          caretHost = paragraph;
        }

        caretHost.insertBefore(marker, caretHost.firstChild || null);
        const withMarker = serializeEditorHtml(editor, {
          preserveCaretMarkers: true,
          preserveEditingBoundaries: true,
        });

        const cleaned = renderPaginatedHtml(withMarker, {
          preserveSelection: false,
          keepViewport: true,
          caretMarkerId: markerId,
        });
        lastEmittedHtmlRef.current = cleaned;
        markLocalMutation();
        onChange?.(cleaned);
        recordEditorTransaction(
          {
            transactionType: "text",
            transactionId,
            operation: "insert-page-break",
            beforeHtml,
            afterHtml: cleaned,
          },
          cleaned
        );
        updateCounts();

        // The callback is now a notification hook only. Canvas page-break
        // semantics remain authoritative here so toolbar wiring cannot replace
        // or weaken Ctrl/Cmd+Enter behavior.
        try {
          onInsertPageBreak?.({ source: "canvas", html: cleaned });
        } catch (_) {}

        return true;
      } catch (_) {
        return false;
      }
    };


    const removeManualPageBreak = (
      breakNode = selectedPageBreakRef.current
    ) => {
      if (effectiveReadOnly) return false;

      const editor = editorRef.current;
      if (
        !editor ||
        !breakNode ||
        !breakNode.isConnected ||
        !editor.contains(breakNode) ||
        !isPageBreakElement(breakNode)
      ) {
        selectedPageBreakRef.current = null;
        return false;
      }

      const beforeHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      const pagesBefore = Array.from(
        editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
      );
      const originalPage = canvasPageFromNode(breakNode);
      const originalPageIndex = Math.max(0, pagesBefore.indexOf(originalPage));

      breakNode.remove();
      selectedPageBreakRef.current = null;
      clearCanvasPageBreakSelection(editor);
      markLocalMutation();

      // Removing a hard boundary converts it back to ordinary soft flow. Pull
      // later content upward as far as it naturally fits, cascading across all
      // following pages without cloning or deleting document objects.
      rebalanceLiveCanvasPages({
        preserveSelection: false,
        compact: true,
        keepViewport: true,
      });

      const afterHtml = serializeEditorHtml(editor, {
        preserveEditingBoundaries: true,
      });
      lastEmittedHtmlRef.current = afterHtml;
      onChange?.(afterHtml);
      recordEditorTransaction(
        {
          transactionType: "text",
          transactionId: createDocumentObjectId("editortx"),
          operation: "remove-page-break",
          beforeHtml,
          afterHtml,
        },
        afterHtml
      );
      updateCounts();

      const livePages = Array.from(
        editor.querySelectorAll(':scope > [data-doc-canvas-page="true"]')
      );
      setPageCount(Math.max(1, livePages.length));

      window.requestAnimationFrame(() => {
        const focusPage =
          livePages[Math.min(originalPageIndex, livePages.length - 1)] ||
          livePages[livePages.length - 1] ||
          null;
        if (focusPage) {
          focusEditorWithoutOuterScroll();
          placeCaretInsideCanvasPage(focusPage, true);
          captureToolbarSelection();
        }
      });

      return true;
    };


    const isCaretInEditor = () => {
      const root = editorRef.current;
      if (!root) return false;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return false;
      }

      let node = selection.anchorNode;

      while (node) {
        if (node === root) return true;
        node = node.parentNode;
      }

      return false;
    };

    // Kept for the existing toolbar contract. DocCanvasToolbar performs the
    // actual measurements through wrapRef and editorRef.
    const updateFloatingLayout = () => {};

    const isMac = useMemo(() => {
      if (typeof navigator === "undefined") return false;

      return /Mac|iPhone|iPad|iPod/.test(
        navigator.platform || navigator.userAgent || ""
      );
    }, []);

    const accelPressed = (event) =>
      isMac ? event.metaKey : event.ctrlKey;

    const onKeyDown = (event) => {
      if (event.defaultPrevented) return;

      const keyLower = String(event.key || "").toLowerCase();
      if (accelPressed(event) && keyLower === "z") {
        event.preventDefault();
        if (event.shiftKey) redoEditorTransaction();
        else undoEditorTransaction();
        return;
      }
      if (accelPressed(event) && keyLower === "y") {
        event.preventDefault();
        redoEditorTransaction();
        return;
      }

      if (
        ["Backspace", "Delete"].includes(event.key) &&
        selectedPageBreakRef.current?.isConnected
      ) {
        event.preventDefault();
        removeManualPageBreak(selectedPageBreakRef.current);
        return;
      }

      const isEditingKey =
        event.key.length === 1 ||
        ["Enter", "Backspace", "Delete", "Tab"].includes(event.key);

      if (isEditingKey && !ensureCaretInsideCanvasPage()) {
        event.preventDefault();
        return;
      }

      const cell = activeCell();
      const table = cell?.closest("table") || null;

      if (event.key === "Escape") {
        clearSelectedObjects();
        return;
      }

      if (
        !cell &&
        ["Backspace", "Delete"].includes(event.key) &&
        selectedVisualIdRef.current
      ) {
        event.preventDefault();
        removeSelectedVisual();
        return;
      }

      if (event.key === "Enter" && !accelPressed(event)) {
        const selectedBlock = activeProtectedBlock();
        if (!cell && selectedBlock && selectedVisualIdRef.current) {
          event.preventDefault();
          const paragraph = editorRef.current.ownerDocument.createElement("p");
          paragraph.appendChild(editorRef.current.ownerDocument.createElement("br"));
          selectedBlock.parentNode?.insertBefore(paragraph, selectedBlock.nextSibling || null);
          clearSelectedObjects();
          placeCaretInElement(paragraph, true);
          handleInput();
          return;
        }

        // Repeated Enter on an empty boundary paragraph is made deterministic.
        // Native contentEditable is reliable inside ordinary non-empty prose, so
        // leave that path alone; only the blank direct-page case is handled here.
        const selection = window.getSelection?.();
        const range =
          selection && selection.rangeCount > 0
            ? selection.getRangeAt(0)
            : null;
        const page = range ? canvasPageFromNode(range.startContainer) : null;
        const directBlock =
          page && range?.collapsed
            ? directChildWithinPage(page, range.startContainer)
            : null;

        if (
          directBlock &&
          ["P", "DIV"].includes(directBlock.tagName) &&
          isCanvasEditableFlowElement(directBlock) &&
          isBoundaryEmpty(directBlock)
        ) {
          event.preventDefault();

          outerScrollGuardRef.current = captureOuterScrollState(
            wrapRef.current || editorRef.current,
            workspaceRef.current
          );
          pendingNativeEditRef.current = {
            transactionId: createDocumentObjectId("editortx"),
            beforeHtml: serializeEditorHtml(editorRef.current, {
              preserveEditingBoundaries: true,
            }),
            inputType: "insertParagraph",
          };
          nativeTypingGroupRef.current = null;

          const paragraph = createCanvasProseParagraph(
            editorRef.current.ownerDocument
          );
          directBlock.parentNode?.insertBefore(
            paragraph,
            directBlock.nextSibling || null
          );
          clearSelectedObjects();
          placeCaretInElement(paragraph, true);
          pendingParagraphInputRef.current = true;
          handleInput({ inputType: "insertParagraph" });
          return;
        }

        pendingParagraphInputRef.current = true;
      }

      // Desktop keyboard fallback for persistent typing color. Mobile and IME
      // input use the beforeinput guard above.
      if (
        event.key.length === 1 &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey
      ) {
        const typingColor =
          editorRef.current?.getAttribute("data-doc-typing-color") || "";
        if (typingColor) {
          try {
            document.execCommand("styleWithCSS", false, "true");
            document.execCommand("foreColor", false, typingColor);
          } catch (_) {}
        }
      }

      if (!accelPressed(event)) {
        if (event.key === "Tab") {
          event.preventDefault();
          document.execCommand(
            event.shiftKey ? "outdent" : "indent"
          );
          handleInput();
        }

        return;
      }

      if (event.key.toLowerCase() === "enter") {
        event.preventDefault();
        insertPageBreak();
        return;
      }

      if (
        event.shiftKey &&
        event.key.toLowerCase() === "f"
      ) {
        event.preventDefault();
        setFindOpen((current) => !current);
        setTimeout(updateFloatingLayout, 0);
      }
    };

    const styles = useMemo(
      () => ({
        wrap: {
          position: "relative",
          overflow:
            floatToolbar || autoFloat
              ? "visible"
              : "hidden",
          width: "100%",
          minWidth: 0,
          border: "1px solid #D8E0EA",
          borderRadius: 12,
          background: "#F8FAFC",
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
        },

        toolbarHost: {
          display:
            showToolbar && !effectiveReadOnly
              ? "block"
              : "none",
          position: "relative",
          zIndex: 5,
          background: "#F8FAFC",
          borderBottom: "1px solid #D8E0EA",
        },

        toolbarSpacer: {
          height: toolbarMetrics.height,
        },

        editorBox: {
          position: "relative",
          minWidth: 0,
          width: "100%",
          // The Canvas owns a fixed one-page viewport. All additional pages live
          // inside this same scroll container and are reached with its right-side
          // scrollbar, mouse wheel/touchpad, PageUp/PageDown, or gutter arrow keys.
          height: FULL_PAGE_WORKSPACE_HEIGHT,
          minHeight: FULL_PAGE_WORKSPACE_HEIGHT,
          maxHeight: FULL_PAGE_WORKSPACE_HEIGHT,
          overflowX: "auto",
          overflowY: "scroll",
          // Always reserve the Canvas scrollbar track. Multi-page reachability must
          // never depend on the outer DocWriterEditorPage or browser body scrolling.
          scrollbarGutter: "stable",
          padding: WORKSPACE_VERTICAL_GUTTER_PX,
          background: "#E9EEF5",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          overscrollBehaviorY: "contain",
          overflowAnchor: "none",
          scrollbarWidth: "auto",
          touchAction: "pan-x pan-y",
          scrollBehavior: "auto",
          boxSizing: "border-box",
        },

        paperShell: {
          // The shell is deliberately unscaled. Natural page/flex height is the
          // authoritative scroll extent; no calculated transform placeholder can
          // collapse or desynchronize pages 2, 3, 4... from the right scrollbar.
          position: "relative",
          width: LETTER_WIDTH,
          minWidth: LETTER_WIDTH,
          height: "auto",
          minHeight: 0,
          margin: "0 auto",
          overflow: "visible",
        },

        editor: {
          boxSizing: "border-box",
          width: LETTER_WIDTH,
          minWidth: LETTER_WIDTH,
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          gap: PAGE_GAP_PX,
          transform: "none",
          transformOrigin: "top left",
          willChange: "auto",
          outline: "none",
          background: "transparent",
          color: "#111827",
          cursor: effectiveReadOnly ? "default" : "text",
          direction: "ltr",
          unicodeBidi: "plaintext",
          textAlign: "left",
          whiteSpace: "normal",
          // Never inherit overflow-wrap:anywhere into protected objects. It changes
          // the min-content width of KaTeX/fit-content shells and can collapse a
          // perfectly valid equation into a one-character-wide vertical column.
          overflowWrap: "normal",
          wordBreak: "normal",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: "11pt",
          lineHeight: 1.22,
          textRendering: "optimizeLegibility",
          caretColor: "#1E2A78",
        },

        statusBar: {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          minWidth: 0,
          padding: "8px 12px",
          borderTop: "1px solid #D8E0EA",
          background: "#F8FAFC",
          color: "#64748B",
          fontSize: 12,
          lineHeight: 1.35,
        },

        pageInfo: {
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          fontWeight: 750,
        },

        countInfo: {
          flex: "0 0 auto",
          whiteSpace: "nowrap",
        },

        redlineNote: {
          marginTop: 8,
          color: "#6B7280",
          fontSize: 12,
        },

        css: `
          .doc-canvas-workspace {
            scrollbar-width: auto;
            scrollbar-color: #94A3B8 #E2E8F0;
            overflow-y: scroll !important;
            overflow-anchor: none;
            overscroll-behavior-y: contain;
          }

          .doc-canvas-paper,
          .doc-canvas-page {
            overflow-anchor: none;
          }

          .doc-canvas-workspace::-webkit-scrollbar {
            width: 12px;
            height: 12px;
          }

          .doc-canvas-workspace::-webkit-scrollbar-track {
            background: #E2E8F0;
          }

          .doc-canvas-workspace::-webkit-scrollbar-thumb {
            background: #94A3B8;
            border-radius: 999px;
            border: 3px solid #E2E8F0;
          }

          .doc-canvas-workspace::-webkit-scrollbar-thumb:hover {
            background: #64748B;
          }

          .doc-canvas-paper {
            outline: none;
          }

          /* Prose may wrap aggressively. DocWriterMath is a component-owned
             presentation island: Canvas must not reach into its shell/KaTeX tree
             to repair wrapping or impose an Editor minimum width. The component
             itself owns white-space, overflow, width, height, typography, and all
             visible Math geometry exactly as it does in its Live Preview. */
          .doc-canvas-paper p,
          .doc-canvas-paper li,
          .doc-canvas-paper blockquote,
          .doc-canvas-paper pre,
          .doc-canvas-paper code {
            overflow-wrap: anywhere;
            word-break: normal;
          }

          /* A normalized/manual/AI table is always document content. Never let
             stale pasted display/visibility rules or a previous editor snapshot
             suppress it in the live Canvas. */
          .doc-canvas-page table {
            display: table !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          .doc-canvas-page thead { display: table-header-group !important; }
          .doc-canvas-page tbody { display: table-row-group !important; }
          .doc-canvas-page tfoot { display: table-footer-group !important; }
          .doc-canvas-page tr { display: table-row !important; }
          .doc-canvas-page th,
          .doc-canvas-page td {
            display: table-cell !important;
            visibility: visible !important;
            opacity: 1 !important;
          }

          /* Do not hide transient direct children of the contentEditable root.
             Toolbar insertion can briefly place a new table/object at the root
             before the scheduled pagination pass wraps it in a Letter page. Hiding
             that node made successful insertions look as if they disappeared. */
          .doc-canvas-paper > :not([data-doc-canvas-page="true"]) {
            max-width: 100%;
          }

          .doc-canvas-page {
            box-sizing: border-box;
            position: relative;
            width: 8.5in;
            height: 11in;
            min-height: 11in;
            padding: 0.75in;
            overflow: hidden;
            border: 1px solid #CBD5E1;
            border-radius: 2px;
            background: #FFFFFF;
            box-shadow: 0 12px 30px rgba(15, 23, 42, 0.14);
          }

          .doc-canvas-page[${CANVAS_OVERFLOW_ATTR}="true"] {
            outline: 1px dashed rgba(185, 28, 28, 0.28);
            outline-offset: -1px;
          }

          .doc-canvas-page::after {
            content: "Page " attr(data-page-number);
            position: absolute;
            right: 0.32in;
            bottom: 0.22in;
            color: #94A3B8;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 8.5pt;
            font-weight: 700;
            line-height: 1;
            pointer-events: none;
          }

          .doc-canvas-page:first-child:empty::before {
            content: attr(data-placeholder);
            color: #94A3B8;
            pointer-events: none;
          }

          .doc-canvas-paper:focus .doc-canvas-page {
            border-color: rgba(30, 42, 120, 0.42);
          }

          /* Canvas document boxes use border-box, but component-owned Math
             and Table presentation islands keep their own internal box models.
             Canvas owns page flow/placement; DocWriterMath and DocWriterTable own
             the visible geometry inside their respective objects. */
          .doc-canvas-page *:not(.docwriter-math-shell):not(.docwriter-math-shell *):not([data-doc-writer-table="true"]):not([data-doc-writer-table="true"] *) {
            box-sizing: border-box;
          }

          .doc-canvas-page > :first-child {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }

          .doc-canvas-page > :last-child {
            margin-bottom: 0 !important;
          }

          /* Generic document-flow sizing must stop at the Math component boundary.
             In the old Canvas these selectors also matched KaTeX/display DIVs and
             changed their max-width/line-height, while the portal Live Preview was
             outside this scope and therefore rendered correctly. */
          .doc-canvas-paper div:not(.docwriter-math-shell):not(.docwriter-math-shell *),
          .doc-canvas-paper section:not(.docwriter-math-shell *),
          .doc-canvas-paper article:not(.docwriter-math-shell *),
          .doc-canvas-paper main:not(.docwriter-math-shell *),
          .doc-canvas-paper header:not(.docwriter-math-shell *),
          .doc-canvas-paper footer:not(.docwriter-math-shell *),
          .doc-canvas-paper aside:not(.docwriter-math-shell *) {
            max-width: 100%;
            line-height: inherit;
          }

          .doc-canvas-paper div[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper section[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper article[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper main[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper header[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper footer[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper aside[${CANVAS_GROUP_ATTR}] {
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }

          .doc-canvas-paper p {
            margin: 0 0 0.32em !important;
            font-size: 11pt;
            line-height: 1.22 !important;
            white-space: normal !important;
            orphans: 2;
            widows: 2;
          }

          /* Chromium may use a direct DIV as a paragraph after native Enter.
             Keep simple prose DIVs inside the printable width, but never inherit
             aggressive wrapping into a protected Chart/Math/Image/Table island. */
          .doc-canvas-page > div:not(.docwriter-math-shell):not([${DOC_PROTECTED_BLOCK_ATTR}="true"]):not(:has(table, figure, img, svg, canvas, [${DOC_PROTECTED_BLOCK_ATTR}="true"])) {
            max-width: 100% !important;
            white-space: normal !important;
            overflow-wrap: anywhere;
            word-break: normal;
          }

          .doc-canvas-paper p:empty,
          .doc-canvas-paper div:empty:not(.docwriter-math-shell):not(.docwriter-math-shell *) {
            min-height: 0.72em;
            margin-bottom: 0.14em !important;
          }

          .doc-canvas-paper p:has(> br:only-child),
          .doc-canvas-paper div:has(> br:only-child):not(.docwriter-math-shell):not(.docwriter-math-shell *) {
            min-height: 0.72em;
            margin-bottom: 0.14em !important;
          }

          .doc-canvas-paper h1,
          .doc-canvas-paper h2,
          .doc-canvas-paper h3,
          .doc-canvas-paper h4,
          .doc-canvas-paper h5,
          .doc-canvas-paper h6 {
            margin: 0.52em 0 0.2em !important;
            line-height: 1.14 !important;
            break-after: avoid;
            page-break-after: avoid;
            orphans: 3;
            widows: 3;
          }

          .doc-canvas-paper h1 {
            font-size: 18pt !important;
          }

          .doc-canvas-paper h2 {
            font-size: 15pt !important;
          }

          .doc-canvas-paper h3 {
            font-size: 12.5pt !important;
          }

          .doc-canvas-paper h4,
          .doc-canvas-paper h5,
          .doc-canvas-paper h6 {
            font-size: 11pt !important;
          }

          .doc-canvas-paper ul,
          .doc-canvas-paper ol {
            margin: 0 0 0.32em !important;
            padding-left: 1.3em !important;
            line-height: 1.22 !important;
          }

          .doc-canvas-paper ul[${CANVAS_GROUP_ATTR}] + ul[${CANVAS_GROUP_ATTR}],
          .doc-canvas-paper ol[${CANVAS_GROUP_ATTR}] + ol[${CANVAS_GROUP_ATTR}] {
            margin-top: -0.32em !important;
          }

          .doc-canvas-paper li {
            margin: 0 0 0.06em !important;
            padding: 0 !important;
            font-size: 11pt;
            line-height: 1.22 !important;
          }

          .doc-canvas-paper li > p {
            margin-bottom: 0.08em !important;
          }

          .doc-canvas-paper blockquote {
            margin: 0.38em 0 0.38em 0.85em !important;
            padding-left: 0.65em !important;
            border-left: 3px solid #CBD5E1;
            color: #334155;
            line-height: 1.22 !important;
          }

          .doc-canvas-paper a {
            color: #1E2A78;
            text-decoration: underline;
            text-underline-offset: 2px;
          }

          .doc-canvas-paper img {
            display: block;
            max-width: 100% !important;
            max-height: 9.25in !important;
            height: auto !important;
            margin: 0.35em auto !important;
            object-fit: contain;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .doc-canvas-paper .doc-canvas-image-block img {
            width: 100% !important;
            height: 100% !important;
            max-width: 100% !important;
            max-height: none !important;
            margin: 0 !important;
          }

          .doc-canvas-paper table {
            width: 100%;
            max-width: 100% !important;
            margin: 0.38em 0 !important;
            border-collapse: collapse;
            table-layout: auto;
            font-size: 10.5pt;
            line-height: 1.18 !important;
            overflow: visible !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
          }

          .doc-canvas-paper table[data-doc-table-resized="true"] {
            table-layout: fixed;
          }

          .doc-canvas-paper table[${CANVAS_GROUP_ATTR}] + table[${CANVAS_GROUP_ATTR}] {
            margin-top: -0.38em !important;
          }

          .doc-canvas-paper table[${CANVAS_SELECTED_TABLE_ATTR}="true"] {
            outline: 2px solid rgba(30, 42, 120, 0.58);
            outline-offset: 2px;
          }

          .doc-canvas-paper table[${CANVAS_SELECTED_TABLE_ATTR}="true"] th:first-child,
          .doc-canvas-paper table[${CANVAS_SELECTED_TABLE_ATTR}="true"] td:first-child {
            position: relative;
          }

          .doc-canvas-paper [${CANVAS_DRAG_OVER_ATTR}] {
            position: relative;
          }

          .doc-canvas-paper [${CANVAS_DRAG_OVER_ATTR}="before"],
          .doc-canvas-paper [${CANVAS_DRAG_OVER_ATTR}="inside-start"] {
            box-shadow: 0 -3px 0 rgba(30, 42, 120, 0.72);
          }

          .doc-canvas-paper [${CANVAS_DRAG_OVER_ATTR}="after"],
          .doc-canvas-paper [${CANVAS_DRAG_OVER_ATTR}="inside-end"] {
            box-shadow: 0 3px 0 rgba(30, 42, 120, 0.72);
          }

          .doc-canvas-paper th,
          .doc-canvas-paper td {
            min-width: 0;
            min-height: 1.55em;
            height: auto;
            max-height: none !important;
            padding: 4px 6px !important;
            border: 1px solid #CBD5E1;
            overflow: visible !important;
            overflow-wrap: anywhere;
            word-break: normal;
            white-space: normal !important;
            vertical-align: top;
            line-height: 1.18 !important;
          }

          .doc-canvas-paper th[${CANVAS_SELECTED_CELL_ATTR}="true"],
          .doc-canvas-paper td[${CANVAS_SELECTED_CELL_ATTR}="true"] {
            box-shadow: inset 0 0 0 2px rgba(30, 42, 120, 0.72);
          }

          .doc-canvas-paper th {
            background: #F8FAFC;
            font-weight: 700;
          }

          .doc-canvas-paper tr {
            height: auto;
            max-height: none !important;
            overflow: visible !important;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"] {
            position: relative;
            display: block;
            max-width: 100% !important;
            margin-top: 0.38em !important;
            margin-bottom: 0.38em !important;
            overflow: visible;
            user-select: none;
            cursor: pointer;
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"][${DOC_VISUAL_ALIGNMENT_ATTR}="left"] {
            margin-left: 0 !important;
            margin-right: auto !important;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"][${DOC_VISUAL_ALIGNMENT_ATTR}="center"] {
            margin-left: auto !important;
            margin-right: auto !important;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"][${DOC_VISUAL_ALIGNMENT_ATTR}="right"] {
            margin-left: auto !important;
            margin-right: 0 !important;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"][${DOC_BLOCK_TYPE_ATTR}="math"] {
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
            overflow: visible !important;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"][${CANVAS_SELECTED_VISUAL_ATTR}="true"] {
            outline: 2px solid rgba(30, 42, 120, 0.7);
            outline-offset: 3px;
          }

          .doc-canvas-paper [${DOC_VISUAL_HOST_ATTR}="true"] {
            box-sizing: border-box;
            width: 100%;
            min-width: 0;
            margin: 0 !important;
            padding: 0 !important;
          }

          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"] svg,
          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"] canvas,
          .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"] img {
            max-width: 100% !important;
          }

          .doc-canvas-paper pre {
            margin: 0.38em 0 !important;
            white-space: pre-wrap;
            overflow-wrap: anywhere;
            font-size: 10pt;
            line-height: 1.18 !important;
          }

          .doc-canvas-paper code {
            white-space: pre-wrap;
            overflow-wrap: anywhere;
          }

          .doc-canvas-paper hr:not([data-page-break]) {
            margin: 0.5em 0 !important;
            border: 0;
            border-top: 1px solid #CBD5E1;
          }

          .doc-canvas-paper .page-break,
          .doc-canvas-paper [data-page-break="true"],
          .doc-canvas-paper [data-doc-page-break="true"],
          .doc-canvas-paper hr[data-page-break] {
            display: block !important;
            position: relative;
            z-index: 4;
            width: 100%;
            height: 0 !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            border: 0 !important;
            overflow: visible !important;
            background: transparent !important;
            cursor: pointer;
            user-select: none;
          }

          .doc-canvas-paper .page-break::before,
          .doc-canvas-paper [data-page-break="true"]::before,
          .doc-canvas-paper [data-doc-page-break="true"]::before,
          .doc-canvas-paper hr[data-page-break]::before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            top: 0;
            border-top: 1px dashed #94A3B8;
            pointer-events: auto;
          }

          .doc-canvas-paper .page-break::after,
          .doc-canvas-paper [data-page-break="true"]::after,
          .doc-canvas-paper [data-doc-page-break="true"]::after,
          .doc-canvas-paper hr[data-page-break]::after {
            content: "Page Break";
            position: absolute;
            left: 50%;
            top: -2px;
            transform: translate(-50%, -100%);
            padding: 1px 8px;
            border-radius: 999px;
            background: #FFFFFF;
            color: #64748B;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 8.5pt;
            font-weight: 700;
            line-height: 1.2;
            white-space: nowrap;
            pointer-events: auto;
          }

          .doc-canvas-paper [${CANVAS_SELECTED_PAGE_BREAK_ATTR}="true"]::before {
            border-top-color: #1E2A78;
            border-top-width: 2px;
          }

          .doc-canvas-paper [${CANVAS_SELECTED_PAGE_BREAK_ATTR}="true"]::after {
            content: "Page Break · Delete/Backspace to remove";
            color: #1E2A78;
            box-shadow: 0 0 0 1px rgba(30, 42, 120, 0.2);
          }

          .doc-canvas-paper .mark-insert {
            background: #E6FFED;
          }

          .doc-canvas-paper .mark-delete {
            background: #FFE6E6;
            text-decoration: line-through;
          }

          @media (max-width: 1024px) {
            .doc-canvas-workspace {
              padding: 12px !important;
              min-height: 430px !important;
            }

            .doc-canvas-paper-shell,
            .doc-canvas-paper {
              width: 8.5in !important;
              min-width: 8.5in !important;
              transform: none !important;
            }

            .doc-canvas-status {
              align-items: flex-start !important;
              flex-direction: column !important;
              gap: 4px !important;
            }
          }

          @media print {
            .doc-canvas-toolbar-host,
            .doc-canvas-status {
              display: none !important;
            }

            .doc-canvas-workspace {
              height: auto !important;
              max-height: none !important;
              overflow: visible !important;
              padding: 0 !important;
              background: #FFFFFF !important;
            }

            .doc-canvas-paper-shell,
            .doc-canvas-paper {
              width: auto !important;
              min-width: 0 !important;
              height: auto !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              display: block !important;
              gap: 0 !important;
              transform: none !important;
              zoom: 1 !important;
            }

            .doc-canvas-page {
              width: 8.5in !important;
              height: 11in !important;
              min-height: 11in !important;
              margin: 0 !important;
              padding: 0.75in !important;
              overflow: visible !important;
              border: 0 !important;
              border-radius: 0 !important;
              box-shadow: none !important;
              break-after: page;
              page-break-after: always;
            }

            .doc-canvas-page:last-child {
              break-after: auto;
              page-break-after: auto;
            }

            .doc-canvas-page::after {
              display: none !important;
            }

            .doc-canvas-paper .page-break,
            .doc-canvas-paper [data-page-break="true"],
            .doc-canvas-paper [data-doc-page-break="true"],
            .doc-canvas-paper hr[data-page-break] {
              height: 0 !important;
              min-height: 0 !important;
              margin: 0 !important;
              padding: 0 !important;
              border: 0 !important;
              overflow: hidden !important;
              background: transparent !important;
              pointer-events: none !important;
            }

            .doc-canvas-paper .page-break::before,
            .doc-canvas-paper .page-break::after,
            .doc-canvas-paper [data-page-break="true"]::before,
            .doc-canvas-paper [data-page-break="true"]::after,
            .doc-canvas-paper [data-doc-page-break="true"]::before,
            .doc-canvas-paper [data-doc-page-break="true"]::after,
            .doc-canvas-paper hr[data-page-break]::before,
            .doc-canvas-paper hr[data-page-break]::after {
              display: none !important;
            }

            .doc-canvas-paper table[${CANVAS_SELECTED_TABLE_ATTR}="true"],
            .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"][${CANVAS_SELECTED_VISUAL_ATTR}="true"] {
              outline: none !important;
            }

            .doc-canvas-paper th[${CANVAS_SELECTED_CELL_ATTR}="true"],
            .doc-canvas-paper td[${CANVAS_SELECTED_CELL_ATTR}="true"] {
              box-shadow: none !important;
            }

            .doc-canvas-paper [${DOC_PROTECTED_BLOCK_ATTR}="true"] {
              overflow: visible !important;
            }
          }
        `,
      }),
      [
        autoFloat,
        effectiveReadOnly,
        floatToolbar,
        showToolbar,
        toolbarMetrics.height,
        pageCount,
      ]
    );

    const handleWorkspaceKeyDown = (event) => {
      // When the user clicks the gray Canvas gutter, make the workspace itself
      // keyboard-scrollable. Do not intercept keys originating inside the
      // contentEditable document; those remain normal editing/caret commands.
      if (event.target !== event.currentTarget) return;
      const workspace = workspaceRef.current;
      if (!workspace) return;

      const lineStep = 52;
      const pageStep = Math.max(120, Math.round(workspace.clientHeight * 0.88));
      let delta = 0;

      if (event.key === "ArrowDown") delta = lineStep;
      else if (event.key === "ArrowUp") delta = -lineStep;
      else if (event.key === "PageDown") delta = pageStep;
      else if (event.key === "PageUp") delta = -pageStep;
      else if (event.key === "Home") {
        event.preventDefault();
        workspace.scrollTop = 0;
        return;
      } else if (event.key === "End") {
        event.preventDefault();
        workspace.scrollTop = Math.max(
          0,
          workspace.scrollHeight - workspace.clientHeight
        );
        return;
      } else {
        return;
      }

      event.preventDefault();
      workspace.scrollTop += delta;
    };

    return (
      <>
        <style>{styles.css}</style>

        <div ref={wrapRef} style={styles.wrap}>
          <div
            className="doc-canvas-toolbar-host"
            style={styles.toolbarHost}
            aria-label="Editor toolbar host"
          >
            <DocCanvasToolbar
              wrapRef={wrapRef}
              editorRef={editorRef}
              showToolbar={showToolbar}
              readOnly={effectiveReadOnly}
              floatToolbar={floatToolbar}
              autoFloat={autoFloat}
              isFloatingNow={isFloatingNow}
              setFloatToolbar={setFloatToolbar}
              setAutoFloat={setAutoFloat}
              setIsFloatingNow={setIsFloatingNow}
              toolbarMetrics={toolbarMetrics}
              setToolbarMetrics={setToolbarMetrics}
              findOpen={findOpen}
              setFindOpen={setFindOpen}
              findQuery={findQuery}
              setFindQuery={setFindQuery}
              findReplace={findReplace}
              setFindReplace={setFindReplace}
              findCaseSensitive={findCaseSensitive}
              setFindCaseSensitive={setFindCaseSensitive}
              lastFindEnd={lastFindEnd}
              setLastFindEnd={setLastFindEnd}
              findFixedPos={findFixedPos}
              setFindFixedPos={setFindFixedPos}
              isCaretInEditor={isCaretInEditor}
              onInsertPageBreak={insertPageBreak}
              undoEditorTransaction={undoEditorTransaction}
              redoEditorTransaction={redoEditorTransaction}
              getEditorHistoryState={getEditorHistoryState}
              insertCanvasTable={insertTableThroughDocWriter}
              insertCanvasImage={insertCanvasImage}
              refreshCanvasLayout={() =>
                scheduleRepagination({
                  immediate: true,
                  preserveSelection: true,
                  force: true,
                })
              }
              captureEditorSelection={captureToolbarSelection}
              restoreEditorSelection={restoreToolbarSelection}
              beginEditorInteraction={beginToolbarInteraction}
              endEditorInteraction={endToolbarInteraction}
              updateFloatingLayout={updateFloatingLayout}
            />
          </div>

          {(floatToolbar || autoFloat) &&
          isFloatingNow ? (
            <div style={styles.toolbarSpacer} />
          ) : null}

          <div
            ref={workspaceRef}
            className="doc-canvas-workspace"
            style={styles.editorBox}
            tabIndex={0}
            role="region"
            aria-label={`Document pages. ${pageCount} page${pageCount === 1 ? "" : "s"}.`}
            onKeyDown={handleWorkspaceKeyDown}
            onDragOver={handleCanvasDragOver}
            onDrop={handleCanvasDrop}
          >
            <div
              className="doc-canvas-paper-shell"
              style={styles.paperShell}
            >
              <div
                ref={editorRef}
                className="doc-canvas-paper"
                style={styles.editor}
                contentEditable={!effectiveReadOnly}
                suppressContentEditableWarning
                spellCheck={!effectiveReadOnly}
                onPointerDownCapture={handleCanvasPointerDownCapture}
                onPointerDown={handleCanvasPointerDown}
                onPointerMove={handleCanvasPointerMove}
                onPointerLeave={handleCanvasPointerLeave}
                onDragStart={handleCanvasDragStart}
                onDragEnd={handleCanvasDragEnd}
                onBeforeInput={handleBeforeInput}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onInput={handleInput}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={onKeyDown}
                aria-label="Document editor"
                aria-multiline="true"
                role="textbox"
                data-placeholder={placeholder}
              />
            </div>
          </div>

          <div
            className="doc-canvas-status"
            style={styles.statusBar}
          >
            <span style={styles.pageInfo}>
              {pageCount.toLocaleString()} page{pageCount === 1 ? "" : "s"} ·
              Letter · 0.75 in margins · print-like canvas · PDF preview confirms exact pagination
            </span>

            <span style={styles.countInfo}>
              Words: {counts.words.toLocaleString()} · Characters:{" "}
              {counts.chars.toLocaleString()}
            </span>
          </div>
        </div>

        {redline ? (
          <div style={styles.redlineNote}>
            Redline is on: show{" "}
            <span className="mark-insert">
              insertions
            </span>{" "}
            and
            <span className="mark-delete">
              {" "}
              deletions
            </span>
            .
          </div>
        ) : null}
      </>
    );
  }
);

DocCanvasEditor.displayName = "DocCanvasEditor";

export default DocCanvasEditor;
